import React, { useCallback, useMemo, useEffect, useRef } from 'react';
import { NodeProps, useReactFlow, useStore } from '@xyflow/react';
import { UniversalNode } from './nodes/UniversalNode';
import { getNodeDefinition } from '../config/nodeDefinitions';
import { getHandlerType, reactFlowTypeToType, getNodeByReactFlowType, getNodeRegistration, getOutputNodeDimensions } from '../config/nodeRegistry';
import { EDGE_COLOR_MAP } from '../types/nodeConfig';
import {
  parseAspectRatioSetting,
  describeClosestRatio,
  probeMediaRatio,
  probeAudioDuration,
  aspectRatioDrift,
  ASPECT_RATIO_DRIFT_TOLERANCE,
  resolveOfferedOptions,
  decideAspectRatioSnap,
} from '../utils/aspectRatioUtils';
import type { FileContext } from './ui';
import type { ImageData } from './ui/ImageUploadZone';
import type { ChatMessage } from '../types/api';
import openAIService from '../services/OpenAIService';
import claudeService from '../services/ClaudeService';
import kimiService from '../services/KimiService';
import qwenService from '../services/QwenService';
import minimaxService from '../services/MinimaxService';
import geminiTextService from '../services/GeminiTextService';
import grokTextService from '../services/GrokTextService';
import elevenLabsAudioService from '../services/ElevenLabsAudioService';
import falAIService from '../services/FalAIService';
import threadManagementService from '../services/ThreadManagementService';
import brandVoiceStorageService from '../services/BrandVoiceStorageService';
import { fileStorageService } from '../services/FileStorageService';
import { supabaseFlowStorageService as flowStorageService } from '../services/SupabaseFlowStorageService';
import { generationLifecycleService } from '../services/GenerationLifecycleService';
import { supabase } from '../lib/supabase';
import { alertService, type AlertAction } from './Alert';
import { toastService } from './ui/Toast';
import { confirmDialogService } from './ui/ConfirmDialog';
import { promptLimitService } from './ui/PromptLimitModal';
import { getPromptCharLimit, getModelDisplayName } from '../constants/modelConfigs';
import logger from '../utils/logger';
import { isVeo31Model, resolveVeo31Resolution } from '../utils/creditCostUtils';
import { calculateVideoCreditCost, calculateImageCreditCost, calculateWanAnimateCreditCost, calculateVideoScaleCreditCost, calculateGeminiImageCreditCost, calculateLTX23CreditCost, calculateGPTImageCreditCost, calculateElevenLabsTTSCreditCost, resolveAudioDrivenSeconds, AUDIO_DRIVEN_DURATION_MODELS, calculateGrokImageNodeCreditCost, countImagesFromSources } from '../utils/creditCostUtils';
import { GROK_IMAGE_NODE_MODELS, buildGrokImageRequestBody, grokImageApiModel, grokImageRejectsInputImages } from '../utils/grokImagePayload';
import { useCredits } from '../contexts/CreditsContext';
import { useByokKeys } from '../hooks/useByokKeys';
import { useAutoSaveContext } from '../contexts/AutoSaveContext';
import { executionController } from '../services/ExecutionController';
import { getFriendlyErrorMessage, friendlyErrors, isInsufficientCreditsError } from '../utils/friendlyErrorMessages';
import { recordUserAction } from '../services/FlowHistoryRef';
import { endedJobPatch, markJobPolling, endJobPolling } from '../utils/jobState';

// Canonical spawn sizes for output nodes — single source of truth in nodeRegistry.
// Every path that creates a 'universalOutput'/'customOutput' node must use these
// (or call getOutputNodeDimensions directly) so output nodes always spawn equal-sized.
const TEXT_OUTPUT_DIMS = getOutputNodeDimensions('text');
const MEDIA_OUTPUT_DIMS = getOutputNodeDimensions('image');

const CREDITS_ERROR_MESSAGE = "You don't have enough credits to run this. Add more credits or upgrade your plan.";
const CREDITS_ERROR_ACTIONS: AlertAction[] = [
  { label: 'Buy Credits', onClick: () => { window.location.href = '/credits'; }, variant: 'primary' },
  { label: 'Upgrade Plan', onClick: () => { window.location.href = '/pricing'; }, variant: 'secondary' },
];

/**
 * Build authenticated headers for API requests.
 * Attaches the Supabase JWT + x-user-id for backward compatibility.
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }
    if (session?.user?.id) {
      headers['x-user-id'] = session.user.id;
    }
  } catch { /* proceed without auth */ }
  return headers;
}

/** Shows a generation error alert, handling insufficient-credits specially. */
function showGenerationError(error: unknown, defaultMessage: string, suggestions?: string[]): void {
  if (isInsufficientCreditsError(error)) {
    alertService.error(CREDITS_ERROR_MESSAGE, false, undefined, undefined, CREDITS_ERROR_ACTIONS);
  } else {
    alertService.error(getFriendlyErrorMessage(error, defaultMessage), false, suggestions);
  }
}

/**
 * Check if a node type is a video generator (using registry)
 */
function isVideoGeneratorNode(nodeType: string): boolean {
  const pascalType = reactFlowTypeToType(nodeType);
  return getHandlerType(pascalType) === 'video';
}

/**
 * Check if a node type is a fal.ai video generator (using registry)
 */
function isFalAIVideoNode(nodeType: string): boolean {
  const pascalType = reactFlowTypeToType(nodeType);
  return getHandlerType(pascalType) === 'falAIVideo';
}

/**
 * Check if a node type is a fal.ai image generator (using registry)
 */
function isFalAIImageNode(nodeType: string): boolean {
  const pascalType = reactFlowTypeToType(nodeType);
  return getHandlerType(pascalType) === 'falAIImage';
}

/**
 * Check if a node type is an ElevenLabs audio generator (using registry)
 */
function isAudioGenNode(nodeType: string): boolean {
  const pascalType = reactFlowTypeToType(nodeType);
  return getHandlerType(pascalType) === 'audioGen';
}

/**
 * Resolve the text a node receives from its connected text input.
 *
 * Single source of truth shared by the reactive `connectedTextContent`
 * subscription (send-button gate + placeholder), the ElevenLabs character-based
 * credit badge, and handleAudioGenerate — so the number quoted on the button,
 * the text sent to the API, and the characters the server bills all come from
 * the SAME resolution. Per source node the priority is output → content →
 * prompt; the first connected text input wins.
 */
function readConnectedText(
  edges: Array<{ source: string; target: string; targetHandle?: string | null }>,
  getNodeById: (nodeId: string) => { data?: Record<string, unknown> } | undefined,
  targetId: string
): string {
  for (const edge of edges) {
    if (edge.target !== targetId) continue;
    const targetHandle = edge.targetHandle;
    // Only check text input connections
    if (targetHandle !== 'input-text' && targetHandle !== 'input' && targetHandle) continue;
    const sourceNode = getNodeById(edge.source);
    if (!sourceNode?.data) continue;
    // Check various text content fields
    const textOutput = sourceNode.data.output;
    if (textOutput && typeof textOutput === 'string' && textOutput.trim()) {
      return textOutput;
    }
    const content = sourceNode.data.content;
    if (content && typeof content === 'string' && content.trim()) {
      return content;
    }
    const prompt = sourceNode.data.prompt;
    if (prompt && typeof prompt === 'string' && prompt.trim()) {
      return prompt;
    }
  }
  return '';
}


/**
 * Map a node type to the BYOK provider whose key (if configured) would cover it.
 * Returns null for nodes that don't hit a BYOK-eligible provider (e.g. local
 * utility nodes, ImageScale resize, etc).
 *
 * Provider buckets (per BYOK plan):
 *   - openai:    text/GPT/DALL·E
 *   - anthropic: Claude
 *   - google:    Gemini (text + image) / Veo (video)
 *   - falai:     FLUX, Qwen image, Seedream, Recraft, GLM, Z-Image, Kling, Wan,
 *                LTX, Hailuo (Minimax video), HeyGen, and other fal.ai models
 *   - xai:       Grok (image + video)
 *   - ollama:    Kimi, Qwen text, MiniMax text
 *
 * Seedance used to have its own 'piapi' bucket here. It moved to fal.ai in
 * migration 113 and now resolves through the `falAIVideo` branch below.
 */
function getNodeByokProvider(nodeType: string): string | null {
  const pascalType = reactFlowTypeToType(nodeType);
  const handler = getHandlerType(pascalType);

  // Text models: provider depends on the specific node, not the handler.
  if (pascalType === 'Claude') return 'anthropic';
  if (pascalType === 'ChatGPT') return 'openai';
  if (pascalType === 'GeminiText') return 'google';
  if (pascalType === 'GrokText') return 'xai';
  if (pascalType === 'Kimi' || pascalType === 'Qwen' || pascalType === 'Minimax') return 'ollama';

  // Image models routed by handler type
  if (handler === 'image' || handler === 'fluxImage' || handler === 'falAIImage') {
    // GPTImage handler is 'image' but goes to OpenAI; Gemini image (ImagePrompt) → google.
    if (pascalType === 'GPTImage') return 'openai';
    if (pascalType === 'ImagePrompt') return 'google';
    if (pascalType === 'FluxImage') return 'falai';
    return 'falai';
  }
  if (handler === 'xaiImage') return 'xai';

  // Video models
  if (handler === 'video') return 'google'; // Veo via Google (routed through fal.ai)
  if (handler === 'falAIVideo') return 'falai';
  if (handler === 'xaiVideo') return 'xai';
  if (handler === 'heygenVideo') return 'falai';

  return null;
}

/**
 * Upload a base64 data URL image to R2 storage and return the storage URL
 * This ensures generated images are stored in R2, not in the flow state
 * Note: External URLs (http/https) are returned as-is - backend should handle R2 upload
 */
async function uploadImageToStorage(
  imageUrl: string | undefined,
  nodeId?: string,
  /**
   * Flow to register the asset under, captured at GENERATION START by the
   * caller. Falls back to getCurrentFlowId() for call sites that don't
   * thread it — but callers should pass it: reading the current flow at
   * completion time attributes the asset to the wrong flow if the user
   * switched flows while a long generation was running.
   */
  flowId?: string,
): Promise<string> {
  // Guard against undefined or empty imageUrl
  if (!imageUrl) {
    logger.warn('[NodeAdapter] uploadImageToStorage called with undefined/empty imageUrl');
    throw new Error('No image URL provided for storage upload');
  }

  // Check if it's already an R2 URL (our storage) or an external URL
  const r2PublicUrl = import.meta.env.VITE_R2_PUBLIC_URL || 'r2.dev';
  if (imageUrl.includes('r2.dev') || imageUrl.includes(r2PublicUrl)) {
    return imageUrl;
  }

  // External URLs should be handled server-side to avoid CORS issues
  // Return as-is and let the backend handle the upload
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    logger.debug('[NodeAdapter] External URL detected, returning as-is (should be uploaded server-side):', imageUrl.substring(0, 100));
    return imageUrl;
  }

  // Handle base64 data URLs - upload to R2
  if (!imageUrl.startsWith('data:')) {
    return imageUrl;
  }

  const match = imageUrl.match(/^data:([^;]+);base64,(.+)$/);
  if (!match) {
    logger.warn('[NodeAdapter] Invalid data URL format, returning as-is');
    return imageUrl;
  }

  const mimeType = match[1];
  const base64Data = match[2];
  const binary = atob(base64Data);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    array[i] = binary.charCodeAt(i);
  }

  // Determine file extension
  const extensions: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
  };
  const extension = extensions[mimeType] || 'png';
  const fileName = `generated-${Date.now()}.${extension}`;
  const file = new File([array], fileName, { type: mimeType });

  const targetFlowId = flowId ?? flowStorageService.getCurrentFlowId() ?? undefined;

  // Upload with one retry. Generated images are unique bytes, so dedup
  // buys nothing and skipping it avoids the cross-scope dedup-cache hit
  // that can resolve a flow-scoped upload to a global-scoped object.
  const attemptUpload = () => fileStorageService.uploadFile(file, {
    nodeId,
    folder: 'generated-images',
    isPublic: true,
    scope: 'flow',
    flowId: targetFlowId,
    skipDeduplication: true,
  });

  try {
    let fileInfo;
    try {
      fileInfo = await attemptUpload();
    } catch (firstError) {
      logger.warn('[NodeAdapter] Image upload failed, retrying once:', firstError);
      fileInfo = await attemptUpload();
    }

    logger.debug('[NodeAdapter] Image uploaded to R2:', {
      originalUrl: imageUrl.substring(0, 100),
      storedUrl: fileInfo.url,
      fileId: fileInfo.id,
      scope: 'flow',
      flowId: targetFlowId,
    });

    return fileInfo.url;
  } catch (error) {
    // Both attempts failed. Return the displayable data: URL so the user
    // doesn't lose the image they paid credits for, but surface the
    // failure loudly — a silent fallback here is exactly how images end
    // up rendering forever without ever appearing in the asset gallery.
    logger.error('[NodeAdapter] Image upload failed after retry — image will NOT appear in Flow Assets:', error);
    toastService.error(
      'Image generated, but saving to Flow Assets failed. It will display here but won\'t appear in the gallery. Try duplicating the output or re-generating.'
    );
    return imageUrl;
  }
}

/**
 * Node Adapters
 * Bridge between old node interface and new UniversalNode architecture
 * These adapters wrap UniversalNode with the specific node definition and data handlers
 */

/**
 * Extended node data interface for type safety
 */
interface NodeAdapterData {
  prompt?: string;
  files?: File[];
  selectedModel?: string;
  parameters?: Record<string, unknown>;
  pages?: Array<{ content: string; type?: string; timestamp?: string | number; context?: unknown }>;
  currentPageIndex?: number;
  output?: string;
  content?: string;
  imageUrl?: string;
  videoUrl?: string;
  customNodeDefinition?: {
    name?: string;
    inputs?: Array<{ id: string; variableName?: string }>;
    outputs?: Array<{ id: string }>;
    systemPrompt?: string;
    model?: string;
    [key: string]: unknown;
  };
  variables?: Record<string, string>;
  onOutput?: (data: unknown) => void;
  onReceiveInput?: (handler: (data: unknown) => void) => void;
  fileContexts?: unknown[];
  [key: string]: unknown;
}

/**
 * Create a node adapter that wraps UniversalNode with a specific definition.
 * Results are cached so repeated calls with the same nodeType return the same
 * component reference, preventing ReactFlow from seeing new nodeTypes objects.
 */
const _adapterCache = new Map<string, React.FC<NodeProps>>();
function createNodeAdapter(nodeType: string) {
  const cached = _adapterCache.get(nodeType);
  if (cached) return cached;
  const adapter = (props: NodeProps) => {
    const { data: rawData, id } = props;
    const data = rawData as NodeAdapterData;
    const { setNodes, setEdges, getNode, getEdges } = useReactFlow();
    const { refreshBalance } = useCredits();
    const { providers: byokProviders } = useByokKeys();
    const { markDirty } = useAutoSaveContext();

    // Does the user have their own key for this node's provider? If so, the
    // generation runs on their key and doesn't spend Knods credits.
    const usesOwnKey = useMemo(() => {
      const provider = getNodeByokProvider(nodeType);
      return provider != null && byokProviders.has(provider);
    }, [byokProviders]);

    const batchCancelRef = useRef(false);

    const getBatchSettings = useCallback((): { batchCount: number; batchOutputMode: 'single' | 'multiple'; parameters: Record<string, any> } => {
      const parameters = (data.parameters || {}) as Record<string, any>;
      const rawCount = parameters.batchCount;
      const parsedCount = typeof rawCount === 'number' ? rawCount : parseInt(String(rawCount || '1'), 10);
      const batchCount = Math.min(10, Math.max(1, Number.isFinite(parsedCount) ? parsedCount : 1));
      const batchOutputMode: 'single' | 'multiple' = parameters.batchOutputMode === 'multiple' ? 'multiple' : 'single';
      return { batchCount, batchOutputMode, parameters };
    }, [data.parameters]);

    const requestBatchCancel = useCallback(() => {
      batchCancelRef.current = true;
      // Module-level registry: reaches the running batch loop no matter
      // which component instance started it (survives scroll remounts).
      generationLifecycleService.requestCancel(id);
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, batchCancelRequested: true } }
            : node
        )
      );
    }, [id, setNodes]);

    // Batch loops must poll THIS, not batchCancelRef directly. With
    // onlyRenderVisibleElements, scrolling the node off-screen remounts the
    // component: a batch loop started by the old instance keeps polling the
    // OLD ref while the remounted node's Cancel button writes the NEW ref —
    // the click silently does nothing and the batch keeps burning credits.
    // The GenerationLifecycleService registry (module-level) and node.data
    // (ReactFlow store) both survive remounts; the ref is a fast path for
    // the same-instance case.
    const isBatchCancelRequested = useCallback((): boolean => {
      if (batchCancelRef.current) return true;
      if (generationLifecycleService.isCancelRequested(id)) return true;
      return Boolean((getNode(id)?.data as Record<string, unknown> | undefined)?.batchCancelRequested);
    }, [id, getNode]);

    const resetBatchCancel = useCallback(() => {
      batchCancelRef.current = false;
      generationLifecycleService.clearCancel(id);
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, batchCancelRequested: false } }
            : node
        )
      );
    }, [id, setNodes]);

    const getCustomDimensions = useCallback((parameters: Record<string, any>) => {
      if (parameters.aspectRatio !== 'custom') {
        return null;
      }
      const width = Number(parameters.customWidth);
      const height = Number(parameters.customHeight);
      if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
        return null;
      }
      return { width: Math.round(width), height: Math.round(height) };
    }, []);

    const getClosestAspectRatio = useCallback((width: number, height: number): string => {
      const ratio = width / height;
      const candidates = [
        { ratio: 1 / 1, value: '1:1' },
        { ratio: 9 / 16, value: '9:16' },
        { ratio: 16 / 9, value: '16:9' },
        { ratio: 4 / 3, value: '4:3' },
        { ratio: 3 / 4, value: '3:4' },
        { ratio: 3 / 2, value: '3:2' },
        { ratio: 2 / 3, value: '2:3' },
        { ratio: 5 / 4, value: '5:4' },
        { ratio: 4 / 5, value: '4:5' },
        { ratio: 21 / 9, value: '21:9' },
      ];
      let closest = candidates[0];
      let bestDiff = Math.abs(ratio - closest.ratio);
      for (const candidate of candidates) {
        const diff = Math.abs(ratio - candidate.ratio);
        if (diff < bestDiff) {
          bestDiff = diff;
          closest = candidate;
        }
      }
      return closest.value;
    }, []);

    const resizeImageToDimensions = useCallback(async (imageUrl: string, width: number, height: number) => {
      try {
        const response = await fetch(imageUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch image for resize (${response.status})`);
        }
        const blob = await response.blob();
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error('Canvas not supported');
        }

        if ('createImageBitmap' in window) {
          const bitmap = await createImageBitmap(blob);
          ctx.drawImage(bitmap, 0, 0, width, height);
          bitmap.close();
        } else {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          const imgLoaded = new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject(new Error('Failed to load image for resize'));
          });
          img.src = URL.createObjectURL(blob);
          await imgLoaded;
          ctx.drawImage(img, 0, 0, width, height);
          URL.revokeObjectURL(img.src);
        }

        const resizedBlob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob((result) => {
            if (!result) {
              reject(new Error('Failed to create resized image blob'));
              return;
            }
            resolve(result);
          }, 'image/png');
        });

        const fileName = `generated-${width}x${height}-${Date.now()}.png`;
        const file = new File([resizedBlob], fileName, { type: 'image/png' });
        const currentFlowId = flowStorageService.getCurrentFlowId();
        const fileInfo = await fileStorageService.uploadFile(file, {
          nodeId: id,
          folder: 'generated-images',
          isPublic: true,
          scope: 'flow',
          flowId: currentFlowId || undefined,
        });
        return fileInfo.url;
      } catch (error) {
        logger.warn('[NodeAdapter] Failed to resize image to custom dimensions, using original:', error);
        return imageUrl;
      }
    }, [id]);

    // addImageToOutputs moved to GenerationLifecycleService.deliverImageOutput
    // (module-level, survives node unmount; owns the batch output node id).
    
    // Subscribe to edges targeting this node for real-time validation updates
    // Uses custom equality to only re-render when the relevant edge IDs change
    const incomingEdges = useStore(
      (state) => state.edges.filter((edge) => edge.target === id),
      (a, b) => {
        if (a.length !== b.length) return false;
        // Compare edge IDs and source handles to detect actual changes
        for (let i = 0; i < a.length; i++) {
          if (a[i].id !== b[i].id || a[i].sourceHandle !== b[i].sourceHandle) return false;
        }
        return true;
      }
    );
    
    // Subscribe to source nodes' text content for real-time send button updates
    // This enables the send button when connected source nodes have text output
    const connectedTextContent = useStore(
      (state) => readConnectedText(state.edges, (nodeId) => state.nodes.find((n) => n.id === nodeId), id),
      (a, b) => {
        // Only re-render when the content changes from empty to non-empty or vice versa
        return (!!a.trim()) === (!!b.trim());
      }
    );

    // Live character count of the connected text, for the ElevenLabs char-based
    // credit badge. connectedTextContent above cannot serve this: its equality
    // fn only distinguishes empty from non-empty, so the badge would never see
    // the length change. Numbers compare by identity, so this subscription is
    // always fresh.
    //
    // isAudioGen is resolved ONCE outside the selector on purpose. nodeType is a
    // compile-time constant per adapter, and a selector body runs for every node
    // on every store transaction — including every mousemove of a drag, since
    // positions live in the same store. Two registry lookups in there would be
    // paid by all 100+ nodes of a flow to serve three audio ones.
    const isAudioGen = isAudioGenNode(nodeType);
    const connectedTextCharCount = useStore((state) =>
      isAudioGen
        ? readConnectedText(state.edges, (nodeId) => state.nodes.find((n) => n.id === nodeId), id).length
        : 0
    );

    // Subscribe to connected video URL for VideoTrim/VideoSplit preview
    const connectedVideoUrl = useStore((state) => {
      const edges = state.edges.filter((edge) => edge.target === id);
      for (const edge of edges) {
        const sourceNode = state.nodes.find((n) => n.id === edge.source);
        if (sourceNode?.type === 'customOutput' || sourceNode?.type === 'universalOutput') {
          const pages = (sourceNode.data?.pages || []) as Array<{ content: string; type?: string }>;
          const idx = (sourceNode.data?.currentPageIndex || 0) as number;
          if (pages[idx]?.type === 'video' && pages[idx]?.content) {
            return pages[idx].content as string;
          }
        }
      }
      return '';
    });

    // ── Detect connected image dimensions for ImageScale ──
    useEffect(() => {
      if (nodeType !== 'ImageScale') return;

      let imageUrl: string | undefined;
      for (const edge of incomingEdges) {
        const sourceNode = getNode(edge.source);
        if (!sourceNode) continue;
        if (sourceNode.type === 'customOutput' || sourceNode.type === 'universalOutput') {
          const pages = (sourceNode.data?.pages || []) as Array<{ content: string; type?: string }>;
          const idx = (sourceNode.data?.currentPageIndex || 0) as number;
          if (pages[idx]?.type === 'image' && pages[idx]?.content) {
            imageUrl = pages[idx].content;
            break;
          }
        } else if (sourceNode.type === 'imagePanel' && sourceNode.data?.imageUrl) {
          imageUrl = sourceNode.data.imageUrl as string;
          break;
        }
      }

      if (!imageUrl) {
        setNodes((nodes) => nodes.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, inputWidth: undefined, inputHeight: undefined } } : n
        ));
        return;
      }

      const img = new Image();
      img.onload = () => {
        setNodes((nodes) => nodes.map((n) => {
          if (n.id !== id) return n;
          const params = (n.data.parameters || {}) as Record<string, any>;
          const updates: Record<string, any> = {
            inputWidth: img.naturalWidth,
            inputHeight: img.naturalHeight,
          };
          // Auto-fill W/H when selectors are both "Original" and values haven't been set
          if ((!params.aspectRatio || params.aspectRatio === 'original') &&
              (!params.resolution || params.resolution === 'original')) {
            updates.parameters = {
              ...params,
              targetWidth: img.naturalWidth,
              targetHeight: img.naturalHeight,
            };
          }
          return { ...n, data: { ...n.data, ...updates } };
        }));
      };
      img.src = imageUrl;
    }, [nodeType, id, incomingEdges, getNode, setNodes]);

    // Gather upstream context from connected nodes for prompt improvement
    // This provides text and image context to the "Improve with AI" feature
    const upstreamContext = useMemo(() => {
      const textContexts: string[] = [];
      const imageUrls: string[] = [];
      
      for (const edge of incomingEdges) {
        const sourceNode = getNode(edge.source);
        if (!sourceNode) continue;
        
        // Check Output nodes with pages
        if ((sourceNode.type === 'customOutput' || sourceNode.type === 'universalOutput') && sourceNode.data) {
          const pages = (sourceNode.data?.pages || []) as Array<{ content: string; type?: string }>;
          const currentPageIndex = (sourceNode.data?.currentPageIndex || 0) as number;
          
          if (pages.length > 0 && pages[currentPageIndex]) {
            const currentPage = pages[currentPageIndex];
            if (currentPage.type === 'image' && currentPage.content) {
              imageUrls.push(currentPage.content);
            } else if (currentPage.type === 'text' || !currentPage.type) {
              if (currentPage.content?.trim()) {
                textContexts.push(currentPage.content);
              }
            }
          }
        }
        // Check Image Panel nodes
        else if (sourceNode.type === 'imagePanel' && sourceNode.data?.imageUrl) {
          imageUrls.push(sourceNode.data.imageUrl as string);
        }
        // Check Text/Prompt nodes
        else if (sourceNode.data) {
          const content = sourceNode.data.output || sourceNode.data.content || sourceNode.data.prompt;
          if (content && typeof content === 'string' && content.trim()) {
            textContexts.push(content);
          }
        }
      }
      
      return {
        inputContext: textContexts.length > 0 ? textContexts.join('\n\n---\n\n') : undefined,
        imageContext: imageUrls.length > 0 
          ? `${imageUrls.length} image(s) connected from upstream nodes` 
          : undefined,
        imageUrls: imageUrls.length > 0 ? imageUrls : undefined,
      };
    }, [incomingEdges, getNode]);
    
    // Get the node definition
    const definition = getNodeDefinition(nodeType);
    
    if (!definition) {
      console.error('[NodeAdapter] No definition found for nodeType:', nodeType);
      return (
        <div style={{ padding: '16px', color: '#EF4444' }}>
          Node definition not found: {nodeType}
        </div>
      );
    }

    // Helper: resolve a parameter value, falling back to definition setting default
    const getParam = useCallback((parameters: Record<string, any>, key: string, hardcodedFallback: string): string => {
      if (parameters[key] != null && parameters[key] !== '') return String(parameters[key]);
      // Look up the default from the node definition's settings
      const setting = definition.settings?.find(s => s.id === key);
      if (setting?.default != null) return String(setting.default);
      return hardcodedFallback;
    }, [definition.settings]);

    // Create data change handlers that update React Flow state
    const handlePromptChange = useCallback((value: string) => {
      recordUserAction('Edit prompt', { coalesceKey: `text:${id}:prompt` });
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id ? { ...node, data: { ...node.data, prompt: value } } : node
        )
      );
      markDirty(); // Trigger auto-save
    }, [id, setNodes, markDirty]);

    const handleFilesChange = useCallback((files: File[]) => {
      recordUserAction('Change files');
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id ? { ...node, data: { ...node.data, files } } : node
        )
      );
      markDirty(); // Trigger auto-save
    }, [id, setNodes, markDirty]);

    const handleModelChange = useCallback((modelId: string) => {
      recordUserAction('Change model', { coalesceKey: `setting:${id}:selectedModel` });
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id ? { ...node, data: { ...node.data, selectedModel: modelId } } : node
        )
      );
      markDirty(); // Trigger auto-save
    }, [id, setNodes, markDirty]);

    const handleParameterChange = useCallback((parameterId: string, value: number | boolean | string) => {
      recordUserAction('Change setting', { coalesceKey: `setting:${id}:${parameterId}` });
      console.log('🔧 [NodeAdapter] Parameter changed:', { parameterId, value, valueType: typeof value });
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? {
                ...node,
                data: {
                  ...node.data,
                  parameters: {
                    ...(node.data.parameters || {}),
                    [parameterId]: value,
                  },
                },
              }
            : node
        )
      );
      markDirty(); // Trigger auto-save
    }, [id, setNodes, markDirty]);

    const handlePageChange = useCallback((pageIndex: number) => {
      setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            const pages = Array.isArray(node.data.pages) ? node.data.pages : [];
            const page = pages[pageIndex];
            if (page) {
              // Determine the output type based on page content type
              const outputType = page.type || 'text';
              
              // Calculate new height based on aspect ratio for image/video content
              let newHeight = node.height;
              const currentWidth = node.measured?.width || node.width || 320;
              const contentAreaOffset = 68; // Header + status bar (no padding for edge-to-edge media)
              const minHeight = 200;
              
              // If this page has a cached aspect ratio and it's image/video, adjust height
              if ((outputType === 'image' || outputType === 'video') && page.aspectRatio) {
                const idealContentHeight = currentWidth / page.aspectRatio;
                newHeight = Math.max(minHeight, idealContentHeight + contentAreaOffset);
              }
              
              return {
                ...node,
                // Only update height if we have a valid aspect ratio for media content
                ...(newHeight !== node.height && { height: newHeight }),
                data: {
                  ...node.data,
                  currentPageIndex: pageIndex,
                  content: page.content, // Update current content
                  type: outputType, // Update output media type
                  outputsMediaType: outputType, // Broadcast the media type
                  currentAspectRatio: page.aspectRatio || null,
                  // Preserve the appropriate media field based on type
                  ...(outputType === 'text' && { output: page.content }),
                  ...(outputType === 'image' && { imageUrl: page.content }),
                  ...(outputType === 'video' && { videoUrl: page.content }),
                  ...(outputType === 'audio' && { audioUrl: page.content }),
                }
              };
            }
          }
          return node;
        })
      );
      markDirty(); // Trigger auto-save for page changes
    }, [id, setNodes, markDirty]);

    const handleContentChange = useCallback((value: string) => {
      recordUserAction('Edit text', { coalesceKey: `text:${id}:content`, touchesResults: [id] });
      setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            const pages = Array.isArray(node.data.pages) ? node.data.pages : [];
            const currentPageIndex = node.data.currentPageIndex ?? 0;
            
            // Update both content and the current page content
            const updatedPages = pages.map((page, idx) =>
              idx === currentPageIndex ? { ...page, content: value } : page
            );
            
            return {
              ...node,
              data: {
                ...node.data,
                content: value,
                pages: updatedPages,
              }
            };
          }
          return node;
        })
      );
      markDirty(); // Trigger auto-save
    }, [id, setNodes, markDirty]);

    // Handle video trim - creates a new output node with the trimmed video (like inpaint)
    const handleVideoTrim = useCallback((trimmedVideoUrl: string) => {
      logger.debug('[NodeAdapter] Video trimmed, creating new output node:', { nodeId: id, trimmedUrl: trimmedVideoUrl.substring(0, 50) });
      
      // Get source node position
      const sourceNode = getNode(id);
      if (!sourceNode) {
        logger.error('[NodeAdapter] Source node not found for video trim:', id);
        return;
      }

      // Calculate position for new output node (offset to the right)
      const sourceWidth = sourceNode.measured?.width || sourceNode.width || 320;
      const newX = sourceNode.position.x + sourceWidth + 50;
      const newY = sourceNode.position.y;

      // Create new output node with the trimmed video
      const outputNodeId = `trimmedVideo-${Date.now()}`;
      
      const initialPage = {
        content: trimmedVideoUrl,
        context: null,
        type: 'video' as const,
        timestamp: new Date().toISOString()
      };
      
      const outputNode = {
        id: outputNodeId,
        type: 'universalOutput',
        position: { x: newX, y: newY },
        width: MEDIA_OUTPUT_DIMS.width,
        height: MEDIA_OUTPUT_DIMS.height,
        data: {
          output: trimmedVideoUrl,
          content: trimmedVideoUrl,
          type: 'video',
          outputsMediaType: 'video',
          videoUrl: trimmedVideoUrl,
          pages: [initialPage],
          currentPageIndex: 0,
          streamingInProgress: false
        }
      };

      // Create edge from source to new output
      const edge = {
        id: `${id}-${outputNodeId}`,
        source: id,
        target: outputNodeId,
        type: 'video',
        animated: true,
        style: { stroke: '#FF4B42', strokeWidth: 3 }
      };

      // Add the new node and edge
      setNodes((nodes) => [...nodes, outputNode]);
      setEdges((edges) => [...edges, edge]);

      logger.debug('[NodeAdapter] Created trimmed video output node:', outputNodeId);
    }, [id, getNode, setNodes, setEdges]);

    // Handle direct media upload into Output node (when empty + unconnected)
    const handleOutputMediaUpload = useCallback((imageData: ImageData) => {
      recordUserAction('Upload media', { touchesResults: [id] });
      const type = imageData.url.match(/\.(mp4|webm|mov|avi|mkv|m4v)(\?|$)/i) ? 'video' : 'image';
      const newPage = {
        content: imageData.url,
        context: null,
        type: type as 'image' | 'video',
        timestamp: new Date().toISOString(),
        aspectRatio: imageData.width && imageData.height ? imageData.width / imageData.height : undefined,
      };
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? {
                ...node,
                data: {
                  ...node.data,
                  content: imageData.url,
                  type,
                  outputsMediaType: type,
                  pages: [newPage],
                  currentPageIndex: 0,
                  ...(type === 'image' ? { imageUrl: imageData.url } : { videoUrl: imageData.url }),
                },
              }
            : node
        )
      );
      markDirty();
    }, [id, setNodes, markDirty]);

    // Handle file contexts being dropped.
    // Upserts by fileId rather than blindly appending: FileDropZone calls this twice
    // for a file whose context is analysed on demand (once with an "analyzing"
    // placeholder, once with the resolved context), and dropping the same asset twice
    // should not produce two chips either.
    const handleFilesDropped = useCallback((fileContexts: FileContext[]) => {
      // The second call for an on-demand analysed file only resolves the chip's
      // context (async completion) — only a genuinely new file is a user step.
      const existingIds = new Set(
        ((getNode(id)?.data?.fileContexts as FileContext[] | undefined) || []).map((c) => c?.fileId)
      );
      if (fileContexts.some((c) => !existingIds.has(c.fileId))) {
        recordUserAction('Attach files', { coalesceKey: `files:${id}` });
      }
      setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id !== id) return node;
          const existing = Array.isArray(node.data.fileContexts)
            ? [...(node.data.fileContexts as FileContext[])]
            : [];
          fileContexts.forEach((incoming) => {
            const index = existing.findIndex(
              (entry) => entry && (entry as FileContext).fileId === incoming.fileId
            );
            if (index >= 0) {
              existing[index] = { ...existing[index], ...incoming };
            } else {
              existing.push(incoming);
            }
          });
          return { ...node, data: { ...node.data, fileContexts: existing } };
        })
      );
      markDirty(); // Trigger auto-save
    }, [id, setNodes, markDirty, getNode]);

    // Handle transcription result (for Dictation node) - auto-outputs to connected nodes
    const handleTranscription = useCallback((text: string) => {
      logger.debug('[NodeAdapter] Transcription received:', { nodeId: id, textLength: text.length });
      
      // Update node data
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? {
                ...node,
                data: {
                  ...node.data,
                  transcription: text,
                  output: text, // Also set as output for downstream nodes
                },
              }
            : node
        )
      );
      markDirty(); // Trigger auto-save

      // Auto-output to connected nodes (like Writer does)
      if (text.trim()) {
        const allEdges = getEdges();
        const outgoingEdges = allEdges.filter(edge => edge.source === id);
        
        if (outgoingEdges.length === 0) {
          // No connections - create output node with transcription
          const sourceNode = getNode(id);
          if (sourceNode) {
            const sourceWidth = sourceNode.measured?.width || sourceNode.width || 320;
            const outputNodeId = `output-${Date.now()}`;
            
            const initialPage = {
              content: text,
              context: null,
              type: 'text' as const,
              timestamp: new Date().toISOString()
            };
            
            const outputNode = {
              id: outputNodeId,
              type: 'universalOutput',
              position: {
                x: sourceNode.position.x + sourceWidth + 50,
                y: sourceNode.position.y
              },
              width: TEXT_OUTPUT_DIMS.width,
              height: TEXT_OUTPUT_DIMS.height,
              data: {
                output: text,
                content: text,
                type: 'text',
                outputsMediaType: 'text',
                pages: [initialPage],
                currentPageIndex: 0,
              }
            };
            
            const edge = {
              id: `${id}-${outputNodeId}`,
              source: id,
              target: outputNodeId,
              type: 'text',
              animated: true,
              style: { stroke: '#FED402', strokeWidth: 3 }
            };
            
            setNodes((nodes) => [...nodes, outputNode]);
            setEdges((edges) => [...edges, edge]);
            
            logger.debug('[NodeAdapter] Created output node for transcription:', outputNodeId);
          }
        } else {
          // Update existing connected nodes
          for (const edge of outgoingEdges) {
            const targetNode = getNode(edge.target);
            if (targetNode) {
              // If it's an Output node, add new page or update current
              if (targetNode.type === 'universalOutput' || targetNode.type === 'customOutput') {
                const existingPages = Array.isArray(targetNode.data.pages) ? targetNode.data.pages : [];
                const currentPageIndex = (targetNode.data.currentPageIndex as number) || 0;
                const currentPage = existingPages[currentPageIndex];
                
                // If current page has content, create a new page
                if (currentPage && currentPage.content && currentPage.content.trim() !== '') {
                  const newPage = {
                    content: text,
                    context: null,
                    type: 'text' as const,
                    timestamp: new Date().toISOString()
                  };
                  
                  setNodes((nodes) =>
                    nodes.map((node) => {
                      if (node.id === edge.target) {
                        return {
                          ...node,
                          data: {
                            ...node.data,
                            output: text,
                            content: text,
                            type: 'text',
                            outputsMediaType: 'text',
                            pages: [...existingPages, newPage],
                            currentPageIndex: existingPages.length,
                          }
                        };
                      }
                      return node;
                    })
                  );
                } else {
                  // Update current empty page
                  const updatedPages = [...existingPages];
                  if (updatedPages[currentPageIndex]) {
                    updatedPages[currentPageIndex] = {
                      ...updatedPages[currentPageIndex],
                      content: text,
                      timestamp: new Date().toISOString()
                    };
                  } else {
                    updatedPages.push({
                      content: text,
                      context: null,
                      type: 'text' as const,
                      timestamp: new Date().toISOString()
                    });
                  }
                  
                  setNodes((nodes) =>
                    nodes.map((node) => {
                      if (node.id === edge.target) {
                        return {
                          ...node,
                          data: {
                            ...node.data,
                            output: text,
                            content: text,
                            type: 'text',
                            outputsMediaType: 'text',
                            pages: updatedPages,
                          }
                        };
                      }
                      return node;
                    })
                  );
                }
              } else {
                // For processor nodes, create an Output node in between
                const sourceNode = getNode(id);
                if (sourceNode) {
                  const sourceWidth = sourceNode.measured?.width || sourceNode.width || 320;
                  const outputNodeId = `output-${Date.now()}`;
                  
                  const initialPage = {
                    content: text,
                    context: null,
                    type: 'text' as const,
                    timestamp: new Date().toISOString()
                  };
                  
                  const outputNode = {
                    id: outputNodeId,
                    type: 'universalOutput',
                    position: {
                      x: sourceNode.position.x + sourceWidth + 50,
                      y: sourceNode.position.y
                    },
                    width: TEXT_OUTPUT_DIMS.width,
                    height: TEXT_OUTPUT_DIMS.height,
                    data: {
                      output: text,
                      content: text,
                      type: 'text',
                      outputsMediaType: 'text',
                      pages: [initialPage],
                      currentPageIndex: 0,
                    }
                  };
                  
                  // Create edge from dictation to new output
                  const newEdge1 = {
                    id: `${id}-${outputNodeId}`,
                    source: id,
                    target: outputNodeId,
                    type: 'text',
                    animated: true,
                    style: { stroke: '#FED402', strokeWidth: 3 }
                  };
                  
                  // Create edge from new output to the processor
                  const newEdge2 = {
                    id: `${outputNodeId}-${targetNode.id}`,
                    source: outputNodeId,
                    target: targetNode.id,
                    type: 'any',
                    animated: true,
                    style: { stroke: '#6B7280', strokeWidth: 3 }
                  };
                  
                  // Remove old edge
                  setEdges((edges) => edges.filter(e => e.id !== edge.id));
                  
                  // Add new node and edges
                  setNodes((nodes) => [...nodes, outputNode]);
                  setEdges((edges) => [...edges, newEdge1, newEdge2]);
                  
                  logger.debug('[NodeAdapter] Inserted output node between dictation and processor:', outputNodeId);
                }
              }
            }
          }
          
          logger.debug('[NodeAdapter] Auto-output transcription to connected nodes');
        }
      }
    }, [id, setNodes, setEdges, getEdges, getNode]);

    // Handle transcription edit (for Dictation node - when user edits the transcribed text)
    const handleTranscriptionEdit = useCallback((text: string) => {
      recordUserAction('Edit transcription', { coalesceKey: `text:${id}:transcription`, touchesResults: [id] });
      logger.debug('[NodeAdapter] Transcription edited:', { nodeId: id, textLength: text.length });
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? {
                ...node,
                data: {
                  ...node.data,
                  transcription: text,
                  output: text, // Also update output for downstream nodes
                },
              }
            : node
        )
      );
      markDirty(); // Trigger auto-save
    }, [id, setNodes, markDirty]);

    // Condense request function - just returns condensed text (for modal use)
    const handleCondenseRequest = useCallback(async (textToCondense: string): Promise<string> => {
      logger.debug('[NodeAdapter] Condense request:', { nodeId: id, textLength: textToCondense.length });
      
      const response = await openAIService.generateResponse(
        textToCondense,
        'You are a helpful assistant that condenses and summarizes text. Take the following transcribed text and condense it into a clear, concise version that captures the main ideas and key points. Remove filler words, repetitions, and unnecessary details while preserving the essential meaning. Return only the condensed text, nothing else.'
      );

      const condensedText = response.content.trim();
      
      logger.debug('[NodeAdapter] Text condensed:', { 
        nodeId: id, 
        originalLength: textToCondense.length, 
        condensedLength: condensedText.length 
      });
      
      return condensedText;
    }, [id]);

    // Handle condense (for node footer button - creates new page with condensed text)
    const handleCondense = useCallback(async () => {
      // Get the text content - could be from transcription (Dictation) or pages (Output)
      const transcription = data.transcription as string;
      const pages = data.pages as Array<{ content: string; type: 'text' | 'image' | 'video'; timestamp?: string }> | undefined;
      const currentPageIndex = (data.currentPageIndex as number) || 0;
      
      // For Output nodes, get text from current page
      let textToCondense = transcription;
      let isOutputNode = false;
      
      if (!textToCondense?.trim() && pages && pages.length > 0) {
        const currentPage = pages[currentPageIndex];
        if (currentPage && currentPage.type === 'text') {
          textToCondense = currentPage.content;
          isOutputNode = true;
        }
      }
      
      if (!textToCondense?.trim()) {
        return;
      }

      logger.debug('[NodeAdapter] Condensing text:', { nodeId: id, textLength: textToCondense.length, isOutputNode });

      // Set condensing state and clear any previous error
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, isCondensing: true, error: null } }
            : node
        )
      );

      try {
        const condensedText = await handleCondenseRequest(textToCondense);

        // Update the node with condensed version
        setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id !== id) return node;
            
            if (isOutputNode && pages) {
              // For Output nodes, ADD a new page with condensed content
              const newPage = {
                content: condensedText,
                type: 'text' as const,
                timestamp: new Date().toISOString(),
              };
              const updatedPages = [...pages, newPage];
              const newPageIndex = updatedPages.length - 1;
              
              return {
                ...node,
                data: {
                  ...node.data,
                  pages: updatedPages,
                  currentPageIndex: newPageIndex,
                  output: condensedText,
                  isCondensing: false,
                },
              };
            } else {
              // For Dictation nodes, update transcription
              return {
                ...node,
                data: {
                  ...node.data,
                  transcription: condensedText,
                  output: condensedText,
                  isCondensing: false,
                },
              };
            }
          })
        );
      } catch (error) {
        logger.error('[NodeAdapter] Condensation failed:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to condense text';
        setNodes((nodes) =>
          nodes.map((node) =>
            node.id === id
              ? { ...node, data: { ...node.data, isCondensing: false, error: errorMessage } }
              : node
          )
        );
      }
    }, [id, data.transcription, data.pages, data.currentPageIndex, setNodes, handleCondenseRequest]);

    // Handle language change (for Dictation node)
    const handleLanguageChange = useCallback((language: string) => {
      recordUserAction('Change language');
      logger.debug('[NodeAdapter] Language changed:', { nodeId: id, language });
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? {
                ...node,
                data: {
                  ...node.data,
                  language,
                },
              }
            : node
        )
      );
      markDirty(); // Trigger auto-save
    }, [id, setNodes, markDirty]);

    // Handle recording complete (for Dictation node)
    const handleRecordingComplete = useCallback((audioBlob: Blob, audioUrl: string) => {
      logger.debug('[NodeAdapter] Recording complete:', { nodeId: id, size: audioBlob.size });
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? {
                ...node,
                data: {
                  ...node.data,
                  audioBlob,
                  audioUrl,
                },
              }
            : node
        )
      );
      // Note: Recording data is temporary and doesn't need auto-save
    }, [id, setNodes]);

    // Handle credit deduction request from AudioRecorder
    // Note: Credit deduction is now handled server-side only.
    // The realtime subscription in CreditsContext updates the UI when the server deducts credits.
    // This callback is kept for API compatibility but is a no-op.
    const handleDeductCredits = useCallback((_amount: number) => {
      // No-op: Server handles credit deduction now
      logger.debug('[NodeAdapter] Credit deduction requested (no-op, server handles this):', { nodeId: id, amount: _amount });
    }, [id]);

    // Handle prompt submission (Cmd+Enter)
    const handleSubmit = useCallback(async (promptOverride?: string) => {
      const prompt = typeof promptOverride === 'string'
        ? promptOverride
        : typeof data.prompt === 'string'
          ? data.prompt
          : '';
      const { batchCount } = getBatchSettings();
      
      // Allow submission if there's either a prompt or connected text content
      if (!prompt.trim() && !connectedTextContent.trim()) {
        const errorMsg = 'Please enter a prompt first';
        alertService.error(errorMsg);
        throw new Error(errorMsg); // Throw to signal failure to ExecutionController
      }

      // Prevent multiple simultaneous submissions
      if (data.isProcessing) {
        logger.debug('[NodeAdapter] Already processing, ignoring submit');
        return;
      }

      logger.debug('[NodeAdapter] Submit pressed:', { nodeId: id, prompt });

      // Set processing state
      generationLifecycleService.start(id);
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? { 
                ...node, 
                data: { 
                  ...node.data, 
                  isProcessing: true, 
                  error: null,
                  batchCancelRequested: false,
                  batchProgress: { current: 0, total: batchCount }
                } 
              }
            : node
        )
      );

      resetBatchCancel();

      try {
        // Check if OpenAI is configured
        if (!openAIService.isConfigured()) {
          throw new Error('OpenAI not configured. Please check your .env file.');
        }

        const isStartingPrompt = nodeType === 'StartingPrompt';
        
        // Thread Management: Create new thread or use existing
        let currentThreadId = typeof data.threadId === 'string' ? data.threadId : null;
        
        // Get current brand voice from flow storage service (globally set) or fallback to node's data
        const currentBrandVoiceId = flowStorageService.getCurrentBrandVoiceId() 
          || (typeof data.brandVoiceId === 'string' ? data.brandVoiceId : undefined);
        
        // Check if we need to create a new thread:
        // 1. Starting Prompt always creates new thread
        // 2. No existing thread
        // 3. Brand voice has changed since thread was created
        let needsNewThread = isStartingPrompt || !currentThreadId;
        
        if (!needsNewThread && currentThreadId) {
          // Check if brand voice has changed
          const threadBrandVoiceId = threadManagementService.getThreadBrandVoiceId(currentThreadId);
          if (threadBrandVoiceId !== currentBrandVoiceId) {
            logger.debug('[NodeAdapter] Brand voice changed, creating new thread:', {
              oldBrandVoiceId: threadBrandVoiceId,
              newBrandVoiceId: currentBrandVoiceId
            });
            needsNewThread = true;
          }
        }
        
        if (needsNewThread) {
          // Create new thread with Brand Voice if selected
          let brandVoice = '';
          
          if (currentBrandVoiceId) {
            const brandVoiceData = await brandVoiceStorageService.getBrandVoice(currentBrandVoiceId);
            if (brandVoiceData) {
              brandVoice = brandVoiceData.instructions;
              logger.debug('[NodeAdapter] Using brand voice:', brandVoiceData.name);
            }
          }
          
          currentThreadId = threadManagementService.createThread(brandVoice, undefined, currentBrandVoiceId);
          
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id
                ? { ...node, data: { ...node.data, threadId: currentThreadId } }
                : node
            )
          );
          
          logger.debug('[NodeAdapter] Created new thread:', currentThreadId, brandVoice ? 'with brand voice' : 'without brand voice');
        } else {
          logger.debug('[NodeAdapter] Using existing thread:', currentThreadId);
        }
        
        // Get thread context (create a copy to avoid mutating the original)
        const originalContext = threadManagementService.getThreadContext(currentThreadId as string) || { messages: [] };
        const threadContext = {
          ...originalContext,
          messages: [...(originalContext.messages || [])]
        };
        
        // Build context from file contexts
        const fileContexts = Array.isArray(data.fileContexts) ? data.fileContexts : [];
        
        // Gather context from upstream connected nodes via edges
        const incomingEdgesForContext = getEdges();
        const upstreamContextMessages: ChatMessage[] = [];
        const upstreamTextContexts: string[] = [];
        
        // Find all edges that connect TO this node (target = this node's id)
        const incomingEdges = incomingEdgesForContext.filter(edge => edge.target === id);
        
        for (const edge of incomingEdges) {
          const sourceNode = getNode(edge.source);
          if (sourceNode) {
            // Check if source node is an Output node with pages (both customOutput and universalOutput)
            if ((sourceNode.type === 'customOutput' || sourceNode.type === 'universalOutput') && sourceNode.data) {
              const pages = (sourceNode.data.pages || []) as Array<{ content: string; type?: string; timestamp: number }>;
              const currentPageIndex = (sourceNode.data.currentPageIndex || 0) as number;
              
              // Get the content from the CURRENT page
              if (pages.length > 0 && pages[currentPageIndex]) {
                const currentPage = pages[currentPageIndex];
                
                // Handle different content types
                if (currentPage.type === 'image' && currentPage.content) {
                  // Add image as multimodal content (prevents token overflow)
                  upstreamContextMessages.push({
                    role: 'user',
                    content: [{
                      type: 'image',
                      imageUrl: currentPage.content,
                      mimeType: 'image/png'
                    }]
                  });
                  logger.debug('[NodeAdapter] Added image from Output node to upstream context:', {
                    sourceNodeId: sourceNode.id,
                    currentPageIndex,
                    totalPages: pages.length
                  });
                } else if (currentPage.type === 'text' || !currentPage.type) {
                  // Add text content
                  upstreamTextContexts.push(currentPage.content);
                  logger.debug('[NodeAdapter] Added text from Output node to upstream context:', {
                    sourceNodeId: sourceNode.id,
                    currentPageIndex,
                    totalPages: pages.length,
                    contentPreview: currentPage.content.substring(0, 50) + '...'
                  });
                } else if (currentPage.type === 'video') {
                  // Video URLs as text reference (videos can't be passed to text models)
                  upstreamTextContexts.push(`[Video: ${currentPage.content}]`);
                  logger.debug('[NodeAdapter] Added video reference from Output node:', {
                    sourceNodeId: sourceNode.id,
                    videoUrl: currentPage.content.substring(0, 100) + '...'
                  });
                }
              } else if (sourceNode.data.output || sourceNode.data.content) {
                // Fallback: use output or content field if pages is empty
                const outputContent = (sourceNode.data.output || sourceNode.data.content) as string;
                if (outputContent) {
                  upstreamTextContexts.push(outputContent);
                  logger.debug('[NodeAdapter] Added text from Output node (fallback) to upstream context:', {
                    sourceNodeId: sourceNode.id,
                    contentPreview: outputContent.substring(0, 50) + '...'
                  });
                }
              }
            }
            // Check if source node is a DocumentPanel with content
            else if (sourceNode.type === 'documentPanel' && sourceNode.data?.content) {
              const content = sourceNode.data.content as string;
              upstreamTextContexts.push(content);
              logger.debug('[NodeAdapter] Added upstream context from DocumentPanel node:', {
                sourceNodeId: sourceNode.id,
                fileName: sourceNode.data.fileName,
                contentPreview: content.substring(0, 50) + '...'
              });
            }
            // Check if source node is an Image Container
            else if (sourceNode.type === 'imagePanel' && sourceNode.data?.imageUrl) {
              upstreamContextMessages.push({
                role: 'user',
                content: [{
                  type: 'image',
                  imageUrl: sourceNode.data.imageUrl as string,
                  mimeType: 'image/png'
                }]
              });
              logger.debug('[NodeAdapter] Added image from Image Container node to upstream context');
            }
            // For other node types, use their output if available
            else if (sourceNode.data?.output) {
              const output = sourceNode.data.output as string;
              upstreamTextContexts.push(output);
              logger.debug('[NodeAdapter] Added upstream context from node:', {
                sourceNodeId: sourceNode.id,
                sourceNodeType: sourceNode.type,
                contentPreview: output.substring(0, 50) + '...'
              });
            }
          }
        }
        
        // Prepend upstream contexts to the thread context messages
        // This way the AI sees: [system] → [upstream outputs] → [conversation history] → [current prompt]
        if (upstreamContextMessages.length > 0 || upstreamTextContexts.length > 0) {
          // Find insertion point after system messages
          let insertionIndex = 0;
          if (threadContext.messages.length > 0) {
            const firstNonSystemIndex = threadContext.messages.findIndex(msg => msg.role !== 'system');
            insertionIndex = firstNonSystemIndex !== -1 ? firstNonSystemIndex : threadContext.messages.length;
          }
          
          // First add text contexts if any
          if (upstreamTextContexts.length > 0) {
            const textMessage = {
              role: 'user' as const,
              content: `Context from upstream nodes:\n\n${upstreamTextContexts.join('\n\n---\n\n')}`
            };
            threadContext.messages.splice(insertionIndex, 0, textMessage);
            insertionIndex++; // Move insertion point forward for images
          }
          
          // Then add multimodal messages (images) - these get inserted individually
          for (const msg of upstreamContextMessages) {
            threadContext.messages.splice(insertionIndex, 0, msg);
            insertionIndex++;
          }
          
          logger.debug('[NodeAdapter] Added upstream contexts to thread:', {
            textContexts: upstreamTextContexts.length,
            imageContexts: upstreamContextMessages.length,
            totalTextLength: upstreamTextContexts.join('\n\n').length
          });
        }
        
        // Call OpenAI service with streaming for instant feedback
        logger.debug('[NodeAdapter] Calling OpenAI with context:', {
          prompt,
          threadId: currentThreadId,
          fileCount: fileContexts.length,
          messageCount: threadContext.messages?.length || 0
        });
        
        // Check if output node already exists or needs to be created
        const allEdges = getEdges();
        const outgoingEdges = allEdges.filter(edge => edge.source === id);
        const existingOutputEdge = outgoingEdges.find(edge => {
          const targetNode = getNode(edge.target);
          return targetNode?.type === 'universalOutput' || targetNode?.type === 'customOutput';
        });
        
        let outputNodeId: string | null = null;
        
        if (!existingOutputEdge) {
          // Create output node immediately before streaming starts
          const sourceNode = getNode(id);
          if (sourceNode) {
            const sourceWidth = sourceNode.measured?.width || sourceNode.width || 320;
            outputNodeId = `customOutput-${Date.now()}`;
            
            const initialPage = {
              content: '',
              context: null,
              type: 'text' as const,
              timestamp: new Date().toISOString()
            };
            
            const outputNode = {
              id: outputNodeId,
              type: 'universalOutput',
              position: {
                x: sourceNode.position.x + sourceWidth + 50,
                y: sourceNode.position.y
              },
              width: TEXT_OUTPUT_DIMS.width,
              height: TEXT_OUTPUT_DIMS.height,
              data: {
                output: '',
                content: '',
                type: 'text',
                outputsMediaType: 'text',
                pages: [initialPage],
                currentPageIndex: 0,
                streamingInProgress: true
              }
            };
            
            const edge = {
              id: `${id}-${outputNodeId}`,
              source: id,
              target: outputNodeId,
              type: 'text',
              animated: true,
              style: { stroke: '#FED402', strokeWidth: 3 }
            };
            
            setNodes((nodes) => [...nodes, outputNode]);
            setEdges((edges) => [...edges, edge]);
            
            logger.debug('[NodeAdapter] Created output node for streaming:', outputNodeId);
          }
        } else {
          outputNodeId = existingOutputEdge.target;
          
          // Check if current page has content - if yes, create a new page for streaming
          const outputNode = getNode(outputNodeId);
          if (outputNode) {
            const nodeData = outputNode.data as NodeAdapterData;
            const existingPages = Array.isArray(nodeData.pages) ? nodeData.pages : [];
            const currentPageIndex = (nodeData.currentPageIndex as number) || 0;
            const currentPage = existingPages[currentPageIndex];
            
            // If current page has content, create a new page for streaming
            if (currentPage && currentPage.content && currentPage.content.trim() !== '') {
              const newPage = {
                content: '',
                context: null,
                type: 'text' as const,
                timestamp: new Date().toISOString()
              };
              
              setNodes((nodes) =>
                nodes.map((node) => {
                  if (node.id === outputNodeId) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        pages: [...existingPages, newPage],
                        currentPageIndex: existingPages.length, // Point to new page
                        streamingInProgress: true
                      }
                    };
                  }
                  return node;
                })
              );
              
              logger.debug('[NodeAdapter] Created new page for streaming in existing output node');
            } else {
              // Current page is empty or doesn't exist, use it for streaming
              setNodes((nodes) =>
                nodes.map((node) => {
                  if (node.id === outputNodeId) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        streamingInProgress: true
                      }
                    };
                  }
                  return node;
                })
              );
              
              logger.debug('[NodeAdapter] Using existing empty page for streaming');
            }
          }
          
          logger.debug('[NodeAdapter] Using existing output node for streaming:', outputNodeId);
        }
        
        let streamedContent = '';
        // Get selected model from node data, default to gpt-5-nano
        const selectedModel = (data.selectedModel as string) || 'gpt-5-nano';
        logger.debug('[NodeAdapter] Using model:', selectedModel);
        
        // Throttle UI updates to prevent React's "Maximum update depth exceeded" error
        // Batch updates using requestAnimationFrame (~60fps max) instead of every token
        let updateScheduled = false;
        
        const response = await openAIService.generateResponseStream(
          prompt,
          null, // System prompt is managed by thread
          threadContext,
          (delta: string) => {
            // Accumulate content and update UI in real-time
            streamedContent += delta;
            
            // Update output node with streamed content (throttled)
            if (outputNodeId && !updateScheduled) {
              updateScheduled = true;
              requestAnimationFrame(() => {
                updateScheduled = false;
                setNodes((nodes) =>
                  nodes.map((node) => {
                    if (node.id === outputNodeId) {
                      const existingPages = Array.isArray(node.data.pages) ? node.data.pages : [];
                      const currentPageIndex = typeof node.data.currentPageIndex === 'number' 
                        ? node.data.currentPageIndex 
                        : existingPages.length - 1;
                      
                      // Update the current page with streamed content
                      const updatedPages = existingPages.length > 0
                        ? existingPages.map((page, idx) => 
                            idx === currentPageIndex 
                              ? { ...page, content: streamedContent }
                              : page
                          )
                        : [{ content: streamedContent, type: 'text' as const, timestamp: new Date().toISOString() }];
                      
                      return {
                        ...node,
                        data: {
                          ...node.data,
                          content: streamedContent,
                          output: streamedContent,
                          pages: updatedPages,
                          streamingInProgress: true
                        }
                      };
                    }
                    return node;
                  })
                );
              });
            }
            
            logger.debug('[NodeAdapter] Streaming delta received:', { 
              delta: delta.substring(0, 20),
              totalLength: streamedContent.length,
              outputNodeId 
            });
          },
          selectedModel
        );
        
        logger.debug('[NodeAdapter] Received response:', response.content.substring(0, 100) + '...');
        
        // Update thread with response
        threadManagementService.appendMessage(currentThreadId as string, {
          role: 'user',
          content: prompt
        });
        
        threadManagementService.appendMessage(currentThreadId as string, {
          role: 'assistant',
          content: response.content
        });
        
        // Update node data with context and finalize streaming
        setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  context: response.context,
                  isProcessing: false,
                  streamingInProgress: false,
                  error: null
                }
              };
            }
            
            // Finalize output node with context
            if (outputNodeId && node.id === outputNodeId) {
              const existingPages = Array.isArray(node.data.pages) ? node.data.pages : [];
              // Finalize the SAME page the streaming filled (currentPageIndex),
              // not blindly the last page. When indices diverge (multi-output /
              // multi-page) writing length-1 overwrote the wrong page and left a
              // leftover empty placeholder behind.
              const finalizeIndex = typeof node.data.currentPageIndex === 'number'
                ? node.data.currentPageIndex
                : existingPages.length - 1;
              const finalPages = existingPages.length > 0
                ? existingPages.map((page, idx) =>
                    idx === finalizeIndex
                      ? {
                          ...page,
                          content: response.content,
                          context: response.context,
                          timestamp: new Date().toISOString()
                        }
                      : page
                  )
                : [{
                    content: response.content,
                    context: response.context,
                    type: 'text' as const,
                    timestamp: new Date().toISOString()
                  }];
              
              return {
                ...node,
                data: {
                  ...node.data,
                  content: response.content,
                  output: response.content,
                  pages: finalPages,
                  streamingInProgress: false
                }
              };
            }
            
            return node;
          })
        );
        
        logger.debug('[NodeAdapter] Streaming complete, output node updated:', outputNodeId);
        
        // Success - no notification needed, visual feedback in node status
      } catch (error: any) {
        logger.error('[NodeAdapter] Execution error:', error);
        setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return { ...node, data: { ...node.data, isProcessing: false, streamingInProgress: false, error: error.message || 'Execution failed' } };
            }
            return node;
          })
        );
        // Also reset streamingInProgress on any connected output nodes
        const errEdges = getEdges().filter(edge => edge.source === id);
        const outputIds = errEdges.map(e => e.target).filter(tid => {
          const n = getNode(tid);
          return n?.type === 'universalOutput' || n?.type === 'customOutput';
        });
        if (outputIds.length > 0) {
          setNodes((nodes) =>
            nodes.map((node) => outputIds.includes(node.id) ? { ...node, data: { ...node.data, streamingInProgress: false } } : node)
          );
        }
        showGenerationError(error, friendlyErrors.generationFailed);
      } finally {
        generationLifecycleService.finish(id);
      }
    }, [id, data.prompt, data.threadId, data.fileContexts, data.isProcessing, connectedTextContent, nodeType, setNodes, getBatchSettings]);

    // Handle Claude (Anthropic) text generation - similar to handleSubmit but uses ClaudeService
    const handleClaudeSubmit = useCallback(async (promptOverride?: string) => {
      const prompt = typeof promptOverride === 'string'
        ? promptOverride
        : typeof data.prompt === 'string'
          ? data.prompt
          : '';
      const { batchCount } = getBatchSettings();
      
      // Allow submission if there's either a prompt or connected text content
      if (!prompt.trim() && !connectedTextContent.trim()) {
        const errorMsg = 'Please enter a prompt first';
        alertService.error(errorMsg);
        throw new Error(errorMsg);
      }

      // Prevent multiple simultaneous submissions
      if (data.isProcessing) {
        logger.debug('[NodeAdapter] Claude: Already processing, ignoring submit');
        return;
      }

      logger.debug('[NodeAdapter] Claude Submit pressed:', { nodeId: id, prompt });

      // Set processing state
      generationLifecycleService.start(id);
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? { 
                ...node, 
                data: { 
                  ...node.data, 
                  isProcessing: true, 
                  error: null,
                  batchCancelRequested: false,
                  batchProgress: { current: 0, total: batchCount }
                } 
              }
            : node
        )
      );

      resetBatchCancel();

      try {
        // Check if Claude is configured
        if (!claudeService.isConfigured()) {
          throw new Error('Anthropic Claude not configured. Please check your ANTHROPIC_API_KEY in .env file.');
        }

        // Thread Management: Create new thread or use existing
        let currentThreadId = typeof data.threadId === 'string' ? data.threadId : null;
        
        // Get current brand voice from flow storage service (globally set) or fallback to node's data
        const currentBrandVoiceId = flowStorageService.getCurrentBrandVoiceId() 
          || (typeof data.brandVoiceId === 'string' ? data.brandVoiceId : undefined);
        
        // Check if we need to create a new thread
        let needsNewThread = !currentThreadId;
        
        if (!needsNewThread && currentThreadId) {
          // Check if brand voice has changed
          const threadBrandVoiceId = threadManagementService.getThreadBrandVoiceId(currentThreadId);
          if (threadBrandVoiceId !== currentBrandVoiceId) {
            logger.debug('[NodeAdapter] Claude: Brand voice changed, creating new thread');
            needsNewThread = true;
          }
        }
        
        if (needsNewThread) {
          // Create new thread with Brand Voice if selected
          let brandVoice = '';
          
          if (currentBrandVoiceId) {
            const brandVoiceData = await brandVoiceStorageService.getBrandVoice(currentBrandVoiceId);
            if (brandVoiceData) {
              brandVoice = brandVoiceData.instructions;
              logger.debug('[NodeAdapter] Claude: Using brand voice:', brandVoiceData.name);
            }
          }
          
          currentThreadId = threadManagementService.createThread(brandVoice, undefined, currentBrandVoiceId);
          
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id
                ? { ...node, data: { ...node.data, threadId: currentThreadId } }
                : node
            )
          );
          
          logger.debug('[NodeAdapter] Claude: Created new thread:', currentThreadId);
        }
        
        // Get thread context
        const originalContext = threadManagementService.getThreadContext(currentThreadId as string) || { messages: [] };
        const threadContext = {
          ...originalContext,
          messages: [...(originalContext.messages || [])]
        };
        
        // Gather context from upstream connected nodes
        const incomingEdgesForContext = getEdges();
        const upstreamContextMessages: ChatMessage[] = [];
        const upstreamTextContexts: string[] = [];
        
        const incomingEdges = incomingEdgesForContext.filter(edge => edge.target === id);
        
        for (const edge of incomingEdges) {
          const sourceNode = getNode(edge.source);
          if (sourceNode) {
            // Check for Output nodes with pages
            if ((sourceNode.type === 'customOutput' || sourceNode.type === 'universalOutput') && sourceNode.data) {
              const pages = (sourceNode.data.pages || []) as Array<{ content: string; type?: string; timestamp: number }>;
              const currentPageIndex = (sourceNode.data.currentPageIndex || 0) as number;
              
              if (pages.length > 0 && pages[currentPageIndex]) {
                const currentPage = pages[currentPageIndex];
                
                if (currentPage.type === 'image' && currentPage.content) {
                  upstreamContextMessages.push({
                    role: 'user',
                    content: [{
                      type: 'image',
                      imageUrl: currentPage.content,
                      mimeType: 'image/png'
                    }]
                  });
                } else if (currentPage.type === 'text' || !currentPage.type) {
                  upstreamTextContexts.push(currentPage.content);
                } else if (currentPage.type === 'video') {
                  upstreamTextContexts.push(`[Video: ${currentPage.content}]`);
                }
              } else if (sourceNode.data.output || sourceNode.data.content) {
                const outputContent = (sourceNode.data.output || sourceNode.data.content) as string;
                if (outputContent) {
                  upstreamTextContexts.push(outputContent);
                }
              }
            }
            // DocumentPanel
            else if (sourceNode.type === 'documentPanel' && sourceNode.data?.content) {
              upstreamTextContexts.push(sourceNode.data.content as string);
            }
            // ImagePanel
            else if (sourceNode.type === 'imagePanel' && sourceNode.data?.imageUrl) {
              upstreamContextMessages.push({
                role: 'user',
                content: [{
                  type: 'image',
                  imageUrl: sourceNode.data.imageUrl as string,
                  mimeType: 'image/png'
                }]
              });
            }
            // Other nodes with output
            else if (sourceNode.data?.output) {
              upstreamTextContexts.push(sourceNode.data.output as string);
            }
          }
        }
        
        // Prepend upstream contexts to thread context
        if (upstreamContextMessages.length > 0 || upstreamTextContexts.length > 0) {
          let insertionIndex = 0;
          if (threadContext.messages.length > 0) {
            const firstNonSystemIndex = threadContext.messages.findIndex(msg => msg.role !== 'system');
            insertionIndex = firstNonSystemIndex !== -1 ? firstNonSystemIndex : threadContext.messages.length;
          }
          
          if (upstreamTextContexts.length > 0) {
            const textMessage = {
              role: 'user' as const,
              content: `Context from upstream nodes:\n\n${upstreamTextContexts.join('\n\n---\n\n')}`
            };
            threadContext.messages.splice(insertionIndex, 0, textMessage);
            insertionIndex++;
          }
          
          for (const msg of upstreamContextMessages) {
            threadContext.messages.splice(insertionIndex, 0, msg);
            insertionIndex++;
          }
        }
        
        // Check if output node exists or needs to be created
        const allEdges = getEdges();
        const outgoingEdges = allEdges.filter(edge => edge.source === id);
        const existingOutputEdge = outgoingEdges.find(edge => {
          const targetNode = getNode(edge.target);
          return targetNode?.type === 'universalOutput' || targetNode?.type === 'customOutput';
        });
        
        let outputNodeId: string | null = null;
        
        if (!existingOutputEdge) {
          const sourceNode = getNode(id);
          if (sourceNode) {
            const sourceWidth = sourceNode.measured?.width || sourceNode.width || 320;
            outputNodeId = `customOutput-${Date.now()}`;
            
            const initialPage = {
              content: '',
              context: null,
              type: 'text' as const,
              timestamp: new Date().toISOString()
            };
            
            const outputNode = {
              id: outputNodeId,
              type: 'universalOutput',
              position: {
                x: sourceNode.position.x + sourceWidth + 50,
                y: sourceNode.position.y
              },
              width: TEXT_OUTPUT_DIMS.width,
              height: TEXT_OUTPUT_DIMS.height,
              data: {
                output: '',
                content: '',
                type: 'text',
                outputsMediaType: 'text',
                pages: [initialPage],
                currentPageIndex: 0,
                streamingInProgress: true
              }
            };
            
            const edge = {
              id: `${id}-${outputNodeId}`,
              source: id,
              target: outputNodeId,
              type: 'text',
              animated: true,
              style: { stroke: '#FED402', strokeWidth: 3 }
            };
            
            setNodes((nodes) => [...nodes, outputNode]);
            setEdges((edges) => [...edges, edge]);
            
            logger.debug('[NodeAdapter] Claude: Created output node:', outputNodeId);
          }
        } else {
          outputNodeId = existingOutputEdge.target;
          
          const outputNode = getNode(outputNodeId);
          if (outputNode) {
            const nodeData = outputNode.data as NodeAdapterData;
            const existingPages = Array.isArray(nodeData.pages) ? nodeData.pages : [];
            const currentPageIndex = (nodeData.currentPageIndex as number) || 0;
            const currentPage = existingPages[currentPageIndex];
            
            if (currentPage && currentPage.content && currentPage.content.trim() !== '') {
              const newPage = {
                content: '',
                context: null,
                type: 'text' as const,
                timestamp: new Date().toISOString()
              };
              
              setNodes((nodes) =>
                nodes.map((node) => {
                  if (node.id === outputNodeId) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        pages: [...existingPages, newPage],
                        currentPageIndex: existingPages.length,
                        streamingInProgress: true
                      }
                    };
                  }
                  return node;
                })
              );
            } else {
              setNodes((nodes) =>
                nodes.map((node) => {
                  if (node.id === outputNodeId) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        streamingInProgress: true
                      }
                    };
                  }
                  return node;
                })
              );
            }
          }
        }
        
        let streamedContent = '';
        // Must match CLAUDE_NODE_DEF's `default` and the registry's
        // initialData.selectedModel — this fallback had drifted to sonnet-4-6,
        // so a node without an explicit model ran a 5-credit model while the UI
        // and the server registry both said haiku (2 credits).
        const selectedModel = (data.selectedModel as string) || 'claude-haiku-4-5';
        logger.debug('[NodeAdapter] Claude: Using model:', selectedModel);
        
        let updateScheduled = false;
        
        const response = await claudeService.generateResponseStream(
          prompt,
          null,
          threadContext,
          (delta: string) => {
            streamedContent += delta;
            
            if (outputNodeId && !updateScheduled) {
              updateScheduled = true;
              requestAnimationFrame(() => {
                updateScheduled = false;
                setNodes((nodes) =>
                  nodes.map((node) => {
                    if (node.id === outputNodeId) {
                      const existingPages = Array.isArray(node.data.pages) ? node.data.pages : [];
                      const currentPageIndex = typeof node.data.currentPageIndex === 'number' 
                        ? node.data.currentPageIndex 
                        : existingPages.length - 1;
                      
                      const updatedPages = existingPages.length > 0
                        ? existingPages.map((page, idx) => 
                            idx === currentPageIndex 
                              ? { ...page, content: streamedContent }
                              : page
                          )
                        : [{ content: streamedContent, type: 'text' as const, timestamp: new Date().toISOString() }];
                      
                      return {
                        ...node,
                        data: {
                          ...node.data,
                          content: streamedContent,
                          output: streamedContent,
                          pages: updatedPages,
                          streamingInProgress: true
                        }
                      };
                    }
                    return node;
                  })
                );
              });
            }
          },
          selectedModel
        );
        
        logger.debug('[NodeAdapter] Claude: Received response:', response.content.substring(0, 100) + '...');
        
        // Update thread with response
        threadManagementService.appendMessage(currentThreadId as string, {
          role: 'user',
          content: prompt
        });
        
        threadManagementService.appendMessage(currentThreadId as string, {
          role: 'assistant',
          content: response.content
        });
        
        // Update node data and finalize streaming
        setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  context: response.context,
                  isProcessing: false,
                  streamingInProgress: false,
                  error: null
                }
              };
            }
            
            if (outputNodeId && node.id === outputNodeId) {
              const existingPages = Array.isArray(node.data.pages) ? node.data.pages : [];
              // Finalize the SAME page the streaming filled (currentPageIndex),
              // not blindly the last page. When indices diverge (multi-output /
              // multi-page) writing length-1 overwrote the wrong page and left a
              // leftover empty placeholder behind.
              const finalizeIndex = typeof node.data.currentPageIndex === 'number'
                ? node.data.currentPageIndex
                : existingPages.length - 1;
              const finalPages = existingPages.length > 0
                ? existingPages.map((page, idx) =>
                    idx === finalizeIndex
                      ? {
                          ...page,
                          content: response.content,
                          context: response.context,
                          timestamp: new Date().toISOString()
                        }
                      : page
                  )
                : [{
                    content: response.content,
                    context: response.context,
                    type: 'text' as const,
                    timestamp: new Date().toISOString()
                  }];
              
              return {
                ...node,
                data: {
                  ...node.data,
                  content: response.content,
                  output: response.content,
                  pages: finalPages,
                  streamingInProgress: false
                }
              };
            }
            
            return node;
          })
        );
        
        logger.debug('[NodeAdapter] Claude: Streaming complete');
        
      } catch (error: any) {
        logger.error('[NodeAdapter] Claude execution error:', error);
        setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return { ...node, data: { ...node.data, isProcessing: false, streamingInProgress: false, error: error.message || 'Execution failed' } };
            }
            return node;
          })
        );
        // Also reset streamingInProgress on any connected output nodes
        const errEdges = getEdges().filter(edge => edge.source === id);
        const outputIds = errEdges.map(e => e.target).filter(tid => {
          const n = getNode(tid);
          return n?.type === 'universalOutput' || n?.type === 'customOutput';
        });
        if (outputIds.length > 0) {
          setNodes((nodes) =>
            nodes.map((node) => outputIds.includes(node.id) ? { ...node, data: { ...node.data, streamingInProgress: false } } : node)
          );
        }
        showGenerationError(error, friendlyErrors.generationFailed);
      } finally {
        generationLifecycleService.finish(id);
      }
    }, [id, data.prompt, data.threadId, data.isProcessing, connectedTextContent, setNodes, getBatchSettings]);

    // ========================
    // ========================
    // Handle Kimi submit (Moonshot AI via Ollama Cloud)
    // ========================
    const handleKimiSubmit = useCallback(async (promptOverride?: string) => {
      const prompt = typeof promptOverride === 'string'
        ? promptOverride
        : typeof data.prompt === 'string'
          ? data.prompt
          : '';

      if (!prompt.trim() && !connectedTextContent.trim()) {
        const errorMsg = 'Please enter a prompt first';
        alertService.error(errorMsg);
        throw new Error(errorMsg);
      }

      if (data.isProcessing) {
        logger.debug('[NodeAdapter] Kimi: Already processing, ignoring submit');
        return;
      }

      logger.debug('[NodeAdapter] Kimi Submit pressed:', { nodeId: id, prompt });

      generationLifecycleService.start(id);
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? {
                ...node,
                data: {
                  ...node.data,
                  isProcessing: true,
                  error: null,
                  batchCancelRequested: false,
                  batchProgress: { current: 0, total: 1 }
                }
              }
            : node
        )
      );

      try {
        if (!kimiService.isConfigured()) {
          throw new Error('Kimi not configured. Please check your OLLAMA_API_KEY in .env file.');
        }

        // Thread Management
        let currentThreadId = typeof data.threadId === 'string' ? data.threadId : null;
        const currentBrandVoiceId = flowStorageService.getCurrentBrandVoiceId()
          || (typeof data.brandVoiceId === 'string' ? data.brandVoiceId : undefined);

        let needsNewThread = !currentThreadId;
        if (!needsNewThread && currentThreadId) {
          const threadBrandVoiceId = threadManagementService.getThreadBrandVoiceId(currentThreadId);
          if (threadBrandVoiceId !== currentBrandVoiceId) {
            needsNewThread = true;
          }
        }

        if (needsNewThread) {
          let brandVoice = '';
          if (currentBrandVoiceId) {
            const brandVoiceData = await brandVoiceStorageService.getBrandVoice(currentBrandVoiceId);
            if (brandVoiceData) brandVoice = brandVoiceData.instructions;
          }
          currentThreadId = threadManagementService.createThread(brandVoice, undefined, currentBrandVoiceId);
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id
                ? { ...node, data: { ...node.data, threadId: currentThreadId } }
                : node
            )
          );
        }

        const originalContext = threadManagementService.getThreadContext(currentThreadId as string) || { messages: [] };
        const threadContext = {
          ...originalContext,
          messages: [...(originalContext.messages || [])]
        };

        // Gather upstream context
        const incomingEdgesForContext = getEdges();
        const upstreamTextContexts: string[] = [];
        const upstreamImageUrls: string[] = [];
        const incomingEdges = incomingEdgesForContext.filter(edge => edge.target === id);

        for (const edge of incomingEdges) {
          const sourceNode = getNode(edge.source);
          if (sourceNode) {
            if ((sourceNode.type === 'customOutput' || sourceNode.type === 'universalOutput') && sourceNode.data) {
              const pages = (sourceNode.data.pages || []) as Array<{ content: string; type?: string }>;
              const currentPageIndex = (sourceNode.data.currentPageIndex || 0) as number;
              if (pages.length > 0 && pages[currentPageIndex]) {
                const currentPage = pages[currentPageIndex];
                if (currentPage.type === 'image' && currentPage.content) {
                  upstreamImageUrls.push(currentPage.content);
                } else if (currentPage.type === 'text' || !currentPage.type) {
                  upstreamTextContexts.push(currentPage.content);
                } else if (currentPage.type === 'video') {
                  upstreamTextContexts.push(`[Video: ${currentPage.content}]`);
                }
              } else if (sourceNode.data.output || sourceNode.data.content) {
                upstreamTextContexts.push((sourceNode.data.output || sourceNode.data.content) as string);
              }
            }
            // Image Container nodes
            else if (sourceNode.type === 'imagePanel' && sourceNode.data?.imageUrl) {
              upstreamImageUrls.push(sourceNode.data.imageUrl as string);
            } else if (sourceNode.type === 'documentPanel' && sourceNode.data?.content) {
              upstreamTextContexts.push(sourceNode.data.content as string);
            } else if (sourceNode.data?.output) {
              upstreamTextContexts.push(sourceNode.data.output as string);
            }
          }
        }

        if (upstreamTextContexts.length > 0) {
          let insertionIndex = 0;
          if (threadContext.messages.length > 0) {
            const firstNonSystemIndex = threadContext.messages.findIndex(msg => msg.role !== 'system');
            insertionIndex = firstNonSystemIndex !== -1 ? firstNonSystemIndex : threadContext.messages.length;
          }
          threadContext.messages.splice(insertionIndex, 0, {
            role: 'user' as const,
            content: `Context from upstream nodes:\n\n${upstreamTextContexts.join('\n\n---\n\n')}`
          });
        }

        // Create or reuse output node
        const allEdges = getEdges();
        const outgoingEdges = allEdges.filter(edge => edge.source === id);
        const existingOutputEdge = outgoingEdges.find(edge => {
          const targetNode = getNode(edge.target);
          return targetNode?.type === 'universalOutput' || targetNode?.type === 'customOutput';
        });

        let outputNodeId: string | null = null;

        if (!existingOutputEdge) {
          const sourceNode = getNode(id);
          if (sourceNode) {
            const sourceWidth = sourceNode.measured?.width || sourceNode.width || 320;
            outputNodeId = `customOutput-${Date.now()}`;
            const outputNode = {
              id: outputNodeId,
              type: 'universalOutput',
              position: { x: sourceNode.position.x + sourceWidth + 50, y: sourceNode.position.y },
              width: TEXT_OUTPUT_DIMS.width,
              height: TEXT_OUTPUT_DIMS.height,
              data: {
                output: '', content: '', type: 'text', outputsMediaType: 'text',
                pages: [{ content: '', context: null, type: 'text' as const, timestamp: new Date().toISOString() }],
                currentPageIndex: 0, streamingInProgress: true
              }
            };
            const edge = {
              id: `${id}-${outputNodeId}`, source: id, target: outputNodeId,
              type: 'text', animated: true, style: { stroke: '#FED402', strokeWidth: 3 }
            };
            setNodes((nodes) => [...nodes, outputNode]);
            setEdges((edges) => [...edges, edge]);
          }
        } else {
          outputNodeId = existingOutputEdge.target;
          const outputNode = getNode(outputNodeId);
          if (outputNode) {
            const nodeData = outputNode.data as NodeAdapterData;
            const existingPages = Array.isArray(nodeData.pages) ? nodeData.pages : [];
            const currentPageIndex = (nodeData.currentPageIndex as number) || 0;
            const currentPage = existingPages[currentPageIndex];
            if (currentPage && currentPage.content && currentPage.content.trim() !== '') {
              setNodes((nodes) =>
                nodes.map((node) => {
                  if (node.id === outputNodeId) {
                    return { ...node, data: { ...node.data, pages: [...existingPages, { content: '', context: null, type: 'text' as const, timestamp: new Date().toISOString() }], currentPageIndex: existingPages.length, streamingInProgress: true } };
                  }
                  return node;
                })
              );
            } else {
              setNodes((nodes) =>
                nodes.map((node) => node.id === outputNodeId ? { ...node, data: { ...node.data, streamingInProgress: true } } : node)
              );
            }
          }
        }

        let streamedContent = '';
        const selectedModel = (data.selectedModel as string) || 'kimi-k2.6';
        let updateScheduled = false;

        const response = await kimiService.generateResponseStream(
          prompt,
          null,
          threadContext,
          (delta: string) => {
            streamedContent += delta;
            if (outputNodeId && !updateScheduled) {
              updateScheduled = true;
              requestAnimationFrame(() => {
                updateScheduled = false;
                setNodes((nodes) =>
                  nodes.map((node) => {
                    if (node.id === outputNodeId) {
                      const existingPages = Array.isArray(node.data.pages) ? node.data.pages : [];
                      const currentPageIndex = typeof node.data.currentPageIndex === 'number'
                        ? node.data.currentPageIndex
                        : existingPages.length - 1;
                      const updatedPages = existingPages.length > 0
                        ? existingPages.map((page, idx) =>
                            idx === currentPageIndex ? { ...page, content: streamedContent } : page
                          )
                        : [{ content: streamedContent, type: 'text' as const, timestamp: new Date().toISOString() }];
                      return { ...node, data: { ...node.data, content: streamedContent, output: streamedContent, pages: updatedPages, streamingInProgress: true } };
                    }
                    return node;
                  })
                );
              });
            }
          },
          selectedModel,
          upstreamImageUrls.length > 0 ? upstreamImageUrls : undefined
        );

        threadManagementService.appendMessage(currentThreadId as string, { role: 'user', content: prompt });
        threadManagementService.appendMessage(currentThreadId as string, { role: 'assistant', content: response.content });

        setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return { ...node, data: { ...node.data, context: response.context, isProcessing: false, streamingInProgress: false, error: null } };
            }
            if (outputNodeId && node.id === outputNodeId) {
              const existingPages = Array.isArray(node.data.pages) ? node.data.pages : [];
              const finalPages = existingPages.length > 0
                ? [...existingPages.slice(0, -1), { ...existingPages[existingPages.length - 1], content: response.content, context: response.context, timestamp: new Date().toISOString() }]
                : [{ content: response.content, context: response.context, type: 'text' as const, timestamp: new Date().toISOString() }];
              return { ...node, data: { ...node.data, content: response.content, output: response.content, pages: finalPages, streamingInProgress: false } };
            }
            return node;
          })
        );
      } catch (error: any) {
        logger.error('[NodeAdapter] Kimi execution error:', error);
        setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return { ...node, data: { ...node.data, isProcessing: false, streamingInProgress: false, error: error.message || 'Execution failed' } };
            }
            return node;
          })
        );
        // Also reset streamingInProgress on any connected output nodes
        const errEdges = getEdges().filter(edge => edge.source === id);
        const outputIds = errEdges.map(e => e.target).filter(tid => {
          const n = getNode(tid);
          return n?.type === 'universalOutput' || n?.type === 'customOutput';
        });
        if (outputIds.length > 0) {
          setNodes((nodes) =>
            nodes.map((node) => outputIds.includes(node.id) ? { ...node, data: { ...node.data, streamingInProgress: false } } : node)
          );
        }
        showGenerationError(error, friendlyErrors.generationFailed);
      } finally {
        generationLifecycleService.finish(id);
      }
    }, [id, data.prompt, data.threadId, data.isProcessing, connectedTextContent, setNodes, getBatchSettings]);

    // ========================
    // Handle Qwen submit (Alibaba via Ollama Cloud)
    // ========================
    const handleQwenSubmit = useCallback(async (promptOverride?: string) => {
      const prompt = typeof promptOverride === 'string'
        ? promptOverride
        : typeof data.prompt === 'string'
          ? data.prompt
          : '';

      if (!prompt.trim() && !connectedTextContent.trim()) {
        const errorMsg = 'Please enter a prompt first';
        alertService.error(errorMsg);
        throw new Error(errorMsg);
      }

      if (data.isProcessing) return;

      generationLifecycleService.start(id);
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, isProcessing: true, error: null, batchCancelRequested: false, batchProgress: { current: 0, total: 1 } } }
            : node
        )
      );

      try {
        if (!qwenService.isConfigured()) {
          throw new Error('Qwen not configured. Please check your OLLAMA_API_KEY in .env file.');
        }

        let currentThreadId = typeof data.threadId === 'string' ? data.threadId : null;
        const currentBrandVoiceId = flowStorageService.getCurrentBrandVoiceId()
          || (typeof data.brandVoiceId === 'string' ? data.brandVoiceId : undefined);

        if (!currentThreadId) {
          let brandVoice = '';
          if (currentBrandVoiceId) {
            const brandVoiceData = await brandVoiceStorageService.getBrandVoice(currentBrandVoiceId);
            if (brandVoiceData) brandVoice = brandVoiceData.instructions;
          }
          currentThreadId = threadManagementService.createThread(brandVoice, undefined, currentBrandVoiceId);
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id ? { ...node, data: { ...node.data, threadId: currentThreadId } } : node
            )
          );
        }

        const originalContext = threadManagementService.getThreadContext(currentThreadId as string) || { messages: [] };
        const threadContext = { ...originalContext, messages: [...(originalContext.messages || [])] };

        // Gather upstream text context and image URLs
        const incomingEdges = getEdges().filter(edge => edge.target === id);
        const upstreamTextContexts: string[] = [];
        const upstreamImageUrls: string[] = [];
        for (const edge of incomingEdges) {
          const sourceNode = getNode(edge.source);
          if (sourceNode) {
            if ((sourceNode.type === 'customOutput' || sourceNode.type === 'universalOutput') && sourceNode.data) {
              const pages = (sourceNode.data.pages || []) as Array<{ content: string; type?: string }>;
              const currentPageIndex = (sourceNode.data.currentPageIndex || 0) as number;
              if (pages.length > 0 && pages[currentPageIndex]) {
                const currentPage = pages[currentPageIndex];
                if (currentPage.type === 'image' && currentPage.content) {
                  upstreamImageUrls.push(currentPage.content);
                } else if (currentPage.type !== 'video') {
                  upstreamTextContexts.push(currentPage.content);
                }
              } else if (sourceNode.data.output || sourceNode.data.content) {
                upstreamTextContexts.push((sourceNode.data.output || sourceNode.data.content) as string);
              }
            } else if (sourceNode.data?.output) {
              upstreamTextContexts.push(sourceNode.data.output as string);
            }
          }
        }

        if (upstreamTextContexts.length > 0) {
          const firstNonSystemIndex = threadContext.messages.findIndex(msg => msg.role !== 'system');
          const insertionIndex = firstNonSystemIndex !== -1 ? firstNonSystemIndex : threadContext.messages.length;
          threadContext.messages.splice(insertionIndex, 0, {
            role: 'user' as const,
            content: `Context from upstream nodes:\n\n${upstreamTextContexts.join('\n\n---\n\n')}`
          });
        }

        // Create or reuse output node
        const allEdges = getEdges();
        const existingOutputEdge = allEdges.filter(edge => edge.source === id).find(edge => {
          const targetNode = getNode(edge.target);
          return targetNode?.type === 'universalOutput' || targetNode?.type === 'customOutput';
        });

        let outputNodeId: string | null = null;
        if (!existingOutputEdge) {
          const sourceNode = getNode(id);
          if (sourceNode) {
            const sourceWidth = sourceNode.measured?.width || sourceNode.width || 320;
            outputNodeId = `customOutput-${Date.now()}`;
            const outputNode = {
              id: outputNodeId, type: 'universalOutput',
              position: { x: sourceNode.position.x + sourceWidth + 50, y: sourceNode.position.y },
              width: TEXT_OUTPUT_DIMS.width, height: TEXT_OUTPUT_DIMS.height,
              data: { output: '', content: '', type: 'text', outputsMediaType: 'text', pages: [{ content: '', context: null, type: 'text' as const, timestamp: new Date().toISOString() }], currentPageIndex: 0, streamingInProgress: true }
            };
            const edge = { id: `${id}-${outputNodeId}`, source: id, target: outputNodeId, type: 'text', animated: true, style: { stroke: '#FED402', strokeWidth: 3 } };
            setNodes((nodes) => [...nodes, outputNode]);
            setEdges((edges) => [...edges, edge]);
          }
        } else {
          outputNodeId = existingOutputEdge.target;
          const outputNode = getNode(outputNodeId);
          if (outputNode) {
            const existingPages = Array.isArray(outputNode.data.pages) ? outputNode.data.pages : [];
            const currentPageIndex = (outputNode.data.currentPageIndex as number) || 0;
            const currentPage = existingPages[currentPageIndex];
            if (currentPage && currentPage.content && (currentPage.content as string).trim() !== '') {
              setNodes((nodes) => nodes.map((node) => {
                if (node.id === outputNodeId) {
                  return { ...node, data: { ...node.data, pages: [...existingPages, { content: '', context: null, type: 'text' as const, timestamp: new Date().toISOString() }], currentPageIndex: existingPages.length, streamingInProgress: true } };
                }
                return node;
              }));
            } else {
              setNodes((nodes) => nodes.map((node) => node.id === outputNodeId ? { ...node, data: { ...node.data, streamingInProgress: true } } : node));
            }
          }
        }

        let streamedContent = '';
        const selectedModel = (data.selectedModel as string) || 'qwen-3.5';
        let updateScheduled = false;

        const response = await qwenService.generateResponseStream(
          prompt, null, threadContext,
          (delta: string) => {
            streamedContent += delta;
            if (outputNodeId && !updateScheduled) {
              updateScheduled = true;
              requestAnimationFrame(() => {
                updateScheduled = false;
                setNodes((nodes) => nodes.map((node) => {
                  if (node.id === outputNodeId) {
                    const existingPages = Array.isArray(node.data.pages) ? node.data.pages : [];
                    const currentPageIndex = typeof node.data.currentPageIndex === 'number' ? node.data.currentPageIndex : existingPages.length - 1;
                    const updatedPages = existingPages.length > 0
                      ? existingPages.map((page, idx) => idx === currentPageIndex ? { ...page, content: streamedContent } : page)
                      : [{ content: streamedContent, type: 'text' as const, timestamp: new Date().toISOString() }];
                    return { ...node, data: { ...node.data, content: streamedContent, output: streamedContent, pages: updatedPages, streamingInProgress: true } };
                  }
                  return node;
                }));
              });
            }
          },
          selectedModel
        );

        threadManagementService.appendMessage(currentThreadId as string, { role: 'user', content: prompt });
        threadManagementService.appendMessage(currentThreadId as string, { role: 'assistant', content: response.content });

        setNodes((nodes) => nodes.map((node) => {
          if (node.id === id) return { ...node, data: { ...node.data, context: response.context, isProcessing: false, streamingInProgress: false, error: null } };
          if (outputNodeId && node.id === outputNodeId) {
            const existingPages = Array.isArray(node.data.pages) ? node.data.pages : [];
            const finalPages = existingPages.length > 0
              ? [...existingPages.slice(0, -1), { ...existingPages[existingPages.length - 1], content: response.content, context: response.context, timestamp: new Date().toISOString() }]
              : [{ content: response.content, context: response.context, type: 'text' as const, timestamp: new Date().toISOString() }];
            return { ...node, data: { ...node.data, content: response.content, output: response.content, pages: finalPages, streamingInProgress: false } };
          }
          return node;
        }));
      } catch (error: any) {
        logger.error('[NodeAdapter] Qwen execution error:', error);
        setNodes((nodes) => nodes.map((node) => {
          if (node.id === id) return { ...node, data: { ...node.data, isProcessing: false, streamingInProgress: false, error: error.message || 'Execution failed' } };
          return node;
        }));
        const errEdges = getEdges().filter(edge => edge.source === id);
        const outputIds = errEdges.map(e => e.target).filter(tid => { const n = getNode(tid); return n?.type === 'universalOutput' || n?.type === 'customOutput'; });
        if (outputIds.length > 0) {
          setNodes((nodes) => nodes.map((node) => outputIds.includes(node.id) ? { ...node, data: { ...node.data, streamingInProgress: false } } : node));
        }
        showGenerationError(error, friendlyErrors.generationFailed);
      } finally {
        generationLifecycleService.finish(id);
      }
    }, [id, data.prompt, data.threadId, data.isProcessing, connectedTextContent, setNodes, getBatchSettings]);

    // ========================
    // Handle MiniMax submit
    // ========================
    const handleMinimaxSubmit = useCallback(async (promptOverride?: string) => {
      const prompt = typeof promptOverride === 'string'
        ? promptOverride
        : typeof data.prompt === 'string'
          ? data.prompt
          : '';

      if (!prompt.trim() && !connectedTextContent.trim()) {
        const errorMsg = 'Please enter a prompt first';
        alertService.error(errorMsg);
        throw new Error(errorMsg);
      }

      if (data.isProcessing) return;

      generationLifecycleService.start(id);
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, isProcessing: true, error: null, batchCancelRequested: false, batchProgress: { current: 0, total: 1 } } }
            : node
        )
      );

      try {
        if (!minimaxService.isConfigured()) {
          throw new Error('MiniMax not configured. Please check your OLLAMA_API_KEY in .env file.');
        }

        let currentThreadId = typeof data.threadId === 'string' ? data.threadId : null;
        const currentBrandVoiceId = flowStorageService.getCurrentBrandVoiceId()
          || (typeof data.brandVoiceId === 'string' ? data.brandVoiceId : undefined);

        if (!currentThreadId) {
          let brandVoice = '';
          if (currentBrandVoiceId) {
            const brandVoiceData = await brandVoiceStorageService.getBrandVoice(currentBrandVoiceId);
            if (brandVoiceData) brandVoice = brandVoiceData.instructions;
          }
          currentThreadId = threadManagementService.createThread(brandVoice, undefined, currentBrandVoiceId);
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id ? { ...node, data: { ...node.data, threadId: currentThreadId } } : node
            )
          );
        }

        const originalContext = threadManagementService.getThreadContext(currentThreadId as string) || { messages: [] };
        const threadContext = { ...originalContext, messages: [...(originalContext.messages || [])] };

        // Gather upstream text context and image URLs
        const incomingEdges = getEdges().filter(edge => edge.target === id);
        const upstreamTextContexts: string[] = [];
        const upstreamImageUrls: string[] = [];
        for (const edge of incomingEdges) {
          const sourceNode = getNode(edge.source);
          if (sourceNode) {
            if ((sourceNode.type === 'customOutput' || sourceNode.type === 'universalOutput') && sourceNode.data) {
              const pages = (sourceNode.data.pages || []) as Array<{ content: string; type?: string }>;
              const currentPageIndex = (sourceNode.data.currentPageIndex || 0) as number;
              if (pages.length > 0 && pages[currentPageIndex]) {
                const currentPage = pages[currentPageIndex];
                if (currentPage.type === 'image' && currentPage.content) {
                  upstreamImageUrls.push(currentPage.content);
                } else if (currentPage.type !== 'video') {
                  upstreamTextContexts.push(currentPage.content);
                }
              } else if (sourceNode.data.output || sourceNode.data.content) {
                upstreamTextContexts.push((sourceNode.data.output || sourceNode.data.content) as string);
              }
            } else if (sourceNode.data?.output) {
              upstreamTextContexts.push(sourceNode.data.output as string);
            }
          }
        }

        if (upstreamTextContexts.length > 0) {
          const firstNonSystemIndex = threadContext.messages.findIndex(msg => msg.role !== 'system');
          const insertionIndex = firstNonSystemIndex !== -1 ? firstNonSystemIndex : threadContext.messages.length;
          threadContext.messages.splice(insertionIndex, 0, {
            role: 'user' as const,
            content: `Context from upstream nodes:\n\n${upstreamTextContexts.join('\n\n---\n\n')}`
          });
        }

        // Create or reuse output node
        const allEdges = getEdges();
        const existingOutputEdge = allEdges.filter(edge => edge.source === id).find(edge => {
          const targetNode = getNode(edge.target);
          return targetNode?.type === 'universalOutput' || targetNode?.type === 'customOutput';
        });

        let outputNodeId: string | null = null;
        if (!existingOutputEdge) {
          const sourceNode = getNode(id);
          if (sourceNode) {
            const sourceWidth = sourceNode.measured?.width || sourceNode.width || 320;
            outputNodeId = `customOutput-${Date.now()}`;
            const outputNode = {
              id: outputNodeId, type: 'universalOutput',
              position: { x: sourceNode.position.x + sourceWidth + 50, y: sourceNode.position.y },
              width: TEXT_OUTPUT_DIMS.width, height: TEXT_OUTPUT_DIMS.height,
              data: { output: '', content: '', type: 'text', outputsMediaType: 'text', pages: [{ content: '', context: null, type: 'text' as const, timestamp: new Date().toISOString() }], currentPageIndex: 0, streamingInProgress: true }
            };
            const edge = { id: `${id}-${outputNodeId}`, source: id, target: outputNodeId, type: 'text', animated: true, style: { stroke: '#FED402', strokeWidth: 3 } };
            setNodes((nodes) => [...nodes, outputNode]);
            setEdges((edges) => [...edges, edge]);
          }
        } else {
          outputNodeId = existingOutputEdge.target;
          const outputNode = getNode(outputNodeId);
          if (outputNode) {
            const existingPages = Array.isArray(outputNode.data.pages) ? outputNode.data.pages : [];
            const currentPageIndex = (outputNode.data.currentPageIndex as number) || 0;
            const currentPage = existingPages[currentPageIndex];
            if (currentPage && currentPage.content && (currentPage.content as string).trim() !== '') {
              setNodes((nodes) => nodes.map((node) => {
                if (node.id === outputNodeId) {
                  return { ...node, data: { ...node.data, pages: [...existingPages, { content: '', context: null, type: 'text' as const, timestamp: new Date().toISOString() }], currentPageIndex: existingPages.length, streamingInProgress: true } };
                }
                return node;
              }));
            } else {
              setNodes((nodes) => nodes.map((node) => node.id === outputNodeId ? { ...node, data: { ...node.data, streamingInProgress: true } } : node));
            }
          }
        }

        let streamedContent = '';
        const selectedModel = (data.selectedModel as string) || 'minimax-m2.7';
        let updateScheduled = false;

        const response = await minimaxService.generateResponseStream(
          prompt, null, threadContext,
          (delta: string) => {
            streamedContent += delta;
            if (outputNodeId && !updateScheduled) {
              updateScheduled = true;
              requestAnimationFrame(() => {
                updateScheduled = false;
                setNodes((nodes) => nodes.map((node) => {
                  if (node.id === outputNodeId) {
                    const existingPages = Array.isArray(node.data.pages) ? node.data.pages : [];
                    const currentPageIndex = typeof node.data.currentPageIndex === 'number' ? node.data.currentPageIndex : existingPages.length - 1;
                    const updatedPages = existingPages.length > 0
                      ? existingPages.map((page, idx) => idx === currentPageIndex ? { ...page, content: streamedContent } : page)
                      : [{ content: streamedContent, type: 'text' as const, timestamp: new Date().toISOString() }];
                    return { ...node, data: { ...node.data, content: streamedContent, output: streamedContent, pages: updatedPages, streamingInProgress: true } };
                  }
                  return node;
                }));
              });
            }
          },
          selectedModel,
          upstreamImageUrls.length > 0 ? upstreamImageUrls : undefined
        );

        threadManagementService.appendMessage(currentThreadId as string, { role: 'user', content: prompt });
        threadManagementService.appendMessage(currentThreadId as string, { role: 'assistant', content: response.content });

        setNodes((nodes) => nodes.map((node) => {
          if (node.id === id) return { ...node, data: { ...node.data, context: response.context, isProcessing: false, streamingInProgress: false, error: null } };
          if (outputNodeId && node.id === outputNodeId) {
            const existingPages = Array.isArray(node.data.pages) ? node.data.pages : [];
            const finalPages = existingPages.length > 0
              ? [...existingPages.slice(0, -1), { ...existingPages[existingPages.length - 1], content: response.content, context: response.context, timestamp: new Date().toISOString() }]
              : [{ content: response.content, context: response.context, type: 'text' as const, timestamp: new Date().toISOString() }];
            return { ...node, data: { ...node.data, content: response.content, output: response.content, pages: finalPages, streamingInProgress: false } };
          }
          return node;
        }));
      } catch (error: any) {
        logger.error('[NodeAdapter] MiniMax execution error:', error);
        setNodes((nodes) => nodes.map((node) => {
          if (node.id === id) return { ...node, data: { ...node.data, isProcessing: false, streamingInProgress: false, error: error.message || 'Execution failed' } };
          return node;
        }));
        const errEdges = getEdges().filter(edge => edge.source === id);
        const outputIds = errEdges.map(e => e.target).filter(tid => { const n = getNode(tid); return n?.type === 'universalOutput' || n?.type === 'customOutput'; });
        if (outputIds.length > 0) {
          setNodes((nodes) => nodes.map((node) => outputIds.includes(node.id) ? { ...node, data: { ...node.data, streamingInProgress: false } } : node));
        }
        showGenerationError(error, friendlyErrors.generationFailed);
      } finally {
        generationLifecycleService.finish(id);
      }
    }, [id, data.prompt, data.threadId, data.isProcessing, connectedTextContent, setNodes, getBatchSettings]);

    // ========================
    // Handle Gemini Text submit (Google AI direct)
    // Mirrors handleKimiSubmit but routes through GeminiTextService.
    // ========================
    const handleGeminiTextSubmit = useCallback(async (promptOverride?: string) => {
      const prompt = typeof promptOverride === 'string'
        ? promptOverride
        : typeof data.prompt === 'string'
          ? data.prompt
          : '';

      if (!prompt.trim() && !connectedTextContent.trim()) {
        const errorMsg = 'Please enter a prompt first';
        alertService.error(errorMsg);
        throw new Error(errorMsg);
      }

      if (data.isProcessing) return;

      generationLifecycleService.start(id);
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, isProcessing: true, error: null, batchCancelRequested: false, batchProgress: { current: 0, total: 1 } } }
            : node
        )
      );

      try {
        if (!geminiTextService.isConfigured()) {
          throw new Error('Gemini Text not available in this environment.');
        }

        let currentThreadId = typeof data.threadId === 'string' ? data.threadId : null;
        const currentBrandVoiceId = flowStorageService.getCurrentBrandVoiceId()
          || (typeof data.brandVoiceId === 'string' ? data.brandVoiceId : undefined);

        if (!currentThreadId) {
          let brandVoice = '';
          if (currentBrandVoiceId) {
            const brandVoiceData = await brandVoiceStorageService.getBrandVoice(currentBrandVoiceId);
            if (brandVoiceData) brandVoice = brandVoiceData.instructions;
          }
          currentThreadId = threadManagementService.createThread(brandVoice, undefined, currentBrandVoiceId);
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id ? { ...node, data: { ...node.data, threadId: currentThreadId } } : node
            )
          );
        }

        const originalContext = threadManagementService.getThreadContext(currentThreadId as string) || { messages: [] };
        const threadContext = { ...originalContext, messages: [...(originalContext.messages || [])] };

        // Gather upstream text + image context
        const incomingEdges = getEdges().filter(edge => edge.target === id);
        const upstreamTextContexts: string[] = [];
        const upstreamImageUrls: string[] = [];
        for (const edge of incomingEdges) {
          const sourceNode = getNode(edge.source);
          if (sourceNode) {
            if ((sourceNode.type === 'customOutput' || sourceNode.type === 'universalOutput') && sourceNode.data) {
              const pages = (sourceNode.data.pages || []) as Array<{ content: string; type?: string }>;
              const currentPageIndex = (sourceNode.data.currentPageIndex || 0) as number;
              if (pages.length > 0 && pages[currentPageIndex]) {
                const currentPage = pages[currentPageIndex];
                if (currentPage.type === 'image' && currentPage.content) {
                  upstreamImageUrls.push(currentPage.content);
                } else if (currentPage.type !== 'video') {
                  upstreamTextContexts.push(currentPage.content);
                }
              } else if (sourceNode.data.output || sourceNode.data.content) {
                upstreamTextContexts.push((sourceNode.data.output || sourceNode.data.content) as string);
              }
            } else if (sourceNode.type === 'imagePanel' && sourceNode.data?.imageUrl) {
              upstreamImageUrls.push(sourceNode.data.imageUrl as string);
            } else if (sourceNode.type === 'documentPanel' && sourceNode.data?.content) {
              upstreamTextContexts.push(sourceNode.data.content as string);
            } else if (sourceNode.data?.output) {
              upstreamTextContexts.push(sourceNode.data.output as string);
            }
          }
        }

        if (upstreamTextContexts.length > 0) {
          const firstNonSystemIndex = threadContext.messages.findIndex(msg => msg.role !== 'system');
          const insertionIndex = firstNonSystemIndex !== -1 ? firstNonSystemIndex : threadContext.messages.length;
          threadContext.messages.splice(insertionIndex, 0, {
            role: 'user' as const,
            content: `Context from upstream nodes:\n\n${upstreamTextContexts.join('\n\n---\n\n')}`
          });
        }

        // Create or reuse output node
        const allEdges = getEdges();
        const existingOutputEdge = allEdges.filter(edge => edge.source === id).find(edge => {
          const targetNode = getNode(edge.target);
          return targetNode?.type === 'universalOutput' || targetNode?.type === 'customOutput';
        });

        let outputNodeId: string | null = null;
        if (!existingOutputEdge) {
          const sourceNode = getNode(id);
          if (sourceNode) {
            const sourceWidth = sourceNode.measured?.width || sourceNode.width || 320;
            outputNodeId = `customOutput-${Date.now()}`;
            const outputNode = {
              id: outputNodeId, type: 'universalOutput',
              position: { x: sourceNode.position.x + sourceWidth + 50, y: sourceNode.position.y },
              width: TEXT_OUTPUT_DIMS.width, height: TEXT_OUTPUT_DIMS.height,
              data: { output: '', content: '', type: 'text', outputsMediaType: 'text', pages: [{ content: '', context: null, type: 'text' as const, timestamp: new Date().toISOString() }], currentPageIndex: 0, streamingInProgress: true }
            };
            const edge = { id: `${id}-${outputNodeId}`, source: id, target: outputNodeId, type: 'text', animated: true, style: { stroke: '#FED402', strokeWidth: 3 } };
            setNodes((nodes) => [...nodes, outputNode]);
            setEdges((edges) => [...edges, edge]);
          }
        } else {
          outputNodeId = existingOutputEdge.target;
          const outputNode = getNode(outputNodeId);
          if (outputNode) {
            const existingPages = Array.isArray(outputNode.data.pages) ? outputNode.data.pages : [];
            const currentPageIndex = (outputNode.data.currentPageIndex as number) || 0;
            const currentPage = existingPages[currentPageIndex];
            if (currentPage && currentPage.content && (currentPage.content as string).trim() !== '') {
              setNodes((nodes) => nodes.map((node) => {
                if (node.id === outputNodeId) {
                  return { ...node, data: { ...node.data, pages: [...existingPages, { content: '', context: null, type: 'text' as const, timestamp: new Date().toISOString() }], currentPageIndex: existingPages.length, streamingInProgress: true } };
                }
                return node;
              }));
            } else {
              setNodes((nodes) => nodes.map((node) => node.id === outputNodeId ? { ...node, data: { ...node.data, streamingInProgress: true } } : node));
            }
          }
        }

        let streamedContent = '';
        const selectedModel = (data.selectedModel as string) || 'gemini-3.5-flash';
        let updateScheduled = false;

        const response = await geminiTextService.generateResponseStream(
          prompt, null, threadContext,
          (delta: string) => {
            streamedContent += delta;
            if (outputNodeId && !updateScheduled) {
              updateScheduled = true;
              requestAnimationFrame(() => {
                updateScheduled = false;
                setNodes((nodes) => nodes.map((node) => {
                  if (node.id === outputNodeId) {
                    const existingPages = Array.isArray(node.data.pages) ? node.data.pages : [];
                    const currentPageIndex = typeof node.data.currentPageIndex === 'number' ? node.data.currentPageIndex : existingPages.length - 1;
                    const updatedPages = existingPages.length > 0
                      ? existingPages.map((page, idx) => idx === currentPageIndex ? { ...page, content: streamedContent } : page)
                      : [{ content: streamedContent, type: 'text' as const, timestamp: new Date().toISOString() }];
                    return { ...node, data: { ...node.data, content: streamedContent, output: streamedContent, pages: updatedPages, streamingInProgress: true } };
                  }
                  return node;
                }));
              });
            }
          },
          selectedModel,
          upstreamImageUrls.length > 0 ? upstreamImageUrls : undefined
        );

        threadManagementService.appendMessage(currentThreadId as string, { role: 'user', content: prompt });
        threadManagementService.appendMessage(currentThreadId as string, { role: 'assistant', content: response.content });

        setNodes((nodes) => nodes.map((node) => {
          if (node.id === id) return { ...node, data: { ...node.data, context: response.context, isProcessing: false, streamingInProgress: false, error: null } };
          if (outputNodeId && node.id === outputNodeId) {
            const existingPages = Array.isArray(node.data.pages) ? node.data.pages : [];
            const finalPages = existingPages.length > 0
              ? [...existingPages.slice(0, -1), { ...existingPages[existingPages.length - 1], content: response.content, context: response.context, timestamp: new Date().toISOString() }]
              : [{ content: response.content, context: response.context, type: 'text' as const, timestamp: new Date().toISOString() }];
            return { ...node, data: { ...node.data, content: response.content, output: response.content, pages: finalPages, streamingInProgress: false } };
          }
          return node;
        }));
      } catch (error: any) {
        logger.error('[NodeAdapter] Gemini Text execution error:', error);
        setNodes((nodes) => nodes.map((node) => {
          if (node.id === id) return { ...node, data: { ...node.data, isProcessing: false, streamingInProgress: false, error: error.message || 'Execution failed' } };
          return node;
        }));
        const errEdges = getEdges().filter(edge => edge.source === id);
        const outputIds = errEdges.map(e => e.target).filter(tid => { const n = getNode(tid); return n?.type === 'universalOutput' || n?.type === 'customOutput'; });
        if (outputIds.length > 0) {
          setNodes((nodes) => nodes.map((node) => outputIds.includes(node.id) ? { ...node, data: { ...node.data, streamingInProgress: false } } : node));
        }
        showGenerationError(error, friendlyErrors.generationFailed);
      } finally {
        generationLifecycleService.finish(id);
      }
    }, [id, data.prompt, data.threadId, data.isProcessing, connectedTextContent, setNodes, getBatchSettings]);

    // ========================
    // Handle Grok Text submit (xAI direct)
    // Mirrors handleGeminiTextSubmit above — same thread handling, same upstream
    // context gathering, same streaming output-node lifecycle. Only the service
    // and the default model differ. Kept as a sibling rather than folded into a
    // shared helper to match how claude/kimi/qwen/minimax/geminiText are each
    // written here.
    // ========================
    const handleGrokTextSubmit = useCallback(async (promptOverride?: string) => {
      const prompt = typeof promptOverride === 'string'
        ? promptOverride
        : typeof data.prompt === 'string'
          ? data.prompt
          : '';

      if (!prompt.trim() && !connectedTextContent.trim()) {
        const errorMsg = 'Please enter a prompt first';
        alertService.error(errorMsg);
        throw new Error(errorMsg);
      }

      if (data.isProcessing) return;

      generationLifecycleService.start(id);
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, isProcessing: true, error: null, batchCancelRequested: false, batchProgress: { current: 0, total: 1 } } }
            : node
        )
      );

      try {
        if (!grokTextService.isConfigured()) {
          throw new Error('Grok Text not available in this environment.');
        }

        let currentThreadId = typeof data.threadId === 'string' ? data.threadId : null;
        const currentBrandVoiceId = flowStorageService.getCurrentBrandVoiceId()
          || (typeof data.brandVoiceId === 'string' ? data.brandVoiceId : undefined);

        if (!currentThreadId) {
          let brandVoice = '';
          if (currentBrandVoiceId) {
            const brandVoiceData = await brandVoiceStorageService.getBrandVoice(currentBrandVoiceId);
            if (brandVoiceData) brandVoice = brandVoiceData.instructions;
          }
          currentThreadId = threadManagementService.createThread(brandVoice, undefined, currentBrandVoiceId);
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id ? { ...node, data: { ...node.data, threadId: currentThreadId } } : node
            )
          );
        }

        const originalContext = threadManagementService.getThreadContext(currentThreadId as string) || { messages: [] };
        const threadContext = { ...originalContext, messages: [...(originalContext.messages || [])] };

        // Gather upstream text + image context
        const incomingEdges = getEdges().filter(edge => edge.target === id);
        const upstreamTextContexts: string[] = [];
        const upstreamImageUrls: string[] = [];
        for (const edge of incomingEdges) {
          const sourceNode = getNode(edge.source);
          if (sourceNode) {
            if ((sourceNode.type === 'customOutput' || sourceNode.type === 'universalOutput') && sourceNode.data) {
              const pages = (sourceNode.data.pages || []) as Array<{ content: string; type?: string }>;
              const currentPageIndex = (sourceNode.data.currentPageIndex || 0) as number;
              if (pages.length > 0 && pages[currentPageIndex]) {
                const currentPage = pages[currentPageIndex];
                if (currentPage.type === 'image' && currentPage.content) {
                  upstreamImageUrls.push(currentPage.content);
                } else if (currentPage.type !== 'video') {
                  upstreamTextContexts.push(currentPage.content);
                }
              } else if (sourceNode.data.output || sourceNode.data.content) {
                upstreamTextContexts.push((sourceNode.data.output || sourceNode.data.content) as string);
              }
            } else if (sourceNode.type === 'imagePanel' && sourceNode.data?.imageUrl) {
              upstreamImageUrls.push(sourceNode.data.imageUrl as string);
            } else if (sourceNode.type === 'documentPanel' && sourceNode.data?.content) {
              upstreamTextContexts.push(sourceNode.data.content as string);
            } else if (sourceNode.data?.output) {
              upstreamTextContexts.push(sourceNode.data.output as string);
            }
          }
        }

        if (upstreamTextContexts.length > 0) {
          const firstNonSystemIndex = threadContext.messages.findIndex(msg => msg.role !== 'system');
          const insertionIndex = firstNonSystemIndex !== -1 ? firstNonSystemIndex : threadContext.messages.length;
          threadContext.messages.splice(insertionIndex, 0, {
            role: 'user' as const,
            content: `Context from upstream nodes:\n\n${upstreamTextContexts.join('\n\n---\n\n')}`
          });
        }

        // Create or reuse output node
        const allEdges = getEdges();
        const existingOutputEdge = allEdges.filter(edge => edge.source === id).find(edge => {
          const targetNode = getNode(edge.target);
          return targetNode?.type === 'universalOutput' || targetNode?.type === 'customOutput';
        });

        let outputNodeId: string | null = null;
        if (!existingOutputEdge) {
          const sourceNode = getNode(id);
          if (sourceNode) {
            const sourceWidth = sourceNode.measured?.width || sourceNode.width || 320;
            outputNodeId = `customOutput-${Date.now()}`;
            const outputNode = {
              id: outputNodeId, type: 'universalOutput',
              position: { x: sourceNode.position.x + sourceWidth + 50, y: sourceNode.position.y },
              width: TEXT_OUTPUT_DIMS.width, height: TEXT_OUTPUT_DIMS.height,
              data: { output: '', content: '', type: 'text', outputsMediaType: 'text', pages: [{ content: '', context: null, type: 'text' as const, timestamp: new Date().toISOString() }], currentPageIndex: 0, streamingInProgress: true }
            };
            const edge = { id: `${id}-${outputNodeId}`, source: id, target: outputNodeId, type: 'text', animated: true, style: { stroke: '#FED402', strokeWidth: 3 } };
            setNodes((nodes) => [...nodes, outputNode]);
            setEdges((edges) => [...edges, edge]);
          }
        } else {
          outputNodeId = existingOutputEdge.target;
          const outputNode = getNode(outputNodeId);
          if (outputNode) {
            const existingPages = Array.isArray(outputNode.data.pages) ? outputNode.data.pages : [];
            const currentPageIndex = (outputNode.data.currentPageIndex as number) || 0;
            const currentPage = existingPages[currentPageIndex];
            if (currentPage && currentPage.content && (currentPage.content as string).trim() !== '') {
              setNodes((nodes) => nodes.map((node) => {
                if (node.id === outputNodeId) {
                  return { ...node, data: { ...node.data, pages: [...existingPages, { content: '', context: null, type: 'text' as const, timestamp: new Date().toISOString() }], currentPageIndex: existingPages.length, streamingInProgress: true } };
                }
                return node;
              }));
            } else {
              setNodes((nodes) => nodes.map((node) => node.id === outputNodeId ? { ...node, data: { ...node.data, streamingInProgress: true } } : node));
            }
          }
        }

        let streamedContent = '';
        const selectedModel = (data.selectedModel as string) || 'grok-4.3';
        let updateScheduled = false;

        const response = await grokTextService.generateResponseStream(
          prompt, null, threadContext,
          (delta: string) => {
            streamedContent += delta;
            if (outputNodeId && !updateScheduled) {
              updateScheduled = true;
              requestAnimationFrame(() => {
                updateScheduled = false;
                setNodes((nodes) => nodes.map((node) => {
                  if (node.id === outputNodeId) {
                    const existingPages = Array.isArray(node.data.pages) ? node.data.pages : [];
                    const currentPageIndex = typeof node.data.currentPageIndex === 'number' ? node.data.currentPageIndex : existingPages.length - 1;
                    const updatedPages = existingPages.length > 0
                      ? existingPages.map((page, idx) => idx === currentPageIndex ? { ...page, content: streamedContent } : page)
                      : [{ content: streamedContent, type: 'text' as const, timestamp: new Date().toISOString() }];
                    return { ...node, data: { ...node.data, content: streamedContent, output: streamedContent, pages: updatedPages, streamingInProgress: true } };
                  }
                  return node;
                }));
              });
            }
          },
          selectedModel,
          upstreamImageUrls.length > 0 ? upstreamImageUrls : undefined
        );

        threadManagementService.appendMessage(currentThreadId as string, { role: 'user', content: prompt });
        threadManagementService.appendMessage(currentThreadId as string, { role: 'assistant', content: response.content });

        setNodes((nodes) => nodes.map((node) => {
          if (node.id === id) return { ...node, data: { ...node.data, context: response.context, isProcessing: false, streamingInProgress: false, error: null } };
          if (outputNodeId && node.id === outputNodeId) {
            const existingPages = Array.isArray(node.data.pages) ? node.data.pages : [];
            const finalPages = existingPages.length > 0
              ? [...existingPages.slice(0, -1), { ...existingPages[existingPages.length - 1], content: response.content, context: response.context, timestamp: new Date().toISOString() }]
              : [{ content: response.content, context: response.context, type: 'text' as const, timestamp: new Date().toISOString() }];
            return { ...node, data: { ...node.data, content: response.content, output: response.content, pages: finalPages, streamingInProgress: false } };
          }
          return node;
        }));
      } catch (error: any) {
        logger.error('[NodeAdapter] Grok Text execution error:', error);
        setNodes((nodes) => nodes.map((node) => {
          if (node.id === id) return { ...node, data: { ...node.data, isProcessing: false, streamingInProgress: false, error: error.message || 'Execution failed' } };
          return node;
        }));
        const errEdges = getEdges().filter(edge => edge.source === id);
        const outputIds = errEdges.map(e => e.target).filter(tid => { const n = getNode(tid); return n?.type === 'universalOutput' || n?.type === 'customOutput'; });
        if (outputIds.length > 0) {
          setNodes((nodes) => nodes.map((node) => outputIds.includes(node.id) ? { ...node, data: { ...node.data, streamingInProgress: false } } : node));
        }
        showGenerationError(error, friendlyErrors.generationFailed);
      } finally {
        generationLifecycleService.finish(id);
      }
    }, [id, data.prompt, data.threadId, data.isProcessing, connectedTextContent, setNodes, getBatchSettings]);

    // ========================
    // Handle Imagen 4 generation (Google AI direct)
    // ========================
    const handleImageGenerate = useCallback(async (promptOverride?: string) => {
      const prompt = typeof promptOverride === 'string'
        ? promptOverride
        : typeof data.prompt === 'string'
          ? data.prompt
          : '';
      const registration = getNodeRegistration(nodeType) || getNodeByReactFlowType(nodeType);
      const registryDefault = (registration?.initialData?.selectedModel as string) || 'gpt-image-2';
      const selectedModel = typeof data.selectedModel === 'string' ? data.selectedModel : registryDefault;
      const { batchCount, batchOutputMode, parameters } = getBatchSettings();

      console.log('🔍 [NodeAdapter] Raw data.parameters:', data.parameters);
      console.log('🔍 [NodeAdapter] Parsed parameters:', parameters);

      // Allow submission if there's either a prompt or connected text content
      if (!prompt.trim() && !connectedTextContent.trim()) {
        alertService.error('Please enter a prompt first');
        return;
      }

      // Prevent multiple simultaneous generations
      if (data.isProcessing) {
        logger.debug('[NodeAdapter] Already processing, ignoring generate');
        return;
      }

      // Registry entry captured at generation start: owns flowId (so a
      // mid-run flow switch can't misattribute the asset), the batch
      // output node, cancel state, and the timer start time. Must run AFTER
      // the guards — a rejected re-invocation must not replace the live run.
      const generationRun = generationLifecycleService.start(id);
      const generationFlowId = generationRun.flowId;

      const resolution = parameters.resolution || '1024px';
      const baseCreditCost = calculateImageCreditCost(selectedModel, resolution);

      logger.debug('[NodeAdapter] Image generation started:', { nodeId: id, prompt, model: selectedModel, parameters });

      // Set processing state
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? { 
                ...node, 
                data: { 
                  ...node.data, 
                  isProcessing: true, 
                  error: null,
                  batchCancelRequested: false,
                  batchProgress: { current: 0, total: batchCount }
                } 
              }
            : node
        )
      );

      resetBatchCancel();

      try {
        // Gather context from upstream connected nodes (text and images)
        const incomingEdges = getEdges().filter(edge => edge.target === id);
        const contextMessages: Array<{ role: 'user' | 'assistant'; content: any }> = [];
        const textContexts: string[] = [];
        
        // Helper to convert relative URLs to absolute (needed for server-side fetching)
        const makeAbsoluteUrl = (url: string): string => {
          if (url.startsWith('/')) {
            return `${window.location.origin}${url}`;
          }
          return url;
        };
        
        for (const edge of incomingEdges) {
          const sourceNode = getNode(edge.source);
          if (!sourceNode) continue;
          
          logger.debug('[NodeAdapter] Processing edge source:', {
            nodeId: sourceNode.id,
            nodeType: sourceNode.type,
            hasImageUrl: !!sourceNode.data?.imageUrl,
            hasOutput: !!sourceNode.data?.output,
            dataKeys: Object.keys(sourceNode.data || {})
          });
          
          // Handle Output node with pages - use current page only
          if (sourceNode.type === 'customOutput' || sourceNode.type === 'universalOutput') {
            const pages = (sourceNode.data?.pages || []) as Array<{ content: string; type?: string; timestamp: number }>;
            const currentPageIndex = (sourceNode.data?.currentPageIndex || 0) as number;
            
            if (pages.length > 0 && pages[currentPageIndex]) {
              const currentPage = pages[currentPageIndex];
              
              if (currentPage.type === 'image' && currentPage.content) {
                // Add image to context - convert relative URLs to absolute
                const absoluteImageUrl = makeAbsoluteUrl(currentPage.content);
                contextMessages.push({
                  role: 'user',
                  content: [{
                    type: 'image',
                    imageUrl: absoluteImageUrl,
                    mimeType: 'image/png'
                  }]
                });
                logger.debug('[NodeAdapter] Added image from Output node current page:', absoluteImageUrl.substring(0, 80));
              } else if (currentPage.type === 'text' || !currentPage.type) {
                textContexts.push(currentPage.content);
                logger.debug('[NodeAdapter] Added text from Output node current page');
              }
            }
          }
          // Handle Image Container nodes
          else if (sourceNode.type === 'imagePanel' && sourceNode.data?.imageUrl) {
            // Convert relative URLs to absolute for server-side fetching
            const absoluteImageUrl = makeAbsoluteUrl(sourceNode.data.imageUrl as string);
            contextMessages.push({
              role: 'user',
              content: [{
                type: 'image',
                imageUrl: absoluteImageUrl,
                mimeType: 'image/png'
              }]
            });
            logger.debug('[NodeAdapter] Added image from Image Container node:', absoluteImageUrl.substring(0, 80));
          }
          // Handle Text Container nodes
          else if (sourceNode.type === 'documentPanel' && sourceNode.data?.content) {
            textContexts.push(sourceNode.data.content as string);
            logger.debug('[NodeAdapter] Added text from Text Container node:', sourceNode.data.fileName);
          }
          // Handle text output from other nodes
          else if (sourceNode.data?.output) {
            textContexts.push(sourceNode.data.output as string);
            logger.debug('[NodeAdapter] Added text output from node:', sourceNode.type);
          }
        }
        
        // Add text contexts to messages
        if (textContexts.length > 0) {
          contextMessages.push({
            role: 'user',
            content: `Context from connected nodes:\n\n${textContexts.join('\n\n---\n\n')}`
          });
        }
        
        const context = contextMessages.length > 0 ? { messages: contextMessages } : null;
        
        logger.debug('[NodeAdapter] Context gathered:', {
          textContexts: textContexts.length,
          totalMessages: contextMessages.length,
          hasImages: contextMessages.some(m => Array.isArray(m.content))
        });

        // ── Prompt character-limit check ─────────────────────────────────────
        const previewFinalPrompt = textContexts.length > 0
          ? `${textContexts.join('\n')}\n\n${prompt}`
          : prompt;
        const imageCharLimit = getPromptCharLimit(selectedModel);
        if (imageCharLimit && previewFinalPrompt.length > imageCharLimit) {
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id
                ? { ...node, data: { ...node.data, isProcessing: false } }
                : node
            )
          );
          const limitResult = await promptLimitService.check({
            fullText: previewFinalPrompt,
            limit: imageCharLimit,
            modelName: getModelDisplayName(selectedModel),
            contextChars: textContexts.join('\n').length,
            promptChars: prompt.length,
            onCondense: handleCondenseRequest,
          });
          if (limitResult.action === 'cancel') return;
          if (limitResult.action === 'condensed' && limitResult.condensedText) {
            setNodes((nodes) =>
              nodes.map((node) =>
                node.id === id
                  ? { ...node, data: { ...node.data, prompt: limitResult.condensedText } }
                  : node
              )
            );
            toastService.info('Prompt optimized — review and click Send to generate.');
            return;
          }
        }
        // ─────────────────────────────────────────────────────────────────────

        // Get parameters with proper typing
        const customDimensions = getCustomDimensions(parameters);
        const aspectRatio = getParam(parameters, 'aspectRatio', '1:1');
        const generationAspectRatio = aspectRatio === 'custom'
          ? (customDimensions ? getClosestAspectRatio(customDimensions.width, customDimensions.height) : '1:1')
          : aspectRatio;
        const resolution = getParam(parameters, 'resolution', '1024px'); // For credit pricing
        const size = getParam(parameters, 'size', '1024x1024') as '256x256' | '512x512' | '1024x1024' | '1792x1024' | '1024x1792';
        const quality = getParam(parameters, 'quality', 'standard') as 'standard' | 'hd';
        const style = getParam(parameters, 'style', 'vivid') as 'vivid' | 'natural';
        
        logger.debug('[NodeAdapter] Image generation params:', {
          selectedModel,
          aspectRatio: generationAspectRatio,
          size,
          quality,
          style,
          hasContext: !!context,
          contextMessages: context?.messages?.length || 0
        });

        // Credits are deducted server-side. Realtime subscription will update UI.
        const creditCost = baseCreditCost;
        console.log(`💳 [NodeAdapter] Image generation will cost ${creditCost} credits (server-side deduction)`);
        
        const generateImageOnce = async () => {
          let imageUrl: string;
          let revisedPrompt: string | undefined;
          try {
            // Route to appropriate service based on selected model
            if (selectedModel === 'gpt-image-1') {
              // Use OpenAI GPT Image 1 for image generation
              if (!openAIService.isConfigured()) {
                throw new Error('OpenAI not configured. Please check your .env file.');
              }

              logger.debug('[NodeAdapter] Generating image with GPT Image 1');
              // Note: GPT Image 1 doesn't support context images yet
              const finalPrompt = textContexts.length > 0 
                ? `${textContexts.join('\n')}\n\n${prompt}`
                : prompt;
              const result = await openAIService.generateImageWithGPT(finalPrompt);
              imageUrl = result.url;
              revisedPrompt = result.revisedPrompt;
            } else if (selectedModel === 'gemini-nano-banana' || selectedModel === 'gemini-3-pro-image-preview' || selectedModel === 'gemini-3.1-flash-image' || selectedModel === 'gemini-3.1-flash-lite-image') {
              // Route through Google AI directly (not fal.ai) for Gemini image models
              console.log('[NodeAdapter] 🔍 Gemini generation debug:', {
                selectedModel,
                'parameters.aspectRatio': parameters.aspectRatio,
                aspectRatio,
                generationAspectRatio,
                allParameters: JSON.stringify(parameters),
              });
              logger.debug('[NodeAdapter] Generating Gemini image via Google AI:', selectedModel, 'aspect ratio:', generationAspectRatio);
              // Prepend text context to prompt if any (same as other models)
              const finalPrompt = textContexts.length > 0 
                ? `${textContexts.join('\n')}\n\n${prompt}`.trim()
                : prompt;
              
              const headers = await getAuthHeaders();
              const googleRes = await fetch('/api/googleai/generate-image', {
                method: 'POST',
                headers,
                body: JSON.stringify({
                  prompt: finalPrompt,
                  model: selectedModel,
                  aspectRatio: generationAspectRatio,
                  resolution,
                  context: context || undefined,
                }),
              });
              if (!googleRes.ok) {
                const err = await googleRes.json().catch(() => ({}));
                throw new Error(err.error || err.message || `Google AI responded ${googleRes.status}`);
              }
              const googleData = await googleRes.json();
              imageUrl = googleData.imageUrl;
              revisedPrompt = undefined;
            } else if (selectedModel === 'gpt-image-1.5' || selectedModel === 'gpt-image-2') {
              // Use OpenAI GPT Image 1.5 / 2 (same Images API shape)
              if (!openAIService.isConfigured()) {
                throw new Error('OpenAI not configured. Please check your .env file.');
              }

              logger.debug('[NodeAdapter] Generating image with', selectedModel);

              // Map aspect ratio to supported sizes
              // Supported: 1024x1024, 1024x1536, 1536x1024
              let targetSize = '1024x1024';
              if (generationAspectRatio === '9:16' || generationAspectRatio === '2:3' || generationAspectRatio === '3:4') {
                targetSize = '1024x1536'; // Portrait
              } else if (generationAspectRatio === '16:9' || generationAspectRatio === '3:2' || generationAspectRatio === '4:3') {
                targetSize = '1536x1024'; // Landscape
              }

              // Extract input images from context messages
              const inputImages: Array<{ url: string; mimeType?: string }> = [];
              for (const msg of contextMessages) {
                if (Array.isArray(msg.content)) {
                  for (const part of msg.content) {
                    if (part.type === 'image' && part.imageUrl) {
                      inputImages.push({ url: part.imageUrl, mimeType: part.mimeType });
                    }
                  }
                }
              }

              logger.debug('[NodeAdapter]', selectedModel, 'input images:', inputImages.length);

              // Prepend text context to prompt if any (same as DALL-E)
              const finalPrompt = textContexts.length > 0
                ? `${textContexts.join('\n')}\n\n${prompt}`
                : prompt;

              const result = await openAIService.generateImage(finalPrompt, {
                model: selectedModel,
                size: targetSize,
                quality: getParam(parameters, 'quality', 'medium'),
                resolution, // Pass resolution for credit pricing
                images: inputImages,
              });
              imageUrl = result.url;
              revisedPrompt = result.revisedPrompt;
            } else if (GROK_IMAGE_NODE_MODELS.has(selectedModel)) {
              // Use xAI Grok for image generation — direct xAI API (supports t2i and i2i, up to 5 ref images)
              // Map Knods internal IDs → xAI API model names (grokImageApiModel):
              // grok-image → grok-imagine-image, grok-image-pro → -quality,
              // -quality and grok-imagine-image-2.0 pass through unchanged.
              const xaiModel = grokImageApiModel(selectedModel);
              logger.debug('[NodeAdapter] Generating image with Grok:', xaiModel);

              // Collect ALL input images from context for i2i (xAI supports up to 5)
              const inputImageUrls: string[] = [];
              for (const msg of contextMessages) {
                if (Array.isArray(msg.content)) {
                  for (const part of msg.content) {
                    if (part.type === 'image' && part.imageUrl) {
                      inputImageUrls.push(part.imageUrl);
                    }
                  }
                }
              }
              // xAI hard cap: 5 reference images
              const MAX_GROK_REF_IMAGES = 5;
              const grokInputImages = inputImageUrls.slice(0, MAX_GROK_REF_IMAGES);
              if (grokInputImages.length > 0) {
                logger.debug('[NodeAdapter] Grok i2i with', grokInputImages.length, 'input images');
              }

              // Grok Image Pro / Quality are documented as text-to-image only —
              // they do not support /v1/images/edits. Surface this clearly
              // instead of letting xAI return a cryptic error after we've
              // reserved credits.
              // Grok Image 2.0 is NOT in this group: it supports edits.
              const isQualityTier = grokImageRejectsInputImages(selectedModel);
              if (isQualityTier && grokInputImages.length > 0) {
                toastService.error(
                  'Grok Image Quality does not support reference images. Switch to Grok Image, or disconnect the input images.'
                );
                throw new Error('Grok Image Quality does not support input images');
              }

              // Prepend text context to prompt
              const finalPrompt = textContexts.length > 0
                ? `${textContexts.join('\n')}\n\n${prompt}`.trim()
                : prompt;

              const headers = await getAuthHeaders();

              const grokAspectRatio = getParam(parameters, 'aspectRatio', '1:1');
              // Grok Image 2.0 adds explicit quality + resolution (never 'auto');
              // read through getParam so a node saved before those settings
              // existed falls back to the definition defaults (low / 1k).
              const grokRequestBody = buildGrokImageRequestBody({
                selectedModel,
                prompt: finalPrompt,
                aspectRatio: grokAspectRatio,
                parameters: {
                  quality: getParam(parameters, 'quality', 'low'),
                  resolution: getParam(parameters, 'resolution', '1k'),
                },
                inputImageUrls: grokInputImages,
                // Flow captured at generation START (see uploadImageToStorage):
                // the server scopes the R2 upload by this id, so re-reading the
                // open flow here would file the image under the wrong Knod.
                flowId: generationFlowId,
              });

              const grokResponse = await fetch(`/api/xai/generate-image`, {
                method: 'POST',
                headers,
                body: JSON.stringify(grokRequestBody),
              });

              if (!grokResponse.ok) {
                const errData = await grokResponse.json().catch(() => ({ error: 'Unknown error' }));
                throw new Error((errData as any).error || 'Grok image generation failed');
              }

              const grokData = await grokResponse.json();
              const grokImage = grokData.data?.[0];
              if (!grokImage || !grokImage.url) {
                throw new Error('No image data returned from Grok');
              }

              imageUrl = grokImage.url;
              revisedPrompt = grokImage.revised_prompt;
            } else {
              throw new Error(`Unknown image model: ${selectedModel}. This model is not configured for generation.`);
            }
          } catch (genError) {
            // On failure, refresh balance from server (server will have refunded the reservation)
            console.log(`💳 [NodeAdapter] Image generation failed, refreshing balance from server`);
            refreshBalance().catch(err => console.warn('[NodeAdapter] Failed to refresh balance:', err));
            throw genError;
          }

          return { imageUrl, revisedPrompt };
        };

        let lastRevisedPrompt: string | undefined;
        let completedCount = 0;
        for (let batchIndex = 0; batchIndex < batchCount; batchIndex += 1) {
          if (isBatchCancelRequested()) {
            break;
          }

          const { imageUrl: generatedUrl, revisedPrompt: generatedRevisedPrompt } = await generateImageOnce();
          lastRevisedPrompt = generatedRevisedPrompt;
          let imageUrl = generatedUrl;

          logger.debug('[NodeAdapter] Image generated:', { url: imageUrl?.substring(0, 100), revisedPrompt: generatedRevisedPrompt });

          // Upload image to R2 storage (converts base64 data URL to R2 URL)
          imageUrl = await uploadImageToStorage(imageUrl, id, generationFlowId);
          logger.debug('[NodeAdapter] Image stored:', { storedUrl: imageUrl?.substring(0, 100) });

          if (customDimensions) {
            imageUrl = await resizeImageToDimensions(imageUrl, customDimensions.width, customDimensions.height);
          }

          // Update node with generated image (keep processing true during batch)
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id
                ? {
                    ...node,
                    data: {
                      ...node.data,
                      imageUrl,
                      revisedPrompt: generatedRevisedPrompt,
                      isProcessing: true,
                      error: null,
                      batchProgress: { current: batchIndex + 1, total: batchCount }
                    },
                  }
                : node
            )
          );

          completedCount = batchIndex + 1;

          await generationLifecycleService.deliverImageOutput(id, imageUrl, batchOutputMode, batchIndex);
        }

        setNodes((nodes) =>
          nodes.map((node) =>
            node.id === id
              ? {
                  ...node,
                  data: {
                    ...node.data,
                    isProcessing: false,
                    revisedPrompt: lastRevisedPrompt,
                    batchProgress: { current: completedCount, total: batchCount },
                    batchCancelRequested: false
                  },
                }
              : node
          )
        );
        resetBatchCancel();

      } catch (error: any) {
        logger.error('[NodeAdapter] Image generation error:', error);
        setNodes((nodes) =>
          nodes.map((node) =>
            node.id === id
              ? {
                  ...node,
                  data: {
                    ...node.data,
                    isProcessing: false,
                    error: error.message || 'Image generation failed',
                  },
                }
              : node
          )
        );
        showGenerationError(error, friendlyErrors.imageGenerationFailed);
      } finally {
        generationLifecycleService.finish(id);
      }
    }, [
      id,
      data.prompt,
      data.selectedModel,
      data.parameters,
      data.isProcessing,
      connectedTextContent,
      setNodes,
      getEdges,
      getNode,
      setEdges,
      refreshBalance,
      getBatchSettings,
      resetBatchCancel,
      isBatchCancelRequested,
      getCustomDimensions,
      getClosestAspectRatio,
      resizeImageToDimensions,
    ]);

    // Handle FLUX image generation (Together AI)
    const handleFluxImageGenerate = useCallback(async (promptOverride?: string) => {
      const prompt = typeof promptOverride === 'string'
        ? promptOverride
        : typeof data.prompt === 'string'
          ? data.prompt
          : '';
      const selectedModel = typeof data.selectedModel === 'string' && data.selectedModel ? data.selectedModel : 'flux-2-flash';
      const { batchCount, batchOutputMode, parameters } = getBatchSettings();

      // Allow submission if there's either a prompt or connected text content
      if (!prompt.trim() && !connectedTextContent.trim()) {
        alertService.error('Please enter a prompt first');
        return;
      }

      // Prevent multiple simultaneous generations
      if (data.isProcessing) {
        logger.debug('[NodeAdapter] Already processing, ignoring FLUX generate');
        return;
      }

      // Run registry entry — see handleImageGenerate for rationale (after guards).
      const generationRun = generationLifecycleService.start(id);
      const generationFlowId = generationRun.flowId;

      logger.debug('[NodeAdapter] FLUX image generation started:', { nodeId: id, prompt, model: selectedModel, parameters });

      // Set processing state
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? {
                ...node,
                data: {
                  ...node.data,
                  isProcessing: true,
                  error: null,
                  batchCancelRequested: false,
                  batchProgress: { current: 0, total: batchCount }
                }
              }
            : node
        )
      );

      resetBatchCancel();

      try {
        // Gather context from upstream connected nodes based on handle type
        const incomingEdges = getEdges().filter(edge => edge.target === id);
        const textContexts: string[] = [];
        const referenceImages: string[] = [];
        
        for (const edge of incomingEdges) {
          const sourceNode = getNode(edge.source);
          if (!sourceNode) continue;
          
          // Check which handle this edge connects to (input-text or input-image)
          const targetHandle = edge.targetHandle;
          
          if (targetHandle === 'input-image') {
            // This is connected to the IMAGE input - get image URL
            const imageUrl = sourceNode.data?.imageUrl || sourceNode.data?.content || sourceNode.data?.output;
            if (imageUrl && typeof imageUrl === 'string' && 
                (imageUrl.startsWith('http') || imageUrl.startsWith('/'))) {
              // Convert relative URLs to absolute for Together AI
              let absoluteUrl = imageUrl;
              if (imageUrl.startsWith('/')) {
                absoluteUrl = `${window.location.origin}${imageUrl}`;
              }
              referenceImages.push(absoluteUrl);
              logger.debug('[NodeAdapter] Added reference image from IMAGE input:', sourceNode.type, absoluteUrl.substring(0, 50));
            }
          } else if (targetHandle === 'input-text') {
            // This is connected to the TEXT input - get text content (check output, content, and prompt)
            const textOutput = sourceNode.data?.output || sourceNode.data?.content || sourceNode.data?.prompt;
            if (textOutput && typeof textOutput === 'string') {
              textContexts.push(textOutput);
              logger.debug('[NodeAdapter] Added text context from TEXT input:', sourceNode.type);
            }
          } else {
            // Legacy: handle old connections without specific target handles
            const imageUrl = sourceNode.data?.imageUrl || sourceNode.data?.content;
            if (imageUrl && typeof imageUrl === 'string' && 
                (imageUrl.startsWith('http') || imageUrl.startsWith('/'))) {
              let absoluteUrl = imageUrl;
              if (imageUrl.startsWith('/')) {
                absoluteUrl = `${window.location.origin}${imageUrl}`;
              }
              referenceImages.push(absoluteUrl);
              logger.debug('[NodeAdapter] Added reference image (legacy):', sourceNode.type);
            } else {
              // Check for text content in various fields (output, content, prompt)
              const textValue = sourceNode.data?.output || sourceNode.data?.content || sourceNode.data?.prompt;
              if (textValue && typeof textValue === 'string' &&
                       !textValue.startsWith('http') && !textValue.startsWith('/')) {
                textContexts.push(textValue);
                logger.debug('[NodeAdapter] Added text context (legacy):', sourceNode.type);
              }
            }
          }
        }

        // Prepend text context to prompt if any
        const finalPrompt = textContexts.length > 0 
          ? `${textContexts.join('\n')}\n\n${prompt}`
          : prompt;

        // ── Prompt character-limit check ─────────────────────────────────────
        const fluxCharLimit = getPromptCharLimit(selectedModel);
        if (fluxCharLimit && finalPrompt.length > fluxCharLimit) {
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id
                ? { ...node, data: { ...node.data, isProcessing: false } }
                : node
            )
          );
          const limitResult = await promptLimitService.check({
            fullText: finalPrompt,
            limit: fluxCharLimit,
            modelName: getModelDisplayName(selectedModel),
            contextChars: textContexts.join('\n').length,
            promptChars: prompt.length,
            onCondense: handleCondenseRequest,
          });
          if (limitResult.action === 'cancel') return;
          if (limitResult.action === 'condensed' && limitResult.condensedText) {
            setNodes((nodes) =>
              nodes.map((node) =>
                node.id === id
                  ? { ...node, data: { ...node.data, prompt: limitResult.condensedText } }
                  : node
              )
            );
            toastService.info('Prompt optimized — review and click Send to generate.');
            return;
          }
        }
        // ─────────────────────────────────────────────────────────────────────

        // Get parameters
        const customDimensions = getCustomDimensions(parameters);
        const aspectRatio = getParam(parameters, 'aspectRatio', '1:1');
        const generationAspectRatio = aspectRatio === 'custom'
          ? (customDimensions ? getClosestAspectRatio(customDimensions.width, customDimensions.height) : '1:1')
          : aspectRatio;
        const quality = getParam(parameters, 'quality', 'Balanced');

        logger.debug('[NodeAdapter] FLUX generation parameters:', {
          model: selectedModel,
          aspectRatio: generationAspectRatio,
          quality,
          textContextCount: textContexts.length,
          referenceImageCount: referenceImages.length,
        });

        // Credits are deducted server-side. Realtime subscription will update UI.
        // FLUX pricing is per megapixel - use 720p base since base cost already accounts for 1MP
        const creditCost = calculateImageCreditCost(selectedModel, '720p');
        logger.debug(`💳 [NodeAdapter] FLUX image generation will cost ${creditCost} credits (server-side deduction)`);
        
        const generateFluxOnce = async () => {
          let imageUrl: string;
          try {
            // Generate image via Fal.ai FLUX (migrated from Together AI)
            // flowId is required for the server to upload the result under the
            // flow's R2 prefix — without it the image lands global-scoped and
            // never shows up in the flow's asset gallery.
            const result = await falAIService.generateImage({
              prompt: finalPrompt,
              model: selectedModel,
              aspectRatio: generationAspectRatio,
              quality,
              imageUrls: referenceImages.length > 0 ? referenceImages : undefined,
              flowId: generationFlowId,
            });
            imageUrl = result.imageUrl;
            logger.debug('[NodeAdapter] FLUX image generated:', { 
              processingTime: result.processingTime,
              usedReferenceImages: referenceImages.length > 0,
            });
          } catch (genError) {
            // On failure, refresh balance from server
            logger.debug(`💳 [NodeAdapter] FLUX image generation failed, refreshing balance from server`);
            refreshBalance().catch(err => console.warn('[NodeAdapter] Failed to refresh balance:', err));
            throw genError;
          }
          return imageUrl;
        };

        let completedCount = 0;
        for (let batchIndex = 0; batchIndex < batchCount; batchIndex += 1) {
          if (isBatchCancelRequested()) {
            break;
          }

          let imageUrl = await generateFluxOnce();

          // Upload image to R2 storage (converts base64 data URL to R2 URL)
          imageUrl = await uploadImageToStorage(imageUrl, id, generationFlowId);
          logger.debug('[NodeAdapter] FLUX image stored:', { storedUrl: imageUrl?.substring(0, 100) });

          if (customDimensions) {
            imageUrl = await resizeImageToDimensions(imageUrl, customDimensions.width, customDimensions.height);
          }

          // Update node with generated image (keep processing true during batch)
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id
                ? {
                    ...node,
                    data: {
                      ...node.data,
                      imageUrl,
                      isProcessing: true,
                      error: null,
                      batchProgress: { current: batchIndex + 1, total: batchCount }
                    },
                  }
                : node
            )
          );

          completedCount = batchIndex + 1;

          await generationLifecycleService.deliverImageOutput(id, imageUrl, batchOutputMode, batchIndex);
        }

        setNodes((nodes) =>
          nodes.map((node) =>
            node.id === id
              ? {
                  ...node,
                  data: {
                    ...node.data,
                    isProcessing: false,
                    batchProgress: { current: completedCount, total: batchCount },
                    batchCancelRequested: false
                  },
                }
              : node
          )
        );
        resetBatchCancel();

      } catch (error: any) {
        logger.error('[NodeAdapter] FLUX image generation error:', error);
        setNodes((nodes) =>
          nodes.map((node) =>
            node.id === id
              ? {
                  ...node,
                  data: {
                    ...node.data,
                    isProcessing: false,
                    error: error.message || 'FLUX image generation failed',
                  },
                }
              : node
          )
        );
        showGenerationError(error, friendlyErrors.imageGenerationFailed);
      } finally {
        generationLifecycleService.finish(id);
      }
    }, [
      id,
      data.prompt,
      data.selectedModel,
      data.parameters,
      data.isProcessing,
      connectedTextContent,
      setNodes,
      getEdges,
      getNode,
      setEdges,
      refreshBalance,
      getBatchSettings,
      resetBatchCancel,
      isBatchCancelRequested,
      getCustomDimensions,
      getClosestAspectRatio,
      resizeImageToDimensions,
    ]);

    // Handle fal.ai image generation (Z-Image Turbo, Qwen, Seedream, Recraft)
    const handleFalAIImageGenerate = useCallback(async (promptOverride?: string) => {
      const prompt = typeof promptOverride === 'string'
        ? promptOverride
        : typeof data.prompt === 'string'
          ? data.prompt
          : '';
      const { batchCount, batchOutputMode, parameters } = getBatchSettings();

      // Allow submission if there's either a prompt or connected text content
      if (!prompt.trim() && !connectedTextContent.trim()) {
        alertService.error('Please enter a prompt first');
        return;
      }

      // Prevent multiple simultaneous generations
      if (data.isProcessing) {
        logger.debug('[NodeAdapter] Already processing, ignoring fal.ai generate');
        return;
      }

      // Run registry entry — see handleImageGenerate for rationale (after guards).
      const generationRun = generationLifecycleService.start(id);
      const generationFlowId = generationRun.flowId;

      // Map node type to default fal.ai model ID
      const modelMap: Record<string, string> = {
        'ZImageTurbo': 'z-image-turbo',
        'QwenImage': 'qwen-image',
        'QwenImageEdit': 'qwen-image-edit',
        'Seedream': 'seedream-4.5',
        'RecraftV3': 'recraft-v3',
        'GrokImage': 'grok-image',
        'GLMImage': 'glm-image',
        'KlingImage': 'kling-image-o3',
      };

      // Check for model selector override (like video handler)
      const model = typeof data.selectedModel === 'string' && data.selectedModel
        ? data.selectedModel
        : modelMap[nodeType];
      if (!model) {
        alertService.error('Unknown fal.ai model');
        return;
      }

      logger.debug('[NodeAdapter] fal.ai image generation started:', { nodeId: id, prompt, model, parameters });

      // Set processing state
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? {
                ...node,
                data: {
                  ...node.data,
                  isProcessing: true,
                  error: null,
                  batchCancelRequested: false,
                  batchProgress: { current: 0, total: batchCount }
                }
              }
            : node
        )
      );

      resetBatchCancel();

      try {
        // Gather context from upstream connected nodes
        const incomingEdges = getEdges().filter(edge => edge.target === id);
        const textContexts: string[] = [];
        const inputImageUrls: string[] = []; // Support multiple input images
        
        for (const edge of incomingEdges) {
          const sourceNode = getNode(edge.source);
          if (!sourceNode) continue;
          
          // Get image URL for image-to-image (all fal.ai nodes now support it)
          // Check imageUrl first (dedicated image field), then content/output for URL strings
          let imageUrl = sourceNode.data?.imageUrl;
          let contentValue = sourceNode.data?.content || sourceNode.data?.output;

          // Output containers hold PAGES, and the one that matters is the one
          // the user is looking at: pages[currentPageIndex]. Reading
          // data.imageUrl / data.content instead picks up whatever was
          // delivered last — and since handlePageChange only overwrites
          // imageUrl when the incoming page IS an image, a stale image from an
          // earlier page outranks the text page actually on screen. Because
          // generator→generator edges are always rewritten to pass through an
          // auto-inserted Output container, this is the normal chaining path,
          // not an edge case. (handleImageGenerate already does this; this
          // handler was the only generation path missing it.)
          if (sourceNode.type === 'customOutput' || sourceNode.type === 'universalOutput') {
            const pages = Array.isArray(sourceNode.data?.pages) ? sourceNode.data.pages : [];
            const pageIndex = typeof sourceNode.data?.currentPageIndex === 'number'
              ? sourceNode.data.currentPageIndex
              : 0;
            const page = pages[pageIndex] as { content?: string; type?: string } | undefined;
            if (page?.content) {
              // An explicitly non-image page must NOT leak a stale imageUrl.
              imageUrl = page.type === 'image' ? page.content : undefined;
              contentValue = page.content;
            }
          }
          
          // Check if we have a dedicated image URL
          if (imageUrl && typeof imageUrl === 'string') {
            // Handle different URL formats
            if (imageUrl.startsWith('data:image/')) {
              // Data URL (pasted image) - upload to R2 first to get a proper URL
              logger.debug('[NodeAdapter] Found data URL, uploading to R2 for fal.ai...');
              try {
                const uploadedUrl = await uploadImageToStorage(imageUrl, id, generationFlowId);
                if (uploadedUrl && !uploadedUrl.startsWith('data:')) {
                  const absoluteUrl = uploadedUrl.startsWith('/') 
                    ? `${window.location.origin}${uploadedUrl}` 
                    : uploadedUrl;
                  inputImageUrls.push(absoluteUrl);
                  logger.debug('[NodeAdapter] Data URL uploaded to R2:', absoluteUrl.substring(0, 50));
                } else {
                  // Upload returned same data URL (shouldn't happen, but handle gracefully)
                  inputImageUrls.push(imageUrl);
                  logger.warn('[NodeAdapter] R2 upload returned data URL, using as-is');
                }
              } catch (uploadError) {
                logger.error('[NodeAdapter] Failed to upload data URL to R2:', uploadError);
                // Fall back to using data URL directly (fal.ai may accept it)
                inputImageUrls.push(imageUrl);
              }
            } else if (imageUrl.startsWith('http') || imageUrl.startsWith('/')) {
              // Regular URL
              let absoluteUrl = imageUrl;
              if (imageUrl.startsWith('/')) {
                absoluteUrl = `${window.location.origin}${imageUrl}`;
              }
              inputImageUrls.push(absoluteUrl);
              logger.debug('[NodeAdapter] Found input image for fal.ai:', absoluteUrl.substring(0, 50));
            }
          } else if (contentValue && typeof contentValue === 'string') {
            // Check if content is an image URL or text
            if (contentValue.startsWith('data:image/')) {
              // Data URL image in content field
              logger.debug('[NodeAdapter] Found data URL in content, uploading to R2 for fal.ai...');
              try {
                const uploadedUrl = await uploadImageToStorage(contentValue, id, generationFlowId);
                if (uploadedUrl && !uploadedUrl.startsWith('data:')) {
                  const absoluteUrl = uploadedUrl.startsWith('/') 
                    ? `${window.location.origin}${uploadedUrl}` 
                    : uploadedUrl;
                  inputImageUrls.push(absoluteUrl);
                  logger.debug('[NodeAdapter] Content data URL uploaded to R2:', absoluteUrl.substring(0, 50));
                } else {
                  inputImageUrls.push(contentValue);
                }
              } catch (uploadError) {
                logger.error('[NodeAdapter] Failed to upload content data URL to R2:', uploadError);
                inputImageUrls.push(contentValue);
              }
            } else if (contentValue.startsWith('http') || contentValue.startsWith('/')) {
              // HTTP/HTTPS URL image in content field
              let absoluteUrl = contentValue;
              if (contentValue.startsWith('/')) {
                absoluteUrl = `${window.location.origin}${contentValue}`;
              }
              inputImageUrls.push(absoluteUrl);
              logger.debug('[NodeAdapter] Found input image URL in content for fal.ai:', absoluteUrl.substring(0, 50));
            } else {
              // Text content - add to text contexts
              textContexts.push(contentValue);
              logger.debug('[NodeAdapter] Added text context for fal.ai:', sourceNode.type);
            }
          } else {
            // Fallback: Check for text in prompt field
            const textValue = sourceNode.data?.prompt;
            if (textValue && typeof textValue === 'string' &&
                     !textValue.startsWith('http') && !textValue.startsWith('/') &&
                     !textValue.startsWith('data:')) {
              textContexts.push(textValue);
              logger.debug('[NodeAdapter] Added prompt text context for fal.ai:', sourceNode.type);
            }
          }
        }

        // Qwen Image Edit requires an input image
        if (nodeType === 'QwenImageEdit' && inputImageUrls.length === 0) {
          throw new Error('Qwen Image Edit requires an input image. Connect an image source to this node.');
        }

        // Prepend text context to prompt if any
        const finalPrompt = textContexts.length > 0 
          ? `${textContexts.join('\n')}\n\n${prompt}`
          : prompt;

        // ── Prompt character-limit check ─────────────────────────────────────
        const falCharLimit = getPromptCharLimit(model);
        if (falCharLimit && finalPrompt.length > falCharLimit) {
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id
                ? { ...node, data: { ...node.data, isProcessing: false } }
                : node
            )
          );
          const limitResult = await promptLimitService.check({
            fullText: finalPrompt,
            limit: falCharLimit,
            modelName: getModelDisplayName(model),
            contextChars: textContexts.join('\n').length,
            promptChars: prompt.length,
            onCondense: handleCondenseRequest,
          });
          if (limitResult.action === 'cancel') return;
          if (limitResult.action === 'condensed' && limitResult.condensedText) {
            setNodes((nodes) =>
              nodes.map((node) =>
                node.id === id
                  ? { ...node, data: { ...node.data, prompt: limitResult.condensedText } }
                  : node
              )
            );
            toastService.info('Prompt optimized — review and click Send to generate.');
            return;
          }
        }
        // ─────────────────────────────────────────────────────────────────────

        // Get parameters
        const customDimensions = getCustomDimensions(parameters);
        const aspectRatio = getParam(parameters, 'aspectRatio', '1:1');
        const generationAspectRatio = aspectRatio === 'custom'
          ? (customDimensions ? getClosestAspectRatio(customDimensions.width, customDimensions.height) : '1:1')
          : aspectRatio;
        const quality = getParam(parameters, 'quality', 'balanced');
        const negativePrompt = (parameters.negativePrompt as string) || undefined;
        const style = (parameters.style as string) || undefined;
        const enablePromptExpansion = getParam(parameters, 'enablePromptExpansion', 'false') === 'true';
        const strength = typeof parameters.strength === 'number' ? parameters.strength : undefined; // For Z-Image image-to-image

        logger.debug('[NodeAdapter] fal.ai generation parameters:', {
          model,
          aspectRatio: generationAspectRatio,
          quality,
          negativePrompt: !!negativePrompt,
          style,
          enablePromptExpansion,
          inputImageCount: inputImageUrls.length,
          strength,
        });

        // Kling Image is the one fal.ai image model whose price depends on the
        // resolution setting (1K = 2K, 4K doubles). Everything else is flat per
        // generation, so '720p' keeps the existing behavior untouched.
        const falResolution = getParam(parameters, 'resolution', '1K');
        const falOutputFormat = getParam(parameters, 'outputFormat', 'png');
        const isResolutionPriced = model === 'kling-image-o3';

        // Credits are deducted server-side. Realtime subscription will update UI.
        const creditCost = calculateImageCreditCost(model, isResolutionPriced ? falResolution : '720p');
        logger.debug(`💳 [NodeAdapter] fal.ai image generation will cost ${creditCost} credits (server-side deduction)`);

        const generateFalAIOnce = async () => {
          let imageUrl: string;
          try {
            // Generate image via fal.ai.
            // flowId MUST be the flow captured when the run started, not the
            // one open right now: the server scopes the R2 upload by this id,
            // so re-reading it here files the asset under whatever Knod the
            // user switched to mid-batch (or globally, where Flow Assets can
            // never show it).
            const result = await falAIService.generateImage({
              prompt: finalPrompt,
              model,
              aspectRatio: generationAspectRatio,
              quality,
              negativePrompt,
              style,
              enablePromptExpansion,
              imageUrls: inputImageUrls.length > 0 ? inputImageUrls : undefined,
              strength,
              flowId: generationFlowId,
              ...(isResolutionPriced
                ? { resolution: falResolution, outputFormat: falOutputFormat }
                : {}),
            });
            imageUrl = result.imageUrl;
            logger.debug('[NodeAdapter] fal.ai image generated:', { 
              processingTime: result.processingTime,
              width: result.width,
              height: result.height,
              mode: inputImageUrls.length > 0 ? 'image-to-image' : 'text-to-image',
            });
          } catch (genError) {
            // On failure, refresh balance from server
            logger.debug(`💳 [NodeAdapter] fal.ai image generation failed, refreshing balance from server`);
            refreshBalance().catch(err => console.warn('[NodeAdapter] Failed to refresh balance:', err));
            throw genError;
          }
          return imageUrl;
        };

        let completedCount = 0;
        for (let batchIndex = 0; batchIndex < batchCount; batchIndex += 1) {
          if (isBatchCancelRequested()) {
            break;
          }

          let imageUrl = await generateFalAIOnce();

          // Upload image to R2 storage
          imageUrl = await uploadImageToStorage(imageUrl, id, generationFlowId);
          logger.debug('[NodeAdapter] fal.ai image stored:', { storedUrl: imageUrl?.substring(0, 100) });

          if (customDimensions) {
            imageUrl = await resizeImageToDimensions(imageUrl, customDimensions.width, customDimensions.height);
          }

          // Update node with generated image (keep processing true during batch)
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id
                ? {
                    ...node,
                    data: {
                      ...node.data,
                      imageUrl,
                      isProcessing: true,
                      error: null,
                      batchProgress: { current: batchIndex + 1, total: batchCount }
                    },
                  }
                : node
            )
          );

          completedCount = batchIndex + 1;

          await generationLifecycleService.deliverImageOutput(id, imageUrl, batchOutputMode, batchIndex);
        }

        setNodes((nodes) =>
          nodes.map((node) =>
            node.id === id
              ? {
                  ...node,
                  data: {
                    ...node.data,
                    isProcessing: false,
                    batchProgress: { current: completedCount, total: batchCount },
                    batchCancelRequested: false
                  },
                }
              : node
          )
        );
        resetBatchCancel();

      } catch (error: any) {
        logger.error('[NodeAdapter] fal.ai image generation error:', error);
        setNodes((nodes) =>
          nodes.map((node) =>
            node.id === id
              ? {
                  ...node,
                  data: {
                    ...node.data,
                    isProcessing: false,
                    error: error.message || 'fal.ai image generation failed',
                  },
                }
              : node
          )
        );
        // Pass suggestions from enhanced error if available
        showGenerationError(error, friendlyErrors.imageGenerationFailed, error.suggestions);
      } finally {
        generationLifecycleService.finish(id);
      }
    }, [
      id,
      nodeType,
      data.prompt,
      data.parameters,
      data.isProcessing,
      connectedTextContent,
      setNodes,
      getEdges,
      getNode,
      setEdges,
      refreshBalance,
      getBatchSettings,
      resetBatchCancel,
      isBatchCancelRequested,
      getCustomDimensions,
      getClosestAspectRatio,
      resizeImageToDimensions,
    ]);

    // Handle video generation (VEO-3)
    const handleVideoGenerate = useCallback(async (promptOverride?: string) => {
      const prompt = typeof promptOverride === 'string'
        ? promptOverride
        : typeof data.prompt === 'string'
          ? data.prompt
          : '';
      
      // Get the default model from the node definition's modelSelector
      const nodeDefinition = data.definition as { 
        fixedModel?: string;
        components?: Array<{
          type: string;
          props?: {
            modelSelector?: {
              value?: string;
            };
          };
        }>;
      } | undefined;
      
      // Find the model selector's default value from definition
      const parameterControlsComponent = nodeDefinition?.components?.find(c => c.type === 'ParameterControls');
      const defaultModelFromDefinition = parameterControlsComponent?.props?.modelSelector?.value;
      
      // Use selectedModel from data, then definition default / fixedModel (note:
      // data.definition is NOT populated at node creation, so those two are usually
      // dead), then the LIVE registry default for THIS node type — this is what keeps
      // non-Veo video nodes (e.g. Gemini Omni) from silently defaulting to the VEO
      // model — and finally the hardcoded VEO default as a last resort.
      const registryDefaultModel = (getNodeRegistration(nodeType) || getNodeByReactFlowType(nodeType))?.initialData?.selectedModel as string | undefined;
      const selectedModel = (typeof data.selectedModel === 'string' && data.selectedModel)
        ? data.selectedModel
        : defaultModelFromDefinition
        || nodeDefinition?.fixedModel
        || registryDefaultModel
        || 'veo-3.1-fast-generate-preview';
      const parameters = (data.parameters || {}) as Record<string, string>;
      // Batch settings once up-front (batchCount clamped 1-10). The loop below
      // generates `batchCount` distinct videos; delivery honors batchOutputMode.
      const { batchCount, batchOutputMode } = getBatchSettings();

      console.log('🔍 [NodeAdapter] Raw data.parameters:', data.parameters);
      console.log('🔍 [NodeAdapter] Parsed parameters:', parameters);
      console.log('🔍 [NodeAdapter] Using model:', selectedModel, 'defaultFromDef:', defaultModelFromDefinition);
      
      // Allow submission if there's either a prompt or connected text content
      if (!prompt.trim() && !connectedTextContent.trim()) {
        alertService.error('Please enter a prompt first');
        return;
      }

      // Prevent multiple simultaneous generations
      if (data.isProcessing) {
        logger.debug('[NodeAdapter] Already processing, ignoring generate');
        return;
      }

      // ── Prompt character-limit check ───────────────────────────────────────
      const veoCharLimit = getPromptCharLimit(selectedModel);
      if (veoCharLimit && prompt.length > veoCharLimit) {
        const limitResult = await promptLimitService.check({
          fullText: prompt,
          limit: veoCharLimit,
          modelName: getModelDisplayName(selectedModel),
          contextChars: 0,
          promptChars: prompt.length,
          onCondense: handleCondenseRequest,
        });
        if (limitResult.action === 'cancel') return;
        if (limitResult.action === 'condensed' && limitResult.condensedText) {
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id
                ? { ...node, data: { ...node.data, prompt: limitResult.condensedText } }
                : node
            )
          );
          toastService.info('Prompt optimized — review and click Send to generate.');
          return;
        }
      }
      // ───────────────────────────────────────────────────────────────────────

      logger.debug('[NodeAdapter] Video generation started:', { nodeId: id, prompt, model: selectedModel, parameters });

      // Set processing state
      generationLifecycleService.start(id);
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, isProcessing: true, error: null } }
            : node
        )
      );

      try {
        // Get context from connected nodes (text and images) - matching Image Generator pattern
        const edges = getEdges();
        const incomingEdges = edges.filter((edge) => edge.target === id);
        
        const contextMessages: Array<{ role: 'user' | 'assistant'; content: any }> = [];
        const textContexts: string[] = [];
        let firstFrameImage: string | undefined;  // For first-last-frame mode
        let lastFrameImage: string | undefined;   // For first-last-frame mode
        const mode = getParam(parameters, 'mode', 'text-to-video');

        for (const edge of incomingEdges) {
          const sourceNode = getNode(edge.source);
          if (!sourceNode) continue;

          // Determine if this connection is for first or last frame based on targetHandle
          const isFirstFrameHandle = edge.targetHandle === 'input-image-first';
          const isLastFrameHandle = edge.targetHandle === 'input-image-last';
          // Legacy/pre-FFLF single image handle (or unlabeled). Used as a
          // defensive fallback so saved flows the mode-change migration never
          // touched still deliver both frames to the adapter.
          const isLegacyImageHandle =
            edge.targetHandle == null ||
            edge.targetHandle === 'input' ||
            edge.targetHandle === 'input-any' ||
            edge.targetHandle === 'input-image';

          logger.debug('[NodeAdapter] Processing edge source:', {
            nodeId: sourceNode.id,
            nodeType: sourceNode.type,
            hasImageUrl: !!sourceNode.data.imageUrl,
            hasOutput: !!sourceNode.data.output,
            dataKeys: Object.keys(sourceNode.data),
            targetHandle: edge.targetHandle,
          });

          // Handle Output node with pages - use current page only
          if (sourceNode.type === 'customOutput' || sourceNode.type === 'universalOutput') {
            const pages = (sourceNode.data?.pages || []) as Array<{ content: string; type?: string; timestamp: number }>;
            const currentPageIndex = (sourceNode.data?.currentPageIndex || 0) as number;
            
            if (pages.length > 0 && pages[currentPageIndex]) {
              const currentPage = pages[currentPageIndex];
              
              if (currentPage.type === 'image' && currentPage.content) {
                // Collect images based on handle ID for first-last-frame mode
                if (mode === 'first-last-frame') {
                  if (isFirstFrameHandle) {
                    firstFrameImage = currentPage.content;
                    logger.debug('[NodeAdapter] Added FIRST frame image from Output node');
                  } else if (isLastFrameHandle) {
                    lastFrameImage = currentPage.content;
                    logger.debug('[NodeAdapter] Added LAST frame image from Output node');
                  } else if (isLegacyImageHandle) {
                    // Legacy image edge (pre-FFLF handle): fill the first open frame slot.
                    if (!firstFrameImage) { firstFrameImage = currentPage.content; logger.debug('[NodeAdapter] Filled FIRST frame from legacy image handle (Output node)'); }
                    else if (!lastFrameImage) { lastFrameImage = currentPage.content; logger.debug('[NodeAdapter] Filled LAST frame from legacy image handle (Output node)'); }
                  }
                } else {
                  // For non-first-last-frame modes, add to context
                  contextMessages.push({
                    role: 'user',
                    content: [{
                      type: 'image',
                      imageUrl: currentPage.content,
                      mimeType: 'image/png'
                    }]
                  });
                  logger.debug('[NodeAdapter] Added image from Output node current page');
                }
              } else if (currentPage.type === 'text' || !currentPage.type) {
                textContexts.push(currentPage.content);
                logger.debug('[NodeAdapter] Added text from Output node current page');
              }
            }
          }
          // Handle Image Container nodes
          else if (sourceNode.type === 'imagePanel' && sourceNode.data.imageUrl) {
            if (mode === 'first-last-frame') {
              if (isFirstFrameHandle) {
                firstFrameImage = String(sourceNode.data.imageUrl);
                logger.debug('[NodeAdapter] Added FIRST frame image from Image Container');
              } else if (isLastFrameHandle) {
                lastFrameImage = String(sourceNode.data.imageUrl);
                logger.debug('[NodeAdapter] Added LAST frame image from Image Container');
              } else if (isLegacyImageHandle) {
                // Legacy image edge (pre-FFLF handle): fill the first open frame slot.
                if (!firstFrameImage) { firstFrameImage = String(sourceNode.data.imageUrl); logger.debug('[NodeAdapter] Filled FIRST frame from legacy image handle (Image Container)'); }
                else if (!lastFrameImage) { lastFrameImage = String(sourceNode.data.imageUrl); logger.debug('[NodeAdapter] Filled LAST frame from legacy image handle (Image Container)'); }
              }
            } else {
              contextMessages.push({
                role: 'user',
                content: [{
                  type: 'image',
                  imageUrl: sourceNode.data.imageUrl,
                  mimeType: 'image/png'
                }]
              });
              logger.debug('[NodeAdapter] Added image from Image Container node');
            }
          }
          // Handle text output from other nodes (check output, content, and prompt)
          else {
            const textValue = sourceNode.data.output || sourceNode.data.content || sourceNode.data.prompt;
            if (textValue && typeof textValue === 'string') {
              textContexts.push(textValue);
              logger.debug('[NodeAdapter] Added text output from node:', sourceNode.type);
            }
          }
        }

        // Add text contexts to messages
        if (textContexts.length > 0) {
          contextMessages.push({
            role: 'user',
            content: `Context from connected nodes:\n\n${textContexts.join('\n\n---\n\n')}`
          });
        }

        const context = contextMessages.length > 0 ? { messages: contextMessages } : null;

        logger.debug('[NodeAdapter] Context gathered:', {
          textContexts: textContexts.length,
          totalMessages: contextMessages.length,
          hasImages: contextMessages.some(m => Array.isArray(m.content)),
          mode,
          hasFirstFrame: !!firstFrameImage,
          hasLastFrame: !!lastFrameImage
        });

        // Get parameters
        const aspectRatio = getParam(parameters, 'aspectRatio', '16:9') || parameters.size || '16:9';
        let durationSeconds = getParam(parameters, 'duration', '8');
        const resolution = getParam(parameters, 'resolution', '720p'); // For credit pricing
        // VEO "Generate Audio" toggle → GenerateVideosConfig.generateAudio (verified).
        // Only nodes that DEFINE the setting get a boolean; nodes without it (e.g.
        // Gemini Omni, no audio UI) get undefined so the server keeps its default.
        const hasAudioSetting = definition.settings?.some(s => s.id === 'enableAudio');
        const enableAudio = hasAudioSetting
          ? getParam(parameters, 'enableAudio', 'true') === 'true'
          : undefined;

        // Force 8 seconds for first-last-frame mode
        if (mode === 'first-last-frame') {
          durationSeconds = '8';
          logger.debug('[NodeAdapter] First-Last-Frame mode detected, forcing duration to 8 seconds');
        }

        // Validate first-last-frame mode
        if (mode === 'first-last-frame') {
          if (!firstFrameImage || !lastFrameImage) {
            const missing = !firstFrameImage && !lastFrameImage 
              ? 'both images' 
              : !firstFrameImage 
                ? 'first frame image' 
                : 'last frame image';
            alertService.error(`First-to-Last Frame mode requires 2 images. Missing: ${missing}`);
            setNodes((nodes) =>
              nodes.map((node) =>
                node.id === id
                  ? { ...node, data: { ...node.data, isProcessing: false } }
                  : node
              )
            );
            return;
          }
          logger.debug('[NodeAdapter] First-Last-Frame mode validated with 2 images');
        }
        
        logger.debug('[NodeAdapter] Video generation params:', {
          selectedModel,
          aspectRatio,
          durationSeconds,
          resolution,
          mode,
          hasContext: !!context,
          contextMessages: context?.messages?.length || 0
        });

        // Credits are deducted server-side. Realtime subscription will update UI.
        const creditCost = calculateVideoCreditCost(selectedModel, parseInt(durationSeconds, 10), resolution);
        console.log(`💳 [NodeAdapter] Video generation will cost ${creditCost} credits (server-side deduction)`);

        // Generate video using the VEO service (Google models)
        const currentFlowId = flowStorageService.getCurrentFlowId();

        // Helper to handle stale chunk errors from dynamic imports
        // These will be caught by the global useChunkLoadingError hook
        const handleImportError = (importError: any) => {
          if (importError.message?.includes('Failed to fetch dynamically imported module') ||
              importError.message?.includes('error loading dynamically imported module')) {
            logger.error('[NodeAdapter] Stale chunk detected, re-throwing for global handler');
            // Re-throw the original error - the global handler will show the update modal
            throw importError;
          }
          throw importError;
        };

        // Import the VEO service ONCE (dynamic import is cached) before the
        // batch loop so N videos share a single module load.
        let VeoVideoService;
        try {
          VeoVideoService = (await import('../services/VeoVideoService')).default;
        } catch (importError: any) {
          handleImportError(importError);
        }
        if (!VeoVideoService) {
          throw new Error('VEO video service failed to load');
        }

        // Batch loop: generate N distinct videos and deliver each through the
        // hardened GenerationLifecycleService tail (run-scoped target, fan-grid,
        // flow-switch guard) — mirrors the image batch pattern. Each iteration
        // makes a FRESH provider call; credits are charged server-side per call.
        let completedCount = 0;
        for (let batchIndex = 0; batchIndex < batchCount; batchIndex += 1) {
          if (isBatchCancelRequested()) break;

          let result;
          try {
            logger.debug('[NodeAdapter] Generating video with VEO-3, aspect ratio:', aspectRatio);
            result = await VeoVideoService.generateVideo(
              prompt,
              context,
              aspectRatio,
              selectedModel,
              durationSeconds,
              id,  // nodeId for job persistence
              currentFlowId || undefined,  // flowId for job persistence
              resolution,  // resolution for credit pricing
              mode as 'text-to-video' | 'image-to-video' | 'first-last-frame',
              firstFrameImage,
              lastFrameImage,
              enableAudio  // undefined for nodes without an audio toggle (e.g. Gemini Omni)
            );
          } catch (videoError) {
            // On failure, refresh balance from server (server will have refunded the reservation)
            console.log(`💳 [NodeAdapter] Video generation failed, refreshing balance from server`);
            refreshBalance().catch(err => console.warn('[NodeAdapter] Failed to refresh balance:', err));
            throw videoError;
          }

          logger.debug('[NodeAdapter] Video generated:', { url: result.videoUrl?.substring(0, 100) });

          // Keep processing on; surface batch progress like the image path.
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id
                ? { ...node, data: { ...node.data, isProcessing: true, error: null, batchProgress: { current: batchIndex + 1, total: batchCount } } }
                : node
            )
          );

          completedCount = batchIndex + 1;
          await generationLifecycleService.deliverMediaOutput(id, result.videoUrl || '', 'video', batchOutputMode, batchIndex);
        }

        // Clear processing state after the whole batch.
        setNodes((nodes) =>
          nodes.map((node) =>
            node.id === id
              ? { ...node, data: { ...node.data, isProcessing: false, batchProgress: { current: completedCount, total: batchCount }, batchCancelRequested: false } }
              : node
          )
        );
        resetBatchCancel();

      } catch (error: any) {
        logger.error('[NodeAdapter] Video generation error:', error);
        setNodes((nodes) =>
          nodes.map((node) =>
            node.id === id
              ? {
                  ...node,
                  data: {
                    ...node.data,
                    isProcessing: false,
                    error: error.message || 'Video generation failed',
                  },
                }
              : node
          )
        );
        showGenerationError(error, friendlyErrors.videoGenerationFailed);
      } finally {
        generationLifecycleService.finish(id);
      }
    }, [id, data.prompt, data.selectedModel, data.parameters, data.isProcessing, connectedTextContent, setNodes, getEdges, getNode, setEdges, refreshBalance, getBatchSettings, isBatchCancelRequested, resetBatchCancel]);

    // Handle fal.ai video generation (Kling, Wan, LTX, PixVerse, Hunyuan, MiniMax)
    const handleFalAIVideoGenerate = useCallback(async (promptOverride?: string) => {
      const prompt = typeof promptOverride === 'string'
        ? promptOverride
        : typeof data.prompt === 'string'
          ? data.prompt
          : '';
      const parameters = (data.parameters || {}) as Record<string, string | boolean | number>;
      // Batch settings once up-front (batchCount clamped 1-10). The loop below
      // generates `batchCount` distinct videos; delivery honors batchOutputMode.
      const { batchCount, batchOutputMode } = getBatchSettings();

      // Allow submission if there's either a prompt or connected text content
      if (!prompt.trim() && !connectedTextContent.trim()) {
        alertService.error('Please enter a prompt first');
        return;
      }

      if (data.isProcessing) {
        logger.debug('[NodeAdapter] Already processing, ignoring fal.ai video generate');
        return;
      }

      // Map node type to fal.ai video model ID (default model for each node type)
      const modelMap: Record<string, string> = {
        'KlingVideo': 'kling-3.0-standard',  // Unified Kling node — actual model comes from selectedModel
        'Kling26Video': 'kling-2.6',
        'Kling25Video': 'kling-2.5-turbo',
        'KlingO3Video': (parameters?.quality === 'pro') ? 'kling-3.0-pro' : 'kling-3.0-standard',
        'WanVideo': 'wan-2.2',  // Legacy — hidden in palette, kept for back-compat
        'Wan26Video': 'wan-2.7',
        'LTXVideo': 'ltx-2.3',
        'LTXFastVideo': 'ltx-2.3-fast',
        'PixVerseVideo': 'pixverse-6',
        'HunyuanVideo': 'hunyuan-1.5',
        'MinimaxVideo': 'hailuo-2.3',
        'KlingAvatar': 'kling-avatar',
        'Seedance25Video': 'seedance-2.5',
        'Seedance2Video': 'seedance-2-fast',
        'MinimaxH3Video': 'minimax-h3',
      };

      // Check for model selector override
      const selectedModel = typeof data.selectedModel === 'string' && data.selectedModel
        ? data.selectedModel
        : modelMap[nodeType];
        
      if (!selectedModel) {
        alertService.error('Unknown fal.ai video model');
        return;
      }

      // ── Prompt character-limit check ───────────────────────────────────────
      const falVidCharLimit = getPromptCharLimit(selectedModel);
      if (falVidCharLimit && prompt.length > falVidCharLimit) {
        const limitResult = await promptLimitService.check({
          fullText: prompt,
          limit: falVidCharLimit,
          modelName: getModelDisplayName(selectedModel),
          contextChars: 0,
          promptChars: prompt.length,
          onCondense: handleCondenseRequest,
        });
        if (limitResult.action === 'cancel') return;
        if (limitResult.action === 'condensed' && limitResult.condensedText) {
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id
                ? { ...node, data: { ...node.data, prompt: limitResult.condensedText } }
                : node
            )
          );
          toastService.info('Prompt optimized — review and click Send to generate.');
          return;
        }
      }
      // ───────────────────────────────────────────────────────────────────────

      logger.debug('[NodeAdapter] fal.ai video generation started:', { nodeId: id, prompt, model: selectedModel, parameters });

      // Set processing state
      generationLifecycleService.start(id);
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, isProcessing: true, error: null } }
            : node
        )
      );

      try {
        // Get context from connected nodes (text and images)
        const edges = getEdges();
        const incomingEdges = edges.filter((edge) => edge.target === id);
        
        const contextMessages: Array<{ role: 'user' | 'assistant'; content: any }> = [];
        const textContexts: string[] = [];
        let audioUrl: string | undefined;
        let firstFrameImage: string | undefined;  // For first-last-frame mode
        let lastFrameImage: string | undefined;   // For first-last-frame mode

        for (const edge of incomingEdges) {
          const sourceNode = getNode(edge.source);
          if (!sourceNode) continue;

          // Determine if this connection is for first or last frame based on targetHandle
          const isFirstFrameHandle = edge.targetHandle === 'input-image-first';
          const isLastFrameHandle = edge.targetHandle === 'input-image-last';
          // Legacy/pre-FFLF single image handle (or unlabeled). Defensive
          // fallback so saved flows the mode-change migration never touched
          // still deliver both frames to the adapter.
          const isLegacyImageHandle =
            edge.targetHandle == null ||
            edge.targetHandle === 'input' ||
            edge.targetHandle === 'input-any' ||
            edge.targetHandle === 'input-image';

          // Handle Output node with pages
          if (sourceNode.type === 'customOutput' || sourceNode.type === 'universalOutput') {
            const pages = (sourceNode.data?.pages || []) as Array<{ content: string; type?: string }>;
            const currentPageIndex = (sourceNode.data?.currentPageIndex || 0) as number;
            
            if (pages.length > 0 && pages[currentPageIndex]) {
              const currentPage = pages[currentPageIndex];
              
              if (currentPage.type === 'image' && currentPage.content) {
                // Collect images based on handle ID for first-last-frame mode
                if ((parameters.mode as string) === 'first-last-frame') {
                  if (isFirstFrameHandle) {
                    firstFrameImage = currentPage.content;
                    logger.debug('[NodeAdapter] Added FIRST frame image from Output node');
                  } else if (isLastFrameHandle) {
                    lastFrameImage = currentPage.content;
                    logger.debug('[NodeAdapter] Added LAST frame image from Output node');
                  } else if (isLegacyImageHandle) {
                    // Legacy image edge (pre-FFLF handle): fill the first open frame slot.
                    if (!firstFrameImage) { firstFrameImage = currentPage.content; logger.debug('[NodeAdapter] Filled FIRST frame from legacy image handle (Output node)'); }
                    else if (!lastFrameImage) { lastFrameImage = currentPage.content; logger.debug('[NodeAdapter] Filled LAST frame from legacy image handle (Output node)'); }
                  }
                } else {
                  // For non-first-last-frame modes, add to context
                  contextMessages.push({
                    role: 'user',
                    content: [{ type: 'image', imageUrl: currentPage.content, mimeType: 'image/png' }]
                  });
                  logger.debug('[NodeAdapter] Added image from Output node for fal.ai video');
                }
              } else if (currentPage.type === 'audio' && currentPage.content) {
                audioUrl = currentPage.content;
                logger.debug('[NodeAdapter] Added audio from Output node for fal.ai video');
              } else if (currentPage.type === 'text' || !currentPage.type) {
                textContexts.push(currentPage.content);
              }
            }
          }
          // Handle Image Container nodes
          else if (sourceNode.type === 'imagePanel' && sourceNode.data.imageUrl) {
            // Collect images based on handle ID for first-last-frame mode
            if ((parameters.mode as string) === 'first-last-frame') {
              if (isFirstFrameHandle) {
                firstFrameImage = String(sourceNode.data.imageUrl);
                logger.debug('[NodeAdapter] Added FIRST frame image from Image Container');
              } else if (isLastFrameHandle) {
                lastFrameImage = String(sourceNode.data.imageUrl);
                logger.debug('[NodeAdapter] Added LAST frame image from Image Container');
              } else if (isLegacyImageHandle) {
                // Legacy image edge (pre-FFLF handle): fill the first open frame slot.
                if (!firstFrameImage) { firstFrameImage = String(sourceNode.data.imageUrl); logger.debug('[NodeAdapter] Filled FIRST frame from legacy image handle (Image Container)'); }
                else if (!lastFrameImage) { lastFrameImage = String(sourceNode.data.imageUrl); logger.debug('[NodeAdapter] Filled LAST frame from legacy image handle (Image Container)'); }
              }
            } else {
              // For non-first-last-frame modes, add to context
              contextMessages.push({
                role: 'user',
                content: [{ type: 'image', imageUrl: sourceNode.data.imageUrl, mimeType: 'image/png' }]
              });
              logger.debug('[NodeAdapter] Added image from Image Container for fal.ai video');
            }
          }
          // Handle Audio nodes (e.g. Dictation)
          else if (sourceNode.data.audioUrl && typeof sourceNode.data.audioUrl === 'string') {
             audioUrl = sourceNode.data.audioUrl;
             logger.debug('[NodeAdapter] Added audio from source node');
          }
          // Handle text output (check output, content, and prompt)
          else {
            const textValue = sourceNode.data.output || sourceNode.data.content || sourceNode.data.prompt;
            if (textValue && typeof textValue === 'string') {
              textContexts.push(textValue);
            }
            
            // Should also check for audioUrl in generic nodes if not caught above
            if (sourceNode.data.audioUrl && typeof sourceNode.data.audioUrl === 'string' && !audioUrl) {
                audioUrl = sourceNode.data.audioUrl;
            }
          }
        }

        if (textContexts.length > 0) {
          contextMessages.push({
            role: 'user',
            content: `Context:\n\n${textContexts.join('\n\n---\n\n')}`
          });
        }

        const context = contextMessages.length > 0 ? { messages: contextMessages } : null;

        // Get parameters
        const aspectRatio = getParam(parameters, 'aspectRatio', '16:9');
        let durationSeconds = parseInt(getParam(parameters, 'duration', '5'), 10);
        const resolution = getParam(parameters, 'resolution', '720p');
        const enableAudio = getParam(parameters, 'enableAudio', 'false') === 'true';
        const mode = getParam(parameters, 'mode', 'image-to-video');

        // VEO 3.1 FFLF requires a fixed 8-second duration; Kling 3.0 and other
        // FFLF-capable models accept the user-selected duration unchanged.
        if (mode === 'first-last-frame' && selectedModel.includes('veo-3.1')) {
          durationSeconds = 8;
          logger.debug('[NodeAdapter] VEO 3.1 FFLF mode: forcing duration to 8 seconds');
        }

        // Validate first-last-frame mode
        if (mode === 'first-last-frame') {
          if (!firstFrameImage || !lastFrameImage) {
            const missing = !firstFrameImage && !lastFrameImage 
              ? 'both images' 
              : !firstFrameImage 
                ? 'first frame image' 
                : 'last frame image';
            alertService.error(`First-to-Last Frame mode requires 2 images. Missing: ${missing}`);
            setNodes((nodes) =>
              nodes.map((node) =>
                node.id === id
                  ? { ...node, data: { ...node.data, isProcessing: false } }
                  : node
              )
            );
            return;
          }
          logger.debug('[NodeAdapter] First-Last-Frame mode validated with 2 images');
        }

        // Kling Avatar: the clip is exactly as long as the connected audio —
        // the model has no duration parameter. So the seconds we RESERVE have
        // to be the seconds the audio actually runs for, not a setting. Measure
        // it here (the probe caches per URL, so this is normally already warm
        // from the effect that populates the cost badge) and bill that.
        //
        // FAIL CLOSED on anything unmeasurable: no audio connected, an
        // unreachable URL, a stream with no finite length. There is deliberately
        // NO fallback duration, because a fallback is exactly the bug being
        // fixed — the old 5s/10s dropdown under-billed a 30s track by ~4x. A
        // run we cannot price is a run we do not start.
        if (AUDIO_DRIVEN_DURATION_MODELS.has(selectedModel)) {
          const measured = audioUrl ? await probeAudioDuration(audioUrl) : null;
          const seconds = resolveAudioDrivenSeconds(measured);
          if (seconds === null) {
            alertService.error(
              audioUrl
                ? 'Could not read the length of the connected audio, so this generation cannot be priced. Re-connect the audio node and try again.'
                : 'Kling Avatar needs an audio track to lip-sync to. Connect an audio or dictation node.'
            );
            setNodes((nodes) =>
              nodes.map((node) =>
                node.id === id
                  ? { ...node, data: { ...node.data, isProcessing: false } }
                  : node
              )
            );
            return;
          }
          durationSeconds = seconds;
          // Keep the node's stored measurement in step with what we just billed,
          // so the badge and the reservation agree after the run too.
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id && node.data.connectedAudioDuration !== measured
                ? { ...node, data: { ...node.data, connectedAudioDuration: measured ?? undefined } }
                : node
            )
          );
          logger.debug(`[NodeAdapter] Kling Avatar billing ${seconds}s measured from connected audio`);
        }

        // MiniMax H3: `prompt` is the ONLY required field on minimax/h3/image-to-video —
        // `image_url` is optional, and when it is omitted fal SILENTLY runs the request
        // as text-to-video (16:9) and bills the full per-second rate. Without this guard
        // a mis-wired image input produces a t2v clip the user neither asked for nor can
        // tell apart from success, at 120 cr/s. Fail closed instead.
        // Checked against contextMessages because that is exactly where
        // FalAIVideoService.generateVideo() looks for the image-to-video image.
        if (mode === 'image-to-video' && selectedModel === 'minimax-h3') {
          const hasContextImage = contextMessages.some((msg) =>
            Array.isArray(msg.content) &&
            msg.content.some((part: any) => part?.type === 'image' && part?.imageUrl)
          );
          if (!hasContextImage) {
            alertService.error('Image-to-Video requires an image input. Connect an image, or switch the node to Text-to-Video.');
            setNodes((nodes) =>
              nodes.map((node) =>
                node.id === id
                  ? { ...node, data: { ...node.data, isProcessing: false } }
                  : node
              )
            );
            return;
          }
        }

        // Calculate and optimistically deduct credits
        // Base costs per second at 720p with ~2x margin on fal.ai pricing
        const creditCosts: Record<string, { base: number; audio: number }> = {
          'kling-2.6': { base: 32, audio: 33 },      // $0.07/sec + $0.07 audio (32 → 65 with audio)
          'kling-2.5-turbo': { base: 32, audio: 0 }, // $0.07/sec
          // Kling 3.0 — verified fal.ai pricing 2026-06-02:
          // Standard $0.084/s no-audio, $0.112/s audio; Pro $0.112/s, $0.14/s audio.
          'kling-3.0-standard': { base: 39, audio: 13 }, // 39 → 52 with audio
          'kling-3.0-pro': { base: 52, audio: 13 },      // 52 → 65 with audio
          'wan-2.2': { base: 32, audio: 0 },         // Legacy — $0.08/sec at 720p
          'ltx-2': { base: 24, audio: 0 },           // $0.06/sec at 1080p
          'ltx-2-fast': { base: 12, audio: 0 },      // ~$0.03/sec
          'pixverse-5.5': { base: 16, audio: 4 },    // $0.04/sec, +$0.01/sec audio
          'pixverse-6': { base: 21, audio: 7 },      // v6: $0.045/sec, +$0.015/sec audio
          'hunyuan-1.5': { base: 30, audio: 0 },     // $0.075/sec
          'minimax-hailuo': { base: 18, audio: 0 },  // $0.045/sec
          'hailuo-2.3': { base: 22, audio: 0 },      // Hailuo 2.3: ~$0.047/sec at 768p
          'kling-avatar': { base: 28, audio: 0 },    // $0.07/sec
          // LTX-2.3 Pro — I2V default (cheaper); T2V is repriced server-side via
          // ltx-2.3-t2v SKU at 37 cr/s @1080p. See calculateLTX23CreditCost().
          'ltx-2.3': { base: 28, audio: 0 },         // I2V $0.06/sec at 1080p
          // NOTE: this map feeds a debug LOG ONLY — the real charge is
          // server-side and the badge uses calculateVideoCreditCost(). Kept in
          // step anyway so a reader does not trust a stale price.
          'ltx-2.3-fast': { base: 28, audio: 0 },    // Fast $0.06/sec at 1080p (migration 128)
        };
        
        const costConfig = creditCosts[selectedModel] || { base: 20, audio: 0 };
        const resMultiplier = resolution === '1080p' || resolution === '1440p' ? 1.5 : 1;
        const creditCost = Math.ceil(
          (costConfig.base + (enableAudio ? costConfig.audio : 0)) * durationSeconds * resMultiplier
        );
        
        logger.debug(`💳 [NodeAdapter] fal.ai video generation will cost ${creditCost} credits (server-side deduction)`);

        // Import and use fal.ai video service
        let FalAIVideoService;
        try {
          FalAIVideoService = (await import('../services/FalAIVideoService')).default;
        } catch (importError: any) {
          // Dynamic import failed - likely a stale deployment
          // Re-throw to be caught by global useChunkLoadingError hook
          if (importError.message?.includes('Failed to fetch dynamically imported module') ||
              importError.message?.includes('error loading dynamically imported module')) {
            logger.error('[NodeAdapter] Stale chunk detected, re-throwing for global handler');
            throw importError;
          }
          throw importError;
        }
        
        // Batch loop: generate N distinct videos and deliver each through the
        // hardened GenerationLifecycleService tail (run-scoped target, fan-grid,
        // flow-switch guard). Fresh provider call per iteration; credits are
        // charged server-side per call. Mirrors the image batch pattern.
        let completedCount = 0;
        for (let batchIndex = 0; batchIndex < batchCount; batchIndex += 1) {
          if (isBatchCancelRequested()) break;

          let result;
          try {
            result = await FalAIVideoService.generateVideo(
              prompt,
              context,
              aspectRatio,
              selectedModel as any,
              durationSeconds,
              enableAudio,
              resolution,
              id,
              flowStorageService.getCurrentFlowId() || undefined,
              audioUrl,
              mode as 'text-to-video' | 'image-to-video' | 'first-last-frame',
              lastFrameImage,  // Pass last frame image for FLF mode
              firstFrameImage  // Pass first frame image for FLF mode
            );
          } catch (videoError) {
            logger.debug(`💳 [NodeAdapter] fal.ai video generation failed, refreshing balance from server`);
            refreshBalance().catch(err => console.warn('[NodeAdapter] Failed to refresh balance:', err));
            throw videoError;
          }

          logger.debug('[NodeAdapter] fal.ai video generated:', { url: result.videoUrl?.substring(0, 100) });

          // Keep processing on; surface batch progress like the image path.
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id
                ? { ...node, data: { ...node.data, isProcessing: true, error: null, batchProgress: { current: batchIndex + 1, total: batchCount } } }
                : node
            )
          );

          completedCount = batchIndex + 1;
          await generationLifecycleService.deliverMediaOutput(id, result.videoUrl || '', 'video', batchOutputMode, batchIndex);
        }

        // Clear processing state after the whole batch.
        setNodes((nodes) =>
          nodes.map((node) =>
            node.id === id
              ? { ...node, data: { ...node.data, isProcessing: false, batchProgress: { current: completedCount, total: batchCount }, batchCancelRequested: false } }
              : node
          )
        );
        resetBatchCancel();

      } catch (error: any) {
        logger.error('[NodeAdapter] fal.ai video generation error:', error);
        setNodes((nodes) =>
          nodes.map((node) =>
            node.id === id
              ? {
                  ...node,
                  data: {
                    ...node.data,
                    isProcessing: false,
                    error: error.message || 'fal.ai video generation failed',
                  },
                }
              : node
          )
        );
        // Pass suggestions from enhanced error if available
        showGenerationError(error, friendlyErrors.videoGenerationFailed, error.suggestions);
      } finally {
        generationLifecycleService.finish(id);
      }
    }, [id, nodeType, data.prompt, data.selectedModel, data.parameters, data.isProcessing, connectedTextContent, setNodes, getEdges, getNode, setEdges, refreshBalance, getBatchSettings, isBatchCancelRequested, resetBatchCancel]);

    // Handle xAI Grok Video generation (direct xAI API)
    const handleXAIVideoGenerate = useCallback(async (promptOverride?: string) => {
      const parameters = (data.parameters || {}) as Record<string, string | boolean | number>;
      // Batch settings once up-front (batchCount clamped 1-10). The loop below
      // generates `batchCount` distinct videos; delivery honors batchOutputMode.
      const { batchCount, batchOutputMode } = getBatchSettings();
      const prompt = typeof promptOverride === 'string' && promptOverride
        ? promptOverride
        : (typeof data.prompt === 'string' && data.prompt
            ? data.prompt
            : connectedTextContent || '');

      if (!prompt.trim()) {
        alertService.error('Please enter a prompt first');
        return;
      }

      if (data.isProcessing) {
        logger.debug('[NodeAdapter] Already processing, ignoring xAI video generate');
        return;
      }

      // Selected xAI model (added in BATCH 2 alongside 1.5-preview support).
      // Default to legacy grok-imagine-video. Resolved up here — before the
      // limit check — so the check knows which model's cap applies.
      const xaiSelectedModel =
        (typeof data.selectedModel === 'string' && data.selectedModel) || 'grok-imagine-video';

      // ── Prompt character-limit check ───────────────────────────────────────
      // Must run BEFORE generation starts: /api/xai/video rejects prompts over
      // 2 000 chars with a 400, and by then credits are already reserved, so
      // the user pays for a round-trip that ends in a refund.
      const xaiVidCharLimit = getPromptCharLimit(xaiSelectedModel);
      if (xaiVidCharLimit && prompt.length > xaiVidCharLimit) {
        const limitResult = await promptLimitService.check({
          fullText: prompt,
          limit: xaiVidCharLimit,
          modelName: getModelDisplayName(xaiSelectedModel),
          contextChars: 0,
          promptChars: prompt.length,
          onCondense: handleCondenseRequest,
        });
        if (limitResult.action === 'cancel') return;
        if (limitResult.action === 'condensed' && limitResult.condensedText) {
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id
                ? { ...node, data: { ...node.data, prompt: limitResult.condensedText } }
                : node
            )
          );
          toastService.info('Prompt optimized — review and click Send to generate.');
          return;
        }
      }
      // ───────────────────────────────────────────────────────────────────────

      logger.debug('[NodeAdapter] xAI video generation started:', { nodeId: id, prompt });

      // Set processing state
      generationLifecycleService.start(id);
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, isProcessing: true, error: null } }
            : node
        )
      );

      try {
        // Get context from connected nodes (text and images)
        const edges = getEdges();
        const incomingEdges = edges.filter((edge) => edge.target === id);
        
        const textContexts: string[] = [];
        let imageUrl: string | undefined;

        for (const edge of incomingEdges) {
          const sourceNode = getNode(edge.source);
          if (!sourceNode) continue;

          // Handle Output node with pages
          if (sourceNode.type === 'customOutput' || sourceNode.type === 'universalOutput') {
            const pages = (sourceNode.data?.pages || []) as Array<{ content: string; type?: string }>;
            const currentPageIndex = (sourceNode.data?.currentPageIndex || 0) as number;
            
            if (pages.length > 0 && pages[currentPageIndex]) {
              const currentPage = pages[currentPageIndex];
              if (currentPage.type === 'image' && currentPage.content) {
                if (!imageUrl) imageUrl = currentPage.content;
              } else if (currentPage.type === 'text' || !currentPage.type) {
                textContexts.push(currentPage.content);
              }
            }
          }
          // Handle Image Container nodes
          else if (sourceNode.type === 'imagePanel' && sourceNode.data.imageUrl) {
            if (!imageUrl) imageUrl = String(sourceNode.data.imageUrl);
          }
          // Handle text output
          else {
            const textValue = sourceNode.data.output || sourceNode.data.content || sourceNode.data.prompt;
            if (textValue && typeof textValue === 'string') {
              textContexts.push(textValue);
            }
          }
        }

        const context = textContexts.length > 0 
          ? { messages: [{ role: 'user' as const, content: `Context:\n\n${textContexts.join('\n\n---\n\n')}` }] }
          : null;

        // Get parameters
        const aspectRatio = getParam(parameters, 'aspectRatio', '16:9');
        const durationSeconds = parseInt(getParam(parameters, 'duration', '6'), 10);
        const resolution = getParam(parameters, 'resolution', '720p');

        // Calculate credit cost for display — mirrors the credit useMemo above.
        const pricingId =
          xaiSelectedModel === 'grok-imagine-video-1.5-preview'
            ? 'grok-imagine-video-1.5-preview'
            : 'grok-video';
        const creditCost = calculateVideoCreditCost(pricingId, durationSeconds, resolution);

        logger.debug(`💳 [NodeAdapter] xAI video generation will cost ~${creditCost} credits`);

        // Import and use xAI video service
        let XAIVideoService;
        try {
          XAIVideoService = (await import('../services/XAIVideoService')).default;
        } catch (importError: any) {
          if (importError.message?.includes('Failed to fetch dynamically imported module') ||
              importError.message?.includes('error loading dynamically imported module')) {
            throw importError;
          }
          throw importError;
        }

        // Batch loop: generate N distinct videos and deliver each through the
        // hardened GenerationLifecycleService tail (run-scoped target, fan-grid,
        // flow-switch guard). Fresh provider call per iteration; credits are
        // charged server-side per call. Mirrors the image batch pattern.
        let completedCount = 0;
        for (let batchIndex = 0; batchIndex < batchCount; batchIndex += 1) {
          if (isBatchCancelRequested()) break;

          let result;
          try {
            result = await XAIVideoService.generateVideo(
              prompt,
              context,
              aspectRatio,
              durationSeconds,
              resolution,
              id,
              flowStorageService.getCurrentFlowId() || undefined,
              imageUrl,
              xaiSelectedModel,
            );
          } catch (videoError) {
            logger.debug(`💳 [NodeAdapter] xAI video generation failed, refreshing balance from server`);
            refreshBalance().catch(err => console.warn('[NodeAdapter] Failed to refresh balance:', err));
            throw videoError;
          }

          logger.debug('[NodeAdapter] xAI video generated:', { url: result.videoUrl?.substring(0, 100) });

          // Keep processing on; surface batch progress like the image path.
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id
                ? { ...node, data: { ...node.data, isProcessing: true, error: null, batchProgress: { current: batchIndex + 1, total: batchCount } } }
                : node
            )
          );

          completedCount = batchIndex + 1;
          await generationLifecycleService.deliverMediaOutput(id, result.videoUrl || '', 'video', batchOutputMode, batchIndex);
        }

        // Clear processing state after the whole batch.
        setNodes((nodes) =>
          nodes.map((node) =>
            node.id === id
              ? { ...node, data: { ...node.data, isProcessing: false, batchProgress: { current: completedCount, total: batchCount }, batchCancelRequested: false } }
              : node
          )
        );
        resetBatchCancel();

      } catch (error: any) {
        logger.error('[NodeAdapter] xAI video generation error:', error);
        setNodes((nodes) =>
          nodes.map((node) =>
            node.id === id
              ? {
                  ...node,
                  data: {
                    ...node.data,
                    isProcessing: false,
                    error: error.message || 'xAI video generation failed',
                  },
                }
              : node
          )
        );
        showGenerationError(error, friendlyErrors.videoGenerationFailed, error.suggestions);
      } finally {
        generationLifecycleService.finish(id);
      }
    }, [id, data.prompt, data.selectedModel, data.parameters, data.isProcessing, connectedTextContent, setNodes, getEdges, getNode, setEdges, refreshBalance, getBatchSettings, isBatchCancelRequested, resetBatchCancel]);

    // Handle ElevenLabs audio generation (Voice/TTS, Sound Effects, Music).
    // Text → audio. The server route reserves/confirms/refunds credits, calls
    // ElevenLabs, uploads to R2, and returns a permanent audio URL which we
    // deliver through the hardened GenerationLifecycleService tail as 'audio'.
    const handleAudioGenerate = useCallback(async (promptOverride?: string) => {
      const parameters = (data.parameters || {}) as Record<string, string | boolean | number>;
      const { batchCount, batchOutputMode } = getBatchSettings();

      // Resolve the connected text FRESH from the store rather than reusing the
      // `connectedTextContent` subscription: that one's equality fn only
      // distinguishes empty from non-empty, so its value can lag behind a
      // streaming upstream node. TTS is billed per character, so the text sent
      // here must be the same text the credit badge counted — both go through
      // readConnectedText.
      const text = typeof promptOverride === 'string' && promptOverride
        ? promptOverride
        : (typeof data.prompt === 'string' && data.prompt
            ? data.prompt
            : readConnectedText(getEdges(), getNode, id) || '');

      if (!text.trim()) {
        alertService.error('Please enter some text first');
        return;
      }
      if (data.isProcessing) return;

      // nodeType is the PascalCase closure arg: 'VoiceTTS' | 'SoundEffects' | 'Music'.
      const audioKind = nodeType === 'SoundEffects' ? 'sfx' : nodeType === 'Music' ? 'music' : 'tts';
      const format = getParam(parameters, 'format', 'mp3_44100_128');

      // ── Prompt character-limit check ─────────────────────────────────────────
      // ElevenLabs hard-rejects over-long text, and the server route reserves
      // credits before it calls out — so this has to run before the first
      // request or the user pays for a reserve/refund round-trip.
      // Sound Effects and Eleven Music have no published character cap, so they
      // carry no table entry and this no-ops for them.
      const audioModelId =
        audioKind === 'tts'
          ? ((typeof data.selectedModel === 'string' && data.selectedModel) || 'eleven_v3')
          : audioKind === 'music'
            ? ((typeof data.selectedModel === 'string' && data.selectedModel) || 'music_v2')
            : '';
      const audioCharLimit = audioModelId ? getPromptCharLimit(audioModelId) : undefined;
      if (audioCharLimit && text.length > audioCharLimit) {
        const limitResult = await promptLimitService.check({
          fullText: text,
          limit: audioCharLimit,
          modelName: getModelDisplayName(audioModelId),
          contextChars: 0,
          promptChars: text.length,
          onCondense: handleCondenseRequest,
        });
        if (limitResult.action === 'cancel') return;
        if (limitResult.action === 'condensed' && limitResult.condensedText) {
          setNodes((nodes) =>
            nodes.map((n) =>
              n.id === id ? { ...n, data: { ...n.data, prompt: limitResult.condensedText } } : n
            )
          );
          toastService.info('Prompt optimized — review and click Send to generate.');
          return;
        }
      }
      // ─────────────────────────────────────────────────────────────────────────

      generationLifecycleService.start(id);
      setNodes((nodes) =>
        nodes.map((n) => (n.id === id ? { ...n, data: { ...n.data, isProcessing: true, error: null } } : n)),
      );

      try {
        let completedCount = 0;
        for (let batchIndex = 0; batchIndex < batchCount; batchIndex += 1) {
          if (isBatchCancelRequested()) break;

          let result;
          try {
            if (audioKind === 'tts') {
              const model = (typeof data.selectedModel === 'string' && data.selectedModel) || 'eleven_v3';
              const voiceId = getParam(parameters, 'voice', 'JBFqnCBsd6RMkjVDRZzb');
              const stability = parseFloat(getParam(parameters, 'stability', '0.5'));
              const similarityBoost = parseFloat(getParam(parameters, 'similarityBoost', '0.75'));
              result = await elevenLabsAudioService.generateSpeech({
                text, voiceId, model, format,
                stability: Number.isFinite(stability) ? stability : undefined,
                similarityBoost: Number.isFinite(similarityBoost) ? similarityBoost : undefined,
              });
            } else if (audioKind === 'sfx') {
              const durationRaw = getParam(parameters, 'duration', 'auto');
              const durationSeconds = durationRaw === 'auto' ? undefined : parseFloat(durationRaw);
              const promptInfluence = parseFloat(getParam(parameters, 'promptInfluence', '0.3'));
              result = await elevenLabsAudioService.generateSoundEffect({
                text,
                durationSeconds: durationSeconds != null && Number.isFinite(durationSeconds) ? durationSeconds : undefined,
                promptInfluence: Number.isFinite(promptInfluence) ? promptInfluence : undefined,
                format,
              });
            } else {
              const model = (typeof data.selectedModel === 'string' && data.selectedModel) || 'music_v2';
              const durationRaw = getParam(parameters, 'duration', '30');
              const durationSec = durationRaw === 'auto' ? undefined : parseFloat(durationRaw);
              result = await elevenLabsAudioService.generateMusic({
                prompt: text,
                musicLengthMs: durationSec != null && Number.isFinite(durationSec) ? Math.round(durationSec * 1000) : undefined,
                model,
                format,
              });
            }
          } catch (audioError) {
            refreshBalance().catch(() => {});
            throw audioError;
          }

          setNodes((nodes) =>
            nodes.map((n) =>
              n.id === id
                ? { ...n, data: { ...n.data, isProcessing: true, error: null, batchProgress: { current: batchIndex + 1, total: batchCount } } }
                : n,
            ),
          );

          completedCount = batchIndex + 1;
          await generationLifecycleService.deliverMediaOutput(id, result.audioUrl || '', 'audio', batchOutputMode, batchIndex);
        }

        setNodes((nodes) =>
          nodes.map((n) =>
            n.id === id
              ? { ...n, data: { ...n.data, isProcessing: false, batchProgress: { current: completedCount, total: batchCount }, batchCancelRequested: false } }
              : n,
          ),
        );
        resetBatchCancel();
        window.dispatchEvent(new CustomEvent('fileStorageUpdated'));
      } catch (error: any) {
        logger.error('[NodeAdapter] ElevenLabs audio generation error:', error);
        setNodes((nodes) =>
          nodes.map((n) => (n.id === id ? { ...n, data: { ...n.data, isProcessing: false, error: error.message || 'Audio generation failed' } } : n)),
        );
        showGenerationError(error, 'Audio generation failed. Please try again.', error.suggestions);
      } finally {
        generationLifecycleService.finish(id);
      }
    }, [id, nodeType, data.prompt, data.selectedModel, data.parameters, data.isProcessing, getEdges, getNode, setNodes, refreshBalance, getBatchSettings, isBatchCancelRequested, resetBatchCancel, getParam]);

    // Handle image processing (resize / AI enhance)
    const handleImageProcess = useCallback(async () => {
      if (data.isProcessing) return;

      const parameters = (data.parameters || {}) as Record<string, string | boolean | number>;
      const mode = getParam(parameters, 'mode', 'resize');
      const customWidth = parseInt(getParam(parameters, 'targetWidth', '1920'), 10);
      const customHeight = parseInt(getParam(parameters, 'targetHeight', '1080'), 10);
      const aspectRatio = getParam(parameters, 'aspectRatio', 'original');
      const resolution = getParam(parameters, 'resolution', 'original');
      const fitMode = getParam(parameters, 'fitMode', 'crop');
      const cropAnchor = getParam(parameters, 'cropAnchor', 'center');

      // Get input image from connected node
      const edges = getEdges();
      const incomingEdges = edges.filter((edge) => edge.target === id);
      let imageUrl: string | undefined;

      for (const edge of incomingEdges) {
        const sourceNode = getNode(edge.source);
        if (!sourceNode) continue;
        if (sourceNode.type === 'customOutput' || sourceNode.type === 'universalOutput') {
          const pages = (sourceNode.data?.pages || []) as Array<{ content: string; type?: string }>;
          const idx = (sourceNode.data?.currentPageIndex || 0) as number;
          if (pages[idx]?.type === 'image' && pages[idx]?.content) {
            imageUrl = pages[idx].content;
            break;
          }
        } else if (sourceNode.type === 'imagePanel' && sourceNode.data.imageUrl) {
          imageUrl = String(sourceNode.data.imageUrl);
          break;
        }
      }

      if (!imageUrl) {
        alertService.error('Connect an image source to the input');
        return;
      }

      // ── Resolve target dimensions ──
      // Combines (resolution preset, aspect ratio, input dims) into concrete W×H.
      // Resolution presets refer to the short edge — keeps semantics consistent
      // across orientations (e.g. "1080p" is 1920×1080 landscape and 1080×1920 portrait).
      const inputW = (data.inputWidth as number) || 0;
      const inputH = (data.inputHeight as number) || 0;
      const shortEdgePresets: Record<string, number> = {
        '720p': 720, '1080p': 1080, '2k': 1440, '4k': 2160,
      };
      const parseAR = (ar: string): number | null => {
        const m = ar.match(/^(\d+):(\d+)$/);
        if (!m) return null;
        const n = Number(m[1]) / Number(m[2]);
        return Number.isFinite(n) && n > 0 ? n : null;
      };

      let targetWidth: number;
      let targetHeight: number;

      if (resolution === 'custom') {
        targetWidth = customWidth;
        targetHeight = customHeight;
      } else {
        const arRatio = aspectRatio === 'original'
          ? (inputW > 0 && inputH > 0 ? inputW / inputH : 1)
          : (parseAR(aspectRatio) ?? 1);

        // Pick the short edge — explicit preset, else input's short edge.
        const shortEdge = shortEdgePresets[resolution]
          ?? (inputW > 0 && inputH > 0 ? Math.min(inputW, inputH) : 1080);

        if (arRatio >= 1) {
          // Landscape (or square): short edge is height
          targetHeight = shortEdge;
          targetWidth = Math.round(shortEdge * arRatio);
        } else {
          // Portrait: short edge is width
          targetWidth = shortEdge;
          targetHeight = Math.round(shortEdge / arRatio);
        }
      }

      // Bail early on degenerate dims to avoid wasted server roundtrip.
      if (!Number.isFinite(targetWidth) || !Number.isFinite(targetHeight) ||
          targetWidth < 1 || targetHeight < 1) {
        alertService.error('Could not determine target dimensions. Connect an image and try again.');
        return;
      }

      generationLifecycleService.start(id);
      setNodes((nodes) =>
        nodes.map((n) => n.id === id ? { ...n, data: { ...n.data, isProcessing: true, error: null } } : n)
      );

      try {
        const userId = await (async () => {
          const { data: { user } } = await (await import('../lib/supabase')).supabase.auth.getUser();
          return user?.id || '';
        })();

        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (userId) headers['x-user-id'] = userId;

        const response = await fetch(`${(await import('../config/api')).getApiBase()}/api/media/resize-image`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            imageUrl,
            mode,
            targetWidth,
            targetHeight,
            fitMode: aspectRatio !== 'original' ? fitMode : undefined,
            cropAnchor: aspectRatio !== 'original' && fitMode === 'crop' ? cropAnchor : undefined,
            // 'auto' tells the backend to preserve the source image's format
            // (JPEG → JPEG, PNG → PNG, WebP → WebP).
            outputFormat: 'auto',
            flowId: flowStorageService.getCurrentFlowId() || undefined,
            nodeId: id,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error((errorData as any).error || `Image processing failed: ${response.status}`);
        }

        const result = await response.json();
        const resultUrl = result.imageUrl || result.url;
        const outW = result.outputWidth as number | undefined;
        const outH = result.outputHeight as number | undefined;
        const dimLabel = outW && outH ? `${outW}×${outH}` : undefined;

        // Clear processing and store output dimensions
        setNodes((nodes) =>
          nodes.map((n) => n.id === id ? { ...n, data: { ...n.data, isProcessing: false, outputDimensions: dimLabel ?? n.data.outputDimensions } } : n)
        );

        // Send to output node
        const outgoingEdges = getEdges().filter(e => e.source === id);
        const connectedOutput = outgoingEdges.find(e => {
          const t = getNode(e.target);
          return t?.type === 'universalOutput' || t?.type === 'customOutput';
        });

        if (connectedOutput) {
          const targetNodeId = connectedOutput.target;
          setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === targetNodeId) {
                const existingPages = Array.isArray(node.data.pages) ? node.data.pages : [];
                const newPage = { content: resultUrl, context: null, type: 'image' as const, timestamp: new Date().toISOString() };
                return {
                  ...node,
                  data: { ...node.data, content: resultUrl, type: 'image', outputsMediaType: 'image', output: resultUrl, pages: [...existingPages, newPage], currentPageIndex: existingPages.length }
                };
              }
              return node;
            })
          );
        } else {
          // Create new output node
          const sourceNode = getNode(id);
          if (sourceNode) {
            const sourceWidth = sourceNode.measured?.width || sourceNode.width || 320;
            const outputNodeId = `customOutput-${Date.now()}`;
            const initialPage = { content: resultUrl, context: null, type: 'image' as const, timestamp: new Date().toISOString() };
            const outputNode = {
              id: outputNodeId, type: 'universalOutput',
              position: { x: sourceNode.position.x + sourceWidth + 50, y: sourceNode.position.y },
              width: MEDIA_OUTPUT_DIMS.width, height: MEDIA_OUTPUT_DIMS.height,
              data: { output: resultUrl, content: resultUrl, type: 'image', outputsMediaType: 'image', pages: [initialPage], currentPageIndex: 0 }
            };
            const edge = { id: `${id}-${outputNodeId}`, source: id, target: outputNodeId, type: 'image', animated: true, style: { stroke: '#42C3D7', strokeWidth: 3 } };
            setNodes((nodes) => [...nodes, outputNode]);
            setEdges((edges) => [...edges, edge]);
          }
        }

        if (mode === 'ai-enhance') {
          refreshBalance().catch(() => {});
        }

        window.dispatchEvent(new CustomEvent('fileStorageUpdated'));
      } catch (error: any) {
        logger.error('[NodeAdapter] Image processing error:', error);
        setNodes((nodes) =>
          nodes.map((n) => n.id === id ? { ...n, data: { ...n.data, isProcessing: false, error: error.message || 'Image processing failed' } } : n)
        );
        showGenerationError(error, 'Image processing failed', error.suggestions);
      } finally {
        generationLifecycleService.finish(id);
      }
    }, [id, data.parameters, data.isProcessing, setNodes, getEdges, getNode, setEdges, refreshBalance]);

    // Handle video processing (trim, split — FFmpeg.wasm client-side)
    const handleVideoProcess = useCallback(async () => {
      if (data.isProcessing) return;

      const parameters = (data.parameters || {}) as Record<string, string | boolean | number>;
      const pascalType = reactFlowTypeToType(nodeType);

      // Get input video from connected node
      const edges = getEdges();
      const incomingEdges = edges.filter((edge) => edge.target === id);
      let videoUrl: string | undefined;

      for (const edge of incomingEdges) {
        const sourceNode = getNode(edge.source);
        if (!sourceNode) continue;
        if (sourceNode.type === 'customOutput' || sourceNode.type === 'universalOutput') {
          const pages = (sourceNode.data?.pages || []) as Array<{ content: string; type?: string }>;
          const idx = (sourceNode.data?.currentPageIndex || 0) as number;
          if (pages[idx]?.type === 'video' && pages[idx]?.content) {
            videoUrl = pages[idx].content;
            break;
          }
        }
      }

      if (!videoUrl) {
        alertService.error('Connect a video source to the input');
        return;
      }

      generationLifecycleService.start(id);
      setNodes((nodes) =>
        nodes.map((n) => n.id === id ? { ...n, data: { ...n.data, isProcessing: true, error: null } } : n)
      );

      try {
        const videoTrimService = (await import('../services/VideoTrimService')).default;
        const userId = await (async () => {
          const { data: { user } } = await (await import('../lib/supabase')).supabase.auth.getUser();
          return user?.id || '';
        })();

        // Helper to upload blob to R2
        const uploadBlob = async (blob: Blob, label: string): Promise<string> => {
          const buffer = await blob.arrayBuffer();
          const headers: Record<string, string> = { 'x-user-id': userId };
          const formData = new FormData();
          formData.append('file', new File([buffer], `${label}-${Date.now()}.mp4`, { type: 'video/mp4' }));
          if (flowStorageService.getCurrentFlowId()) {
            formData.append('flowId', flowStorageService.getCurrentFlowId()!);
          }
          const response = await fetch(`${(await import('../config/api')).getApiBase()}/api/media/upload`, {
            method: 'POST',
            headers,
            body: formData,
          });
          if (!response.ok) throw new Error('Upload failed');
          const result = await response.json();
          return result.url || `/api/media/proxy?key=${encodeURIComponent(result.key)}`;
        };

        if (pascalType === 'VideoTrim') {
          // ── TRIM ──
          const startTime = parseFloat(getParam(parameters, 'startTime', '0'));
          const endTime = parseFloat(getParam(parameters, 'endTime', '5'));

          const metadata = await videoTrimService.getVideoMetadata(videoUrl);
          const clampedEnd = Math.min(endTime, metadata.duration);
          const trimDuration = clampedEnd - startTime;

          if (trimDuration <= 0) {
            throw new Error('End time must be greater than start time');
          }

          const trimmedBlob = await videoTrimService.trimVideo(videoUrl, {
            trimStartSeconds: startTime,
            trimEndSeconds: metadata.duration - clampedEnd,
          }, (progress, msg) => {
            logger.debug(`[VideoTrim] ${msg} (${(progress * 100).toFixed(0)}%)`);
          });

          const resultUrl = await uploadBlob(trimmedBlob, 'trimmed');

          // Output to connected node
          setNodes((nodes) =>
            nodes.map((n) => n.id === id ? { ...n, data: { ...n.data, isProcessing: false } } : n)
          );

          const outgoingEdges = getEdges().filter(e => e.source === id);
          const connectedOutput = outgoingEdges.find(e => {
            const t = getNode(e.target);
            return t?.type === 'universalOutput' || t?.type === 'customOutput';
          });

          if (connectedOutput) {
            setNodes((nodes) =>
              nodes.map((node) => {
                if (node.id === connectedOutput.target) {
                  const existingPages = Array.isArray(node.data.pages) ? node.data.pages : [];
                  const newPage = { content: resultUrl, context: null, type: 'video' as const, timestamp: new Date().toISOString() };
                  return { ...node, data: { ...node.data, content: resultUrl, type: 'video', outputsMediaType: 'video', output: resultUrl, pages: [...existingPages, newPage], currentPageIndex: existingPages.length } };
                }
                return node;
              })
            );
          } else {
            const sourceNode = getNode(id);
            if (sourceNode) {
              const outputNodeId = `customOutput-${Date.now()}`;
              const initialPage = { content: resultUrl, context: null, type: 'video' as const, timestamp: new Date().toISOString() };
              const outputNode = { id: outputNodeId, type: 'universalOutput', position: { x: sourceNode.position.x + (sourceNode.measured?.width || 320) + 50, y: sourceNode.position.y }, width: MEDIA_OUTPUT_DIMS.width, height: MEDIA_OUTPUT_DIMS.height, data: { output: resultUrl, content: resultUrl, type: 'video', outputsMediaType: 'video', pages: [initialPage], currentPageIndex: 0 } };
              setNodes((nodes) => [...nodes, outputNode]);
              setEdges((edges) => [...edges, { id: `${id}-${outputNodeId}`, source: id, target: outputNodeId, type: 'video', animated: true, style: { stroke: '#FF4B42', strokeWidth: 3 } }]);
            }
          }
        } else if (pascalType === 'VideoSplit') {
          // ── SPLIT ──
          const splitMode = getParam(parameters, 'splitMode', 'equal');
          const numParts = parseInt(getParam(parameters, 'numParts', '2'), 10);
          const segmentDuration = parseFloat(getParam(parameters, 'segmentDuration', '3'));

          const metadata = await videoTrimService.getVideoMetadata(videoUrl);
          const totalDuration = metadata.duration;

          // Calculate split points
          const segments: Array<{ start: number; end: number }> = [];
          if (splitMode === 'equal') {
            const partDuration = totalDuration / numParts;
            for (let i = 0; i < numParts; i++) {
              segments.push({ start: i * partDuration, end: (i + 1) * partDuration });
            }
          } else {
            let t = 0;
            while (t < totalDuration) {
              segments.push({ start: t, end: Math.min(t + segmentDuration, totalDuration) });
              t += segmentDuration;
            }
          }

          // Process each segment
          const resultUrls: string[] = [];
          for (let i = 0; i < segments.length; i++) {
            const seg = segments[i];
            const blob = await videoTrimService.trimVideo(videoUrl, {
              trimStartSeconds: seg.start,
              trimEndSeconds: totalDuration - seg.end,
            }, (progress, msg) => {
              logger.debug(`[VideoSplit] Segment ${i + 1}/${segments.length}: ${msg} (${(progress * 100).toFixed(0)}%)`);
            });
            const url = await uploadBlob(blob, `split-${i + 1}`);
            resultUrls.push(url);
          }

          // Clear processing
          setNodes((nodes) =>
            nodes.map((n) => n.id === id ? { ...n, data: { ...n.data, isProcessing: false } } : n)
          );

          // Output all segments as pages in the connected output node
          const outgoingEdges = getEdges().filter(e => e.source === id);
          const connectedOutput = outgoingEdges.find(e => {
            const t = getNode(e.target);
            return t?.type === 'universalOutput' || t?.type === 'customOutput';
          });

          const pages = resultUrls.map(url => ({ content: url, context: null, type: 'video' as const, timestamp: new Date().toISOString() }));

          if (connectedOutput) {
            setNodes((nodes) =>
              nodes.map((node) => {
                if (node.id === connectedOutput.target) {
                  return { ...node, data: { ...node.data, content: resultUrls[0], type: 'video', outputsMediaType: 'video', output: resultUrls[0], pages, currentPageIndex: 0 } };
                }
                return node;
              })
            );
          } else {
            const sourceNode = getNode(id);
            if (sourceNode) {
              const outputNodeId = `customOutput-${Date.now()}`;
              const outputNode = { id: outputNodeId, type: 'universalOutput', position: { x: sourceNode.position.x + (sourceNode.measured?.width || 320) + 50, y: sourceNode.position.y }, width: MEDIA_OUTPUT_DIMS.width, height: MEDIA_OUTPUT_DIMS.height, data: { output: resultUrls[0], content: resultUrls[0], type: 'video', outputsMediaType: 'video', pages, currentPageIndex: 0 } };
              setNodes((nodes) => [...nodes, outputNode]);
              setEdges((edges) => [...edges, { id: `${id}-${outputNodeId}`, source: id, target: outputNodeId, type: 'video', animated: true, style: { stroke: '#FF4B42', strokeWidth: 3 } }]);
            }
          }
        } else if (pascalType === 'VideoScale') {
          // ── VIDEO SCALE ──
          const mode = getParam(parameters, 'mode', 'resize');
          const targetResolution = getParam(parameters, 'targetResolution', '1080p');
          const crop = getParam(parameters, 'crop', 'none');

          const headers: Record<string, string> = { 'Content-Type': 'application/json' };
          if (userId) headers['x-user-id'] = userId;

          // Send the extracted input video duration so the server can compute the
          // correct per-second × resolution credit reservation. Server falls back
          // to its own probe if this is missing.
          const inputDuration = typeof data.inputVideoDuration === 'number' && data.inputVideoDuration > 0
            ? data.inputVideoDuration
            : undefined;

          const response = await fetch(`${(await import('../config/api')).getApiBase()}/api/media/resize-video`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              videoUrl,
              mode,
              targetResolution,
              crop,
              videoDurationSeconds: inputDuration,
              flowId: flowStorageService.getCurrentFlowId() || undefined,
              nodeId: id,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error((errorData as any).error || `Video scaling failed: ${response.status}`);
          }

          const result = await response.json();
          const resultUrl = result.videoUrl || result.url;
          const vOutW = result.outputWidth as number | undefined;
          const vOutH = result.outputHeight as number | undefined;
          const vDimLabel = vOutW && vOutH ? `${vOutW}×${vOutH}` : undefined;

          setNodes((nodes) =>
            nodes.map((n) => n.id === id ? { ...n, data: { ...n.data, isProcessing: false, outputDimensions: vDimLabel ?? n.data.outputDimensions } } : n)
          );

          const outgoingEdges = getEdges().filter(e => e.source === id);
          const connectedOutput = outgoingEdges.find(e => {
            const t = getNode(e.target);
            return t?.type === 'universalOutput' || t?.type === 'customOutput';
          });

          if (connectedOutput) {
            setNodes((nodes) =>
              nodes.map((node) => {
                if (node.id === connectedOutput.target) {
                  const existingPages = Array.isArray(node.data.pages) ? node.data.pages : [];
                  const newPage = { content: resultUrl, context: null, type: 'video' as const, timestamp: new Date().toISOString() };
                  return { ...node, data: { ...node.data, content: resultUrl, type: 'video', outputsMediaType: 'video', output: resultUrl, pages: [...existingPages, newPage], currentPageIndex: existingPages.length } };
                }
                return node;
              })
            );
          } else {
            const sourceNode = getNode(id);
            if (sourceNode) {
              const outputNodeId = `customOutput-${Date.now()}`;
              const initialPage = { content: resultUrl, context: null, type: 'video' as const, timestamp: new Date().toISOString() };
              const outputNode = { id: outputNodeId, type: 'universalOutput', position: { x: sourceNode.position.x + (sourceNode.measured?.width || 320) + 50, y: sourceNode.position.y }, width: MEDIA_OUTPUT_DIMS.width, height: MEDIA_OUTPUT_DIMS.height, data: { output: resultUrl, content: resultUrl, type: 'video', outputsMediaType: 'video', pages: [initialPage], currentPageIndex: 0 } };
              setNodes((nodes) => [...nodes, outputNode]);
              setEdges((edges) => [...edges, { id: `${id}-${outputNodeId}`, source: id, target: outputNodeId, type: 'video', animated: true, style: { stroke: '#FF4B42', strokeWidth: 3 } }]);
            }
          }

          if (mode === 'ai-enhance') refreshBalance().catch(() => {});
        } else if (pascalType === 'VideoStitch') {
          // ── VIDEO STITCH ──
          // Collect connected videos in clip order (sorted by labeled handle: input-video-1, input-video-2, ...)
          const clipSlots: Record<string, string> = {};
          for (const edge of incomingEdges) {
            const sourceNode = getNode(edge.source);
            if (!sourceNode) continue;
            let videoUrl: string | undefined;
            if (sourceNode.type === 'customOutput' || sourceNode.type === 'universalOutput') {
              const pages = (sourceNode.data?.pages || []) as Array<{ content: string; type?: string }>;
              const idx = (sourceNode.data?.currentPageIndex || 0) as number;
              if (pages[idx]?.type === 'video' && pages[idx]?.content) {
                videoUrl = pages[idx].content;
              }
            }
            if (videoUrl && edge.targetHandle) {
              clipSlots[edge.targetHandle] = videoUrl;
            } else if (videoUrl) {
              // Fallback: append to any available slot
              clipSlots[`input-video-${Object.keys(clipSlots).length + 1}`] = videoUrl;
            }
          }
          // Sort by clip number to maintain order
          const allVideoUrls = Object.keys(clipSlots)
            .sort((a, b) => {
              const nA = parseInt(a.replace(/\D/g, ''), 10) || 0;
              const nB = parseInt(b.replace(/\D/g, ''), 10) || 0;
              return nA - nB;
            })
            .map((k) => clipSlots[k]);

          if (allVideoUrls.length < 2) {
            throw new Error('Connect at least 2 video clips to stitch');
          }

          const outputResolution = getParam(parameters, 'outputResolution', 'match-first');

          const headers: Record<string, string> = { 'Content-Type': 'application/json' };
          if (userId) headers['x-user-id'] = userId;

          const response = await fetch(`${(await import('../config/api')).getApiBase()}/api/media/stitch`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              videoUrls: allVideoUrls,
              outputResolution,
              flowId: flowStorageService.getCurrentFlowId() || undefined,
              nodeId: id,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error((errorData as any).error || `Video stitching failed: ${response.status}`);
          }

          const result = await response.json();
          const resultUrl = result.videoUrl || result.url;
          const dimLabel = (result.outputWidth && result.outputHeight)
            ? `${result.outputWidth}×${result.outputHeight}`
            : undefined;

          setNodes((nodes) =>
            nodes.map((n) => n.id === id ? {
              ...n,
              data: { ...n.data, isProcessing: false, ...(dimLabel ? { outputDimensions: dimLabel } : {}) },
            } : n)
          );

          const outgoingEdges = getEdges().filter(e => e.source === id);
          const connectedOutput = outgoingEdges.find(e => {
            const t = getNode(e.target);
            return t?.type === 'universalOutput' || t?.type === 'customOutput';
          });

          if (connectedOutput) {
            setNodes((nodes) =>
              nodes.map((node) => {
                if (node.id === connectedOutput.target) {
                  const existingPages = Array.isArray(node.data.pages) ? node.data.pages : [];
                  const newPage = { content: resultUrl, context: null, type: 'video' as const, timestamp: new Date().toISOString() };
                  return { ...node, data: { ...node.data, content: resultUrl, type: 'video', outputsMediaType: 'video', output: resultUrl, pages: [...existingPages, newPage], currentPageIndex: existingPages.length } };
                }
                return node;
              })
            );
          } else {
            const sourceNode = getNode(id);
            if (sourceNode) {
              const outputNodeId = `customOutput-${Date.now()}`;
              const initialPage = { content: resultUrl, context: null, type: 'video' as const, timestamp: new Date().toISOString() };
              const outputNode = { id: outputNodeId, type: 'universalOutput', position: { x: sourceNode.position.x + (sourceNode.measured?.width || 320) + 50, y: sourceNode.position.y }, width: MEDIA_OUTPUT_DIMS.width, height: MEDIA_OUTPUT_DIMS.height, data: { output: resultUrl, content: resultUrl, type: 'video', outputsMediaType: 'video', pages: [initialPage], currentPageIndex: 0 } };
              setNodes((nodes) => [...nodes, outputNode]);
              setEdges((edges) => [...edges, { id: `${id}-${outputNodeId}`, source: id, target: outputNodeId, type: 'video', animated: true, style: { stroke: '#FF4B42', strokeWidth: 3 } }]);
            }
          }
        }

        window.dispatchEvent(new CustomEvent('fileStorageUpdated'));
      } catch (error: any) {
        logger.error('[NodeAdapter] Video processing error:', error);
        setNodes((nodes) =>
          nodes.map((n) => n.id === id ? { ...n, data: { ...n.data, isProcessing: false, error: error.message || 'Video processing failed' } } : n)
        );
        showGenerationError(error, 'Video processing failed');
      } finally {
        generationLifecycleService.finish(id);
      }
    }, [id, nodeType, data.parameters, data.isProcessing, setNodes, getEdges, getNode, setEdges, refreshBalance]);

    // Handle HeyGen video generation (via fal.ai)
    const handleHeygenVideoGenerate = useCallback(async () => {
      const parameters = (data.parameters || {}) as Record<string, string | boolean | number>;
      // For Lipsync: the selected variant lives in the lipsyncVariant parameter
      // (rather than a separate model dropdown) so the registry default `selectedModel`
      // gets overridden if the user picks Precision.
      // `nodeType` is the PascalCase closure arg from createNodeAdapter('HeyGenLipsync'),
      // NOT the camelCase React Flow type ('heyGenLipsync'). Comparing against the
      // camelCase spelling here silently pinned every lipsync run to Speed while the
      // cost badge (which compares correctly, below) quoted Precision. Normalise the
      // input so either spelling resolves, and compare against the registry type.
      const isLipsyncNode = reactFlowTypeToType(nodeType) === 'HeyGenLipsync';
      const lipsyncVariant = getParam(parameters, 'lipsyncVariant', 'heygen-lipsync-speed');
      const selectedModel = isLipsyncNode
        ? lipsyncVariant
        : (data.selectedModel as string) || 'heygen-digital-twin-v3';
      const prompt = data.prompt as string || '';

      if (data.isProcessing) {
        logger.debug('[NodeAdapter] Already processing, ignoring HeyGen generate');
        return;
      }

      const isTranslate = selectedModel === 'heygen-translate-speed' || selectedModel === 'heygen-translate-precision';
      const isPhotoVideo = selectedModel === 'heygen-image-to-video';
      const isLipsync = selectedModel === 'heygen-lipsync-speed' || selectedModel === 'heygen-lipsync-precision';

      if (!isTranslate && !isLipsync && !prompt?.trim()) {
        alertService.error('Please enter a script before generating.');
        return;
      }

      // ── Prompt character-limit check ─────────────────────────────────────────
      // Runs before the fal.ai/HeyGen call so credits are never reserved for a
      // script HeyGen would reject. Only the script-driven models (avatar
      // digital twins, video agent) carry a limit entry — translate, lipsync
      // and photo-video take no script, so this no-ops for them.
      const heygenCharLimit = getPromptCharLimit(selectedModel);
      if (heygenCharLimit && prompt.length > heygenCharLimit) {
        const limitResult = await promptLimitService.check({
          fullText: prompt,
          limit: heygenCharLimit,
          modelName: getModelDisplayName(selectedModel),
          contextChars: 0,
          promptChars: prompt.length,
          onCondense: handleCondenseRequest,
        });
        if (limitResult.action === 'cancel') return;
        if (limitResult.action === 'condensed' && limitResult.condensedText) {
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id
                ? { ...node, data: { ...node.data, prompt: limitResult.condensedText } }
                : node
            )
          );
          toastService.info('Prompt optimized — review and click Send to generate.');
          return;
        }
      }
      // ─────────────────────────────────────────────────────────────────────────

      logger.debug('[NodeAdapter] HeyGen generation started:', { nodeId: id, selectedModel });

      generationLifecycleService.start(id);
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id ? { ...node, data: { ...node.data, isProcessing: true, error: null } } : node
        )
      );

      try {
        const edges = getEdges();
        const incomingEdges = edges.filter((edge) => edge.target === id);

        let videoUrl: string | undefined;
        let imageUrl: string | undefined;
        let audioUrl: string | undefined;

        // Extract connected media from upstream Output nodes
        for (const edge of incomingEdges) {
          const sourceNode = getNode(edge.source);
          if (!sourceNode) continue;

          if (sourceNode.type === 'customOutput' || sourceNode.type === 'universalOutput') {
            const pages = (sourceNode.data?.pages || []) as Array<{ content: string; type?: string }>;
            const currentPageIndex = (sourceNode.data?.currentPageIndex || 0) as number;
            if (pages.length > 0 && pages[currentPageIndex]) {
              const currentPage = pages[currentPageIndex];
              if ((isTranslate || isLipsync) && currentPage.type === 'video' && currentPage.content) {
                videoUrl = currentPage.content;
              } else if (isPhotoVideo && currentPage.type === 'image' && currentPage.content) {
                imageUrl = currentPage.content;
              } else if (isLipsync && currentPage.type === 'audio' && currentPage.content) {
                audioUrl = currentPage.content;
              }
            }
          }
        }

        if (isTranslate && !videoUrl) {
          alertService.error('Please connect an Output node containing a video to translate.');
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id ? { ...node, data: { ...node.data, isProcessing: false } } : node
            )
          );
          return;
        }

        if (isLipsync && (!videoUrl || !audioUrl)) {
          alertService.error('Lipsync requires both a video input (face to re-sync) and an audio input (replacement audio).');
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id ? { ...node, data: { ...node.data, isProcessing: false } } : node
            )
          );
          return;
        }

        if (isPhotoVideo && !imageUrl) {
          alertService.error('Please connect an Output node containing an image to animate.');
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id ? { ...node, data: { ...node.data, isProcessing: false } } : node
            )
          );
          return;
        }

        const duration = parseInt(getParam(parameters, 'duration', '30'), 10);
        const resolution = getParam(parameters, 'resolution', '720p');
        const avatar = getParam(parameters, 'avatar', 'Adriana SuitSofa Front');
        const voice = getParam(parameters, 'voice', 'Warm Pro Narrator');
        const outputLanguage = getParam(parameters, 'outputLanguage', 'Spanish');
        const orientation = getParam(parameters, 'orientation', 'landscape');
        const talkingStyle = getParam(parameters, 'talkingStyle', 'stable');

        // Dynamic import (same pattern as handleFalAIVideoGenerate)
        let FalAIVideoService;
        try {
          FalAIVideoService = (await import('../services/FalAIVideoService')).default;
        } catch (importError: any) {
          if (importError.message?.includes('Failed to fetch dynamically imported module') ||
              importError.message?.includes('error loading dynamically imported module')) {
            logger.error('[NodeAdapter] Stale chunk detected, re-throwing for global handler');
            throw importError;
          }
          throw importError;
        }

        // Lipsync-specific optional params
        const enableDynamicDuration = parameters.enableDynamicDuration as boolean | undefined;
        const enableSpeechEnhancement = parameters.enableSpeechEnhancement as boolean | undefined;
        const disableMusicTrack = parameters.disableMusicTrack as boolean | undefined;
        const enableCaption = parameters.enableCaption as boolean | undefined;

        const result = await FalAIVideoService.generateHeygenVideo({
          model: selectedModel,
          prompt,
          avatar,
          voice,
          resolution,
          talkingStyle,
          videoUrl,
          audioUrl,
          outputLanguage,
          orientation,
          duration,
          imageUrl,
          enableDynamicDuration,
          enableSpeechEnhancement,
          disableMusicTrack,
          enableCaption,
          flowId: data.flowId as string | undefined,
          nodeId: id,
        });

        // Reset processing state
        setNodes((nodes) =>
          nodes.map((node) =>
            node.id === id ? { ...node, data: { ...node.data, isProcessing: false, error: null } } : node
          )
        );

        if (!result.videoUrl) {
          throw new Error('No video URL returned from HeyGen');
        }

        // Deliver via the hardened GenerationLifecycleService tail so the result
        // lands on the correct output (run-scoped target) with no empty/duplicate
        // pages. HeyGen is single-output/deterministic — no batch loop, batchIndex 0.
        // deliverMediaOutput dispatches the fileStorageUpdated refresh itself.
        const { batchOutputMode: heygenOutputMode } = getBatchSettings();
        await generationLifecycleService.deliverMediaOutput(id, result.videoUrl, 'video', heygenOutputMode, 0);

        refreshBalance?.();

      } catch (error: any) {
        logger.error('[NodeAdapter] HeyGen generation error:', error);
        setNodes((nodes) =>
          nodes.map((node) =>
            node.id === id
              ? { ...node, data: { ...node.data, isProcessing: false, error: error.message || 'HeyGen video generation failed' } }
              : node
          )
        );
        showGenerationError(error, error.message || 'HeyGen video generation failed');
      } finally {
        generationLifecycleService.finish(id);
      }
    }, [id, nodeType, data.prompt, data.selectedModel, data.parameters, data.isProcessing, data.flowId, setNodes, getEdges, getNode, setEdges, refreshBalance, getParam, getBatchSettings]);

    // Handle Wan Animate generation (video-to-video character animation)
    const handleWanAnimateGenerate = useCallback(async () => {
      const parameters = (data.parameters || {}) as Record<string, string | boolean | number>;

      if (data.isProcessing) {
        logger.debug('[NodeAdapter] Already processing, ignoring Wan Animate generate');
        return;
      }

      logger.debug('[NodeAdapter] Wan Animate generation started:', { nodeId: id, parameters });

      // Set processing state with 10-minute estimate for V2V
      generationLifecycleService.start(id);
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, isProcessing: true, estimatedDuration: 600, error: null } }
            : node
        )
      );

      try {
        // Get video and image from connected nodes
        const edges = getEdges();
        const incomingEdges = edges.filter((edge) => edge.target === id);
        
        let videoUrl: string | undefined;
        let imageUrl: string | undefined;

        for (const edge of incomingEdges) {
          const sourceNode = getNode(edge.source);
          if (!sourceNode) continue;

          // Check Output node pages
          if (sourceNode.type === 'customOutput' || sourceNode.type === 'universalOutput') {
            const pages = (sourceNode.data?.pages || []) as Array<{ content: string; type?: string }>;
            const currentPageIndex = (sourceNode.data?.currentPageIndex || 0) as number;
            
            if (pages.length > 0 && pages[currentPageIndex]) {
              const currentPage = pages[currentPageIndex];
              
              if (currentPage.type === 'video' && currentPage.content) {
                videoUrl = currentPage.content;
                logger.debug('[NodeAdapter] Added video from Output node for Wan Animate');
              } else if (currentPage.type === 'image' && currentPage.content) {
                imageUrl = currentPage.content;
                logger.debug('[NodeAdapter] Added image from Output node for Wan Animate');
              }
            }
          }
          // Check Image Container
          else if (sourceNode.type === 'imagePanel' && sourceNode.data.imageUrl) {
            imageUrl = String(sourceNode.data.imageUrl);
            logger.debug('[NodeAdapter] Added image from Image Container for Wan Animate');
          }
          // Check for video content in node data
          else if (sourceNode.data.videoUrl || (sourceNode.data.output && typeof sourceNode.data.output === 'string' && sourceNode.data.type === 'video')) {
            videoUrl = (sourceNode.data.videoUrl || sourceNode.data.output) as string;
            logger.debug('[NodeAdapter] Added video from source node for Wan Animate');
          }
          // Check for image content
          else if (sourceNode.data.imageUrl) {
            imageUrl = String(sourceNode.data.imageUrl);
            logger.debug('[NodeAdapter] Added image from source node for Wan Animate');
          }
        }

        // Validate inputs
        if (!videoUrl) {
          toastService.error('Connect a VIDEO node as motion reference');
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id
                ? { ...node, data: { ...node.data, isProcessing: false } }
                : node
            )
          );
          return;
        }

        if (!imageUrl) {
          toastService.error('Connect an IMAGE node for the character to animate');
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id
                ? { ...node, data: { ...node.data, isProcessing: false } }
                : node
            )
          );
          return;
        }

        // Check video duration and frame count - block videos over 20 seconds, confirm for 5+ seconds/81+ frames
        // Use the inputVideoDuration from the useEffect if available, otherwise extract it
        let extractedVideoDuration = typeof data.inputVideoDuration === 'number' && data.inputVideoDuration > 0
          ? data.inputVideoDuration
          : 5; // Default fallback
        
        // Extract duration if not already set by useEffect
        if (!data.inputVideoDuration) {
          try {
            const videoElement = document.createElement('video');
            videoElement.preload = 'metadata';
            videoElement.src = videoUrl;
            
            await new Promise<void>((resolve) => {
              videoElement.onloadedmetadata = () => resolve();
              videoElement.onerror = () => resolve();
              setTimeout(() => resolve(), 10000);
            });
            
            const videoDuration = videoElement.duration;
            if (videoDuration && !isNaN(videoDuration) && isFinite(videoDuration)) {
              extractedVideoDuration = videoDuration;
            }
          } catch (durationError) {
            // Non-blocking - continue with default
            logger.debug('[NodeAdapter] Could not get video duration, using default:', durationError);
          }
        }
        
        // Block videos longer than 20 seconds
        if (extractedVideoDuration > 20) {
          toastService.error(`Video is ${Math.round(extractedVideoDuration)} seconds. Maximum allowed is 20 seconds.`);
          logger.debug('[NodeAdapter] Wan Animate video too long:', extractedVideoDuration);
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id
                ? { ...node, data: { ...node.data, isProcessing: false } }
                : node
            )
          );
          return;
        }
        
        // Check for 5+ seconds or 81+ frames and show confirmation
        // Wan Animate optimal quality is ~81 frames at 16.2fps = 5 seconds
        const estimatedFps = 16.2;
        const estimatedFrames = Math.round(extractedVideoDuration * estimatedFps);
        const framesOver81 = estimatedFrames > 81;
        const durationOver5 = extractedVideoDuration > 5;
        
        if (durationOver5 || framesOver81) {
          const durationText = `${extractedVideoDuration.toFixed(1)} seconds (~${estimatedFrames} frames)`;
          
          const shouldContinue = await confirmDialogService.confirm({
            title: '⚠️ Video Quality Warning',
            message: `Your video is ${durationText}. Videos longer than 5 seconds (81 frames) may experience quality degradation.\n\nFor best results, we recommend:\n• Split your video into 5-second chunks\n• Process each chunk separately\n• Combine results afterward`,
            confirmText: 'Proceed Anyway',
            cancelText: 'Cancel',
            type: 'warning'
          });
          
          if (!shouldContinue) {
            logger.debug('[NodeAdapter] Wan Animate generation cancelled by user for long video:', extractedVideoDuration);
            setNodes((nodes) =>
              nodes.map((node) =>
                node.id === id
                  ? { ...node, data: { ...node.data, isProcessing: false } }
                  : node
              )
            );
            return;
          }
          
          logger.debug('[NodeAdapter] Wan Animate proceeding with long video:', { duration: extractedVideoDuration, frames: estimatedFrames });
        }

        // Get parameters
        const mode = getParam(parameters, 'mode', 'replace');
        const resolution = getParam(parameters, 'resolution', '480p');
        const useTurbo = getParam(parameters, 'useTurbo', 'false') === 'true';
        const numInferenceSteps = parameters.numInferenceSteps ? Number(parameters.numInferenceSteps) : undefined;
        const guidanceScale = parameters.guidanceScale ? Number(parameters.guidanceScale) : 1;

        const authHeaders = await getAuthHeaders();

        // Call backend with video duration for accurate credit calculation
        const response = await fetch('/api/falai/animate', {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            videoUrl,
            imageUrl,
            mode,
            resolution,
            useTurbo,
            numInferenceSteps,
            guidanceScale,
            durationSeconds: Math.round(extractedVideoDuration), // Pass duration for credit calculation
            flowId: flowStorageService.getCurrentFlowId() || undefined,
            nodeId: id,
          }),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Animation generation failed');
        }

        // Job queued - poll for completion
        if ((result.status === 'pending' || result.status === 'queued') && result.jobId) {
          logger.debug('[NodeAdapter] Wan Animate job queued:', result.jobId);
          
          // Store job ID for polling
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id
                ? { ...node, data: { ...node.data, jobId: result.jobId, jobStatus: 'processing' } }
                : node
            )
          );
          
          // Start polling for job completion. The lifecycle run ends when this
          // handler returns; the poller registry is what tells canvas undo the
          // job is still in flight.
          markJobPolling(id, result.jobId);
          const pollInterval = setInterval(async () => {
            try {
              const statusResponse = await fetch(`/api/jobs/${result.jobId}`, {
                headers: authHeaders,
              });
              const statusData = await statusResponse.json();
              
              // The API returns { job: { status, result_url, ... } }
              const job = statusData.job;
              if (!job) {
                logger.warn('[NodeAdapter] No job data in response:', statusData);
                return;
              }
              
              if (job.status === 'completed') {
                clearInterval(pollInterval);
                endJobPolling(id, result.jobId);
                
                // Use resultUrl from the job record (camelCase from API)
                const resultUrl = job.resultUrl;
                logger.debug('[NodeAdapter] Wan Animate job completed, result:', resultUrl);
                
                // Update source node with result and stop processing
                setNodes((nodes) =>
                  nodes.map((node) =>
                    node.id === id
                      ? { 
                          ...node, 
                          data: { 
                            ...node.data, 
                            isProcessing: false,
                            jobStatus: 'completed',
                            output: resultUrl,
                            content: resultUrl,
                            type: 'video',
                          } 
                        }
                      : node
                  )
                );
                
                // Deliver via the hardened GenerationLifecycleService tail so the
                // result lands on the correct output (run-scoped target) with no
                // empty/duplicate pages. Single-output/deterministic — batchIndex 0.
                // deliverMediaOutput dispatches the fileStorageUpdated refresh itself.
                const { batchOutputMode: wanAnimateOutputMode } = getBatchSettings();
                await generationLifecycleService.deliverMediaOutput(id, resultUrl, 'video', wanAnimateOutputMode, 0);

                refreshBalance();
                toastService.success('Animation completed!');
              } else if (job.status === 'failed') {
                clearInterval(pollInterval);
                endJobPolling(id, result.jobId);
                setNodes((nodes) =>
                  nodes.map((node) =>
                    node.id === id
                      ? { ...node, data: { ...node.data, ...endedJobPatch('failed', job.errorMessage) } }
                      : node
                  )
                );
                toastService.error(job.errorMessage || 'Animation failed');
              }
            } catch (pollError) {
              logger.error('[NodeAdapter] Wan Animate poll error:', pollError);
            }
          }, 3000); // Poll every 3 seconds
          
          // Timeout after 20 minutes - reset isProcessing state
          setTimeout(() => {
            clearInterval(pollInterval);
            endJobPolling(id, result.jobId);
            // Check if node is still processing (job didn't complete)
            setNodes((currentNodes) => {
              const node = currentNodes.find(n => n.id === id);
              if (node && node.data?.isProcessing) {
                logger.warn('[NodeAdapter] Wan Animate job timed out after 20 minutes:', id);
                alertService.error('Animation timed out. Check the Assets panel for results.');
                return currentNodes.map(n => 
                  n.id === id 
                    ? { ...n, data: { ...n.data, ...endedJobPatch('timeout', 'Generation timed out') } }
                    : n
                );
              }
              return currentNodes;
            });
          }, 20 * 60 * 1000);
        }

      } catch (error: any) {
        logger.error('[NodeAdapter] Wan Animate generation failed:', error);
        setNodes((nodes) =>
          nodes.map((node) =>
            node.id === id
              ? { ...node, data: { ...node.data, isProcessing: false, error: error.message } }
              : node
          )
        );
        showGenerationError(error, friendlyErrors.videoGenerationFailed);
      } finally {
        generationLifecycleService.finish(id);
      }
    }, [id, data.parameters, data.isProcessing, setNodes, getEdges, getNode, refreshBalance, getBatchSettings]);

    // Handle variable changes for custom nodes
    const handleVariableChange = useCallback((variableName: string, value: string) => {
      recordUserAction('Edit variable', { coalesceKey: `text:${id}:var:${variableName}` });
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? {
                ...node,
                data: {
                  ...node.data,
                  variables: {
                    ...(node.data.variables || {}),
                    [variableName]: value,
                  },
                },
              }
            : node
        )
      );
      markDirty(); // Trigger auto-save
    }, [id, setNodes, markDirty]);

    // Handle execute callback for custom nodes (PromptTemplate)
    const handleExecute = useCallback((result: any) => {
      logger.debug('[NodeAdapter] Custom node execution result:', { nodeId: id, result, streaming: result?.streaming });
      
      // Determine output type from result
      let outputType: 'text' | 'image' | 'video' = 'text';
      let outputContent = '';
      
      if (typeof result === 'string') {
        outputContent = result;
        outputType = 'text';
      } else if (result?.type === 'image' || result?.imageUrl) {
        outputContent = result.imageUrl || result.content || '';
        outputType = 'image';
      } else if (result?.type === 'video' || result?.videoUrl) {
        outputContent = result.videoUrl || result.content || '';
        outputType = 'video';
      } else if (result?.content) {
        outputContent = result.content;
        outputType = result.type || 'text';
      }
      
      // For streaming updates, only update the output node content, don't recreate nodes/edges
      if (result?.streaming) {
        const allEdges = getEdges();
        const outgoingEdges = allEdges.filter(edge => edge.source === id);
        const existingOutputEdge = outgoingEdges.find(edge => {
          const targetNode = getNode(edge.target);
          return targetNode?.type === 'universalOutput' || targetNode?.type === 'customOutput';
        });
        
        if (existingOutputEdge) {
          const outputNodeId = existingOutputEdge.target;
          setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === outputNodeId) {
                const existingPages = Array.isArray(node.data.pages) ? node.data.pages : [];
                const currentPageIndex = node.data.currentPageIndex || 0;
                const updatedPages = existingPages.length > 0
                  ? existingPages.map((page, idx) =>
                      idx === currentPageIndex
                        ? { ...page, content: outputContent }
                        : page
                    )
                  : [{ content: outputContent, context: null, type: outputType, timestamp: new Date().toISOString() }];
                
                return {
                  ...node,
                  data: {
                    ...node.data,
                    content: outputContent,
                    pages: updatedPages,
                    streamingInProgress: true
                  }
                };
              }
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    output: outputContent,
                    streamingInProgress: true
                  }
                };
              }
              return node;
            })
          );
        }
        return; // Don't continue with normal flow for streaming updates
      }
      
      // Update source node data with result
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? {
                ...node,
                data: {
                  ...node.data,
                  lastResult: result,
                  lastExecuted: new Date().toISOString(),
                  output: outputContent,
                  isProcessing: false,
                  streamingInProgress: false,
                },
              }
            : node
        )
      );
      
      // Create or update output node
      const allEdges = getEdges();
      const outgoingEdges = allEdges.filter(edge => edge.source === id);
      const existingOutputEdge = outgoingEdges.find(edge => {
        const targetNode = getNode(edge.target);
        return targetNode?.type === 'universalOutput' || targetNode?.type === 'customOutput';
      });
      
      if (!existingOutputEdge) {
        // Create new output node
        const sourceNode = getNode(id);
        if (sourceNode) {
          const sourceWidth = sourceNode.measured?.width || sourceNode.width || 320;
          const outputNodeId = `customOutput-${Date.now()}`;
          
          const initialPage = {
            content: outputContent,
            context: null,
            type: outputType,
            timestamp: new Date().toISOString()
          };
          
          const outputNode = {
            id: outputNodeId,
            type: 'universalOutput',
            position: {
              x: sourceNode.position.x + sourceWidth + 50,
              y: sourceNode.position.y
            },
            width: getOutputNodeDimensions(outputType).width,
            height: getOutputNodeDimensions(outputType).height,
            data: {
              output: outputContent,
              content: outputContent,
              type: outputType,
              outputsMediaType: outputType,
              pages: [initialPage],
              currentPageIndex: 0,
              streamingInProgress: false,
              ...(outputType === 'image' && { imageUrl: outputContent }),
              ...(outputType === 'video' && { videoUrl: outputContent })
            }
          };
          
          // Map output type to edge color (shared EDGE_COLOR_MAP)
          const edgeColor = EDGE_COLOR_MAP[outputType] || EDGE_COLOR_MAP['any'];
          
          const edge = {
            id: `${id}-${outputNodeId}`,
            source: id,
            target: outputNodeId,
            type: outputType,
            animated: true,
            style: { stroke: edgeColor, strokeWidth: 3 }
          };
          
          setNodes((nodes) => [...nodes, outputNode]);
          setEdges((edges) => [...edges, edge]);
          
          logger.debug('[NodeAdapter] Created output node for custom node result:', outputNodeId);
        }
      } else {
        // Update existing output node - create new page
        const outputNodeId = existingOutputEdge.target;
        const outputNode = getNode(outputNodeId);
        
        if (outputNode) {
          const existingPages = Array.isArray(outputNode.data.pages) ? outputNode.data.pages : [];
          const newPage = {
            content: outputContent,
            context: null,
            type: outputType,
            timestamp: new Date().toISOString()
          };
          
          setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === outputNodeId) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    output: outputContent,
                    content: outputContent,
                    type: outputType,
                    outputsMediaType: outputType,
                    pages: [...existingPages, newPage],
                    currentPageIndex: existingPages.length,
                    streamingInProgress: false,
                    ...(outputType === 'image' && { imageUrl: outputContent }),
                    ...(outputType === 'video' && { videoUrl: outputContent })
                  }
                };
              }
              return node;
            })
          );
          
          logger.debug('[NodeAdapter] Created new page in existing output node');
        }
      }
    }, [id, setNodes, setEdges, getEdges, getNode]);

    // Handle dictation submit - sends transcription to connected nodes
    const handleDictationSubmit = useCallback(async () => {
      const transcription = typeof data.transcription === 'string' ? data.transcription : '';
      
      if (!transcription.trim()) {
        return;
      }

      logger.debug('[NodeAdapter] Dictation submit:', { nodeId: id, transcriptionLength: transcription.length });

      // Find connected output nodes and send transcription
      const allEdges = getEdges();
      const outgoingEdges = allEdges.filter(edge => edge.source === id);
      
      if (outgoingEdges.length === 0) {
        // No connections - create output node with transcription
        const sourceNode = getNode(id);
        if (sourceNode) {
          const sourceWidth = sourceNode.measured?.width || sourceNode.width || 320;
          const outputNodeId = `output-${Date.now()}`;
          
          const initialPage = {
            content: transcription,
            context: null,
            type: 'text' as const,
            timestamp: new Date().toISOString()
          };
          
          const outputNode = {
            id: outputNodeId,
            type: 'universalOutput',
            position: {
              x: sourceNode.position.x + sourceWidth + 50,
              y: sourceNode.position.y
            },
            width: TEXT_OUTPUT_DIMS.width,
            height: TEXT_OUTPUT_DIMS.height,
            data: {
              output: transcription,
              content: transcription,
              type: 'text',
              outputsMediaType: 'text',
              pages: [initialPage],
              currentPageIndex: 0,
            }
          };
          
          const edge = {
            id: `${id}-${outputNodeId}`,
            source: id,
            target: outputNodeId,
            type: 'text',
            animated: true,
            style: { stroke: '#FED402', strokeWidth: 3 }
          };
          
          setNodes((nodes) => [...nodes, outputNode]);
          setEdges((edges) => [...edges, edge]);
          
          logger.debug('[NodeAdapter] Created output node for dictation:', outputNodeId);
        }
      } else {
        // Update existing connected nodes
        for (const edge of outgoingEdges) {
          const targetNode = getNode(edge.target);
          if (targetNode) {
            // If it's an Output node, add new page
            if (targetNode.type === 'universalOutput' || targetNode.type === 'customOutput') {
              const existingPages = Array.isArray(targetNode.data.pages) ? targetNode.data.pages : [];
              const newPage = {
                content: transcription,
                context: null,
                type: 'text' as const,
                timestamp: new Date().toISOString()
              };
              
              setNodes((nodes) =>
                nodes.map((node) => {
                  if (node.id === edge.target) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        output: transcription,
                        content: transcription,
                        type: 'text',
                        outputsMediaType: 'text',
                        pages: [...existingPages, newPage],
                        currentPageIndex: existingPages.length,
                      }
                    };
                  }
                  return node;
                })
              );
            } else {
              // For other nodes, set as input prompt
              setNodes((nodes) =>
                nodes.map((node) => {
                  if (node.id === edge.target) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        prompt: transcription,
                      }
                    };
                  }
                  return node;
                })
              );
            }
          }
        }
        
        logger.debug('[NodeAdapter] Sent dictation to connected nodes');
      }
    }, [id, data.transcription, setNodes, setEdges, getEdges, getNode]);

    // Collect variables from connected input nodes (for custom nodes with PromptTemplate)
    // Using useEffect to avoid infinite render loops
    React.useEffect(() => {
      if (!data.customNodeDefinition || !data.variables) return;
      
      const edges = getEdges();
      const incomingEdges = edges.filter((edge) => edge.target === id);
      const customDef = data.customNodeDefinition;
      
      // Map incoming data to template variables by input name
      for (const edge of incomingEdges) {
        const sourceNode = getNode(edge.source);
        if (!sourceNode) continue;
        
        // Find which input this edge is connected to (by handle ID)
        const targetHandleId = edge.targetHandle;
        const inputDef = customDef?.inputs?.find((inp: { id: string }) => inp.id === targetHandleId);
        
        if (inputDef && inputDef.variableName) {
          // Get the content from the source node
          let content = '';
          
          if (sourceNode.type === 'customOutput') {
            const pages = (sourceNode.data?.pages || []) as Array<{ content: string; type?: string }>;
            const currentPageIndex = (sourceNode.data?.currentPageIndex || 0) as number;
            if (pages[currentPageIndex]) {
              content = pages[currentPageIndex].content;
            }
          } else if (sourceNode.data.output) {
            content = sourceNode.data.output as string;
          } else if (sourceNode.data.content) {
            content = sourceNode.data.content as string;
          } else if (sourceNode.data.imageUrl) {
            content = sourceNode.data.imageUrl as string;
          } else if (sourceNode.data.videoUrl) {
            content = sourceNode.data.videoUrl as string;
          }
          
          // Update variable if content has changed
          if (content && data.variables[inputDef.variableName] !== content) {
            logger.debug('[NodeAdapter] Auto-filling variable from input:', {
              nodeId: id,
              variable: inputDef.variableName,
              source: sourceNode.id,
              contentLength: content.length
            });
            
            handleVariableChange(inputDef.variableName, content);
          }
        }
      }
    }, [id, data.customNodeDefinition, data.variables, handleVariableChange, getEdges, getNode]);

    // Register input handler for Output nodes to receive data from connected nodes
    React.useEffect(() => {
      if (nodeType === 'Output' && data.onReceiveInput) {
        const receiveHandler = data.onReceiveInput as (handler: (inputData: { content: string; context?: unknown; type: 'text' | 'image' | 'video'; nodeId?: string }) => void) => void;
        receiveHandler((inputData) => {
          logger.debug('[NodeAdapter:Output] Received input:', {
            nodeId: id,
            type: inputData.type,
            contentLength: inputData.content?.length,
            hasContext: !!inputData.context
          });

          // Add new page to output node with incoming data
          setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                const existingPages = Array.isArray(node.data.pages) ? node.data.pages : [];
                const newPage = {
                  content: inputData.content,
                  context: inputData.context || null,
                  type: inputData.type,
                  timestamp: new Date().toISOString()
                };
                
                return {
                  ...node,
                  data: {
                    ...node.data,
                    content: inputData.content,
                    type: inputData.type,
                    outputsMediaType: inputData.type,
                    pages: [...existingPages, newPage],
                    currentPageIndex: existingPages.length,
                    ...(inputData.type === 'image' && { imageUrl: inputData.content }),
                    ...(inputData.type === 'video' && { videoUrl: inputData.content })
                  }
                };
              }
              return node;
            })
          );
        });
      }
    }, [nodeType, id, data.onReceiveInput, setNodes]);

    // Extract video duration for nodes that price per-second of input video
    // (Wan Animate uses it for the I2V duration; VideoScale uses it for AI Enhance
    // pricing). Runs when video inputs change and stores the duration on the node.
    React.useEffect(() => {
      if (nodeType !== 'WanAnimateVideo' && nodeType !== 'VideoScale') return;
      
      const extractDuration = async () => {
        // Find connected video
        const edges = getEdges();
        const incomingEdges = edges.filter((edge) => edge.target === id);
        
        let videoUrl: string | undefined;
        
        for (const edge of incomingEdges) {
          const sourceNode = getNode(edge.source);
          if (!sourceNode) continue;
          
          // Check Output node pages
          if (sourceNode.type === 'customOutput' || sourceNode.type === 'universalOutput') {
            const pages = (sourceNode.data?.pages || []) as Array<{ content: string; type?: string }>;
            const currentPageIndex = (sourceNode.data?.currentPageIndex || 0) as number;
            
            if (pages.length > 0 && pages[currentPageIndex]?.type === 'video') {
              videoUrl = pages[currentPageIndex].content;
              break;
            }
          }
          // Check for video content in node data
          else if (sourceNode.data.videoUrl || (sourceNode.data.output && typeof sourceNode.data.output === 'string' && sourceNode.data.type === 'video')) {
            videoUrl = (sourceNode.data.videoUrl || sourceNode.data.output) as string;
            break;
          }
        }
        
        if (!videoUrl) {
          // No video connected - reset to default
          setNodes((nodes) =>
            nodes.map((node) =>
              node.id === id && node.data.inputVideoDuration !== undefined
                ? { ...node, data: { ...node.data, inputVideoDuration: undefined } }
                : node
            )
          );
          return;
        }
        
        // Extract duration from video
        try {
          const videoElement = document.createElement('video');
          videoElement.preload = 'metadata';
          videoElement.src = videoUrl;
          
          await new Promise<void>((resolve) => {
            videoElement.onloadedmetadata = () => resolve();
            videoElement.onerror = () => resolve();
            setTimeout(() => resolve(), 5000); // Timeout after 5 seconds
          });
          
          const duration = videoElement.duration;
          if (duration && !isNaN(duration) && isFinite(duration)) {
            setNodes((nodes) =>
              nodes.map((node) =>
                node.id === id
                  ? { ...node, data: { ...node.data, inputVideoDuration: duration } }
                  : node
              )
            );
          }
        } catch (err) {
          // Ignore errors, just won't update duration
        }
      };
      
      extractDuration();
    }, [nodeType, id, incomingEdges, getEdges, getNode, setNodes]);

    // ── Connected-audio duration, for models billed by the audio's length ────
    // Kling Avatar v2 has NO duration parameter: the clip it returns is exactly
    // as long as the audio you feed it. Billing from a setting therefore
    // charges for a length the model never produces — a 30s track billed as the
    // old dropdown's 5s cost us ~4x what we took. Measure the file instead and
    // store it on the node, so the badge, the multi-select estimate and the
    // reservation all read one number.
    //
    // Mirrors the video-duration effect above (and probeMediaRatio's contract):
    // resolves rather than throws, memoised per URL, and writes `undefined`
    // when nothing usable is connected so downstream code fails closed rather
    // than billing a stale value from a previously connected track.
    React.useEffect(() => {
      if (nodeType !== 'KlingAvatar') return;

      let cancelled = false;

      const setMeasured = (seconds: number | undefined) => {
        setNodes((nodes) =>
          nodes.map((node) =>
            node.id === id && node.data.connectedAudioDuration !== seconds
              ? { ...node, data: { ...node.data, connectedAudioDuration: seconds } }
              : node
          )
        );
      };

      const measure = async () => {
        // Find the connected audio the same way the generation path does, so
        // the number we bill is measured off the file we actually send.
        let audioUrl: string | undefined;
        for (const edge of getEdges().filter((e) => e.target === id)) {
          const sourceNode = getNode(edge.source);
          if (!sourceNode) continue;

          if (sourceNode.type === 'customOutput' || sourceNode.type === 'universalOutput') {
            const pages = (sourceNode.data?.pages || []) as Array<{ content: string; type?: string }>;
            const currentPageIndex = (sourceNode.data?.currentPageIndex || 0) as number;
            const page = pages[currentPageIndex];
            if (page?.type === 'audio' && typeof page.content === 'string') {
              audioUrl = page.content;
              break;
            }
          } else if (typeof sourceNode.data?.audioUrl === 'string' && sourceNode.data.audioUrl) {
            audioUrl = sourceNode.data.audioUrl;
            break;
          }
        }

        if (!audioUrl) {
          if (!cancelled) setMeasured(undefined);
          return;
        }

        const seconds = await probeAudioDuration(audioUrl);
        if (cancelled) return;
        // null (unreachable URL, stream with no finite length, decode failure)
        // stores undefined, NOT a fallback — see resolveAudioDrivenSeconds.
        setMeasured(seconds === null ? undefined : seconds);
      };

      measure();
      return () => { cancelled = true; };
    }, [nodeType, id, incomingEdges, getEdges, getNode, setNodes]);

    // ── Connected-input aspect ratio detection ───────────────────────────────
    // Resolve the intrinsic ratio of the image/video wired into this node, then
    // warn when it doesn't match the node's aspectRatio setting (the provider
    // will letterbox or crop).
    //
    // Rewritten because the original never fired: it probed images synchronously
    // inside a useMemo (`img.src = url; if (img.complete)`), which is false for
    // anything not already decoded; it understood only 16:9 and 9:16, silently
    // treating every other setting as 9:16; it ignored video inputs entirely;
    // and it was gated on handlerType 'video', i.e. only VeoVideo and GeminiOmni
    // out of ~15 video nodes. It also re-downloaded media it already had the
    // dimensions for.
    const [detectedInput, setDetectedInput] = React.useState<
      { ratio: number; kind: 'image' | 'video' } | null
    >(null);

    // Which nodes get this: registry-derived, so new nodes are covered for free.
    // A node qualifies if it accepts media input AND exposes an aspectRatio
    // setting to mismatch against.
    const wantsAspectDetection =
      Boolean(definition.acceptsMediaTypes?.some((t) => t === 'image' || t === 'video')) &&
      Boolean(definition.settings?.some((s) => s.id === 'aspectRatio'));

    React.useEffect(() => {
      if (!wantsAspectDetection) {
        setDetectedInput(null);
        return;
      }

      let cancelled = false;

      // Find the first connected media source, preferring dimensions the app has
      // ALREADY measured (Image Panels store preloadedWidth/Height; Output nodes
      // store currentAspectRatio via PagedOutput's onMediaMetadataReady) so the
      // common case needs no network access at all.
      const resolve = async () => {
        for (const edge of incomingEdges) {
          const sourceNode = getNode(edge.source);
          if (!sourceNode) continue;
          const sourceData = sourceNode.data as Record<string, any>;

          // 1. Image Panel — dimensions already known.
          if (sourceNode.type === 'imagePanel') {
            const w = sourceData?.preloadedWidth;
            const h = sourceData?.preloadedHeight;
            if (typeof w === 'number' && typeof h === 'number' && w > 0 && h > 0) {
              if (!cancelled) setDetectedInput({ ratio: w / h, kind: 'image' });
              return;
            }
            if (typeof sourceData?.imageUrl === 'string' && sourceData.imageUrl) {
              const ratio = await probeMediaRatio(sourceData.imageUrl, 'image');
              if (ratio && !cancelled) { setDetectedInput({ ratio, kind: 'image' }); return; }
            }
            continue;
          }

          // 2. Output container — use the page the user is actually looking at.
          if (sourceNode.type === 'customOutput' || sourceNode.type === 'universalOutput') {
            const pages = Array.isArray(sourceData?.pages) ? sourceData.pages : [];
            const pageIndex = typeof sourceData?.currentPageIndex === 'number' ? sourceData.currentPageIndex : 0;
            const page = pages[pageIndex] as { content?: string; type?: string } | undefined;
            if (!page?.content || (page.type !== 'image' && page.type !== 'video')) continue;

            // PagedOutput already measured the visible page for us.
            const known = sourceData?.currentAspectRatio;
            if (typeof known === 'number' && known > 0) {
              if (!cancelled) setDetectedInput({ ratio: known, kind: page.type });
              return;
            }
            const ratio = await probeMediaRatio(page.content, page.type);
            if (ratio && !cancelled) { setDetectedInput({ ratio, kind: page.type }); return; }
            continue;
          }

          // 3. Any other node emitting media directly (generators, panels).
          const directUrl =
            (typeof sourceData?.imageUrl === 'string' && sourceData.imageUrl) ||
            (typeof sourceData?.videoUrl === 'string' && sourceData.videoUrl) ||
            null;
          if (directUrl) {
            const kind: 'image' | 'video' = sourceData?.videoUrl === directUrl ? 'video' : 'image';
            const ratio = await probeMediaRatio(directUrl, kind);
            if (ratio && !cancelled) { setDetectedInput({ ratio, kind }); return; }
          }
        }

        // Nothing connected (or nothing measurable) — clear any stale reading.
        if (!cancelled) setDetectedInput(null);
      };

      resolve();
      return () => { cancelled = true; };
    }, [wantsAspectDetection, incomingEdges, getNode]);

    // ── Connected-input aspect ratio AUTO-SELECT ─────────────────────────────
    // Detection alone only ever produced a warning. Now that the input's real
    // ratio is known, set the node's aspectRatio to the closest option the
    // dropdown is CURRENTLY offering.
    //
    // "Currently offering" matters: the option list is narrowed per selected
    // model via conditionalOptions, so it has to be resolved exactly the way
    // NodeSettingsFooter resolves it before rendering. Kling Image offers 3:2,
    // Kling Video does not — snapping a Kling Video node to 3:2 because the
    // input photo is 3:2 would write a value fal.ai rejects.
    //
    // All the "should we?" logic lives in decideAspectRatioSnap (pure, unit
    // tested). It returns null for "write nothing", which also makes this
    // effect idempotent: the write below changes data.parameters, this effect
    // re-runs once, decide returns null, and it settles.
    React.useEffect(() => {
      if (!wantsAspectDetection) return;

      const field = definition.settings?.find((s) => s.id === 'aspectRatio');
      if (!field) return;

      const params = (data.parameters || {}) as Record<string, unknown>;
      // conditionalOptions usually depends on selectedModel, which lives at the
      // top level of node.data, not in parameters.
      const dependentValues: Record<string, unknown> = {
        ...params,
        selectedModel: (data.selectedModel as unknown) ?? params.selectedModel,
      };
      const offered = resolveOfferedOptions(field, dependentValues);

      const next = decideAspectRatioSnap({
        detectedRatio: detectedInput?.ratio ?? null,
        currentValue: params.aspectRatio,
        defaultValue: typeof field.default === 'string' ? field.default : undefined,
        manuallySet: data.aspectRatioManuallySet === true,
        autoSelected: data.aspectRatioAutoSelected === true,
        offered,
      });
      if (next === null) return;

      setNodes((nodes) =>
        nodes.map((n) =>
          n.id === id
            ? {
                ...n,
                data: {
                  ...n.data,
                  aspectRatioAutoSelected: true,
                  parameters: {
                    ...((n.data.parameters || {}) as Record<string, unknown>),
                    aspectRatio: next,
                  },
                },
              }
            : n
        )
      );
      markDirty(); // A snap is a real settings change — let auto-save keep it.
    }, [
      wantsAspectDetection,
      detectedInput,
      definition,
      data.parameters,
      data.selectedModel,
      data.aspectRatioManuallySet,
      data.aspectRatioAutoSelected,
      id,
      setNodes,
      markDirty,
    ]);

    const aspectRatioWarning = React.useMemo(() => {
      if (!wantsAspectDetection || !detectedInput) return null;

      const params = (data.parameters || {}) as Record<string, unknown>;
      const settingDefault = definition.settings?.find((s) => s.id === 'aspectRatio')?.default;
      const selected = (params.aspectRatio ?? settingDefault) as string | undefined;

      // 'auto' / 'custom' / presets have no fixed target — nothing to mismatch.
      const targetRatio = parseAspectRatioSetting(selected);
      if (targetRatio === null) return null;

      // Symmetric tolerance — see aspectRatioDrift().
      if (aspectRatioDrift(detectedInput.ratio, targetRatio) < ASPECT_RATIO_DRIFT_TOLERANCE) return null;

      const suggestion = describeClosestRatio(detectedInput.ratio);
      const noun = detectedInput.kind === 'video' ? 'Video' : 'Image';
      const verb = definition.outputsMediaType === 'video' ? 'video' : 'result';
      return suggestion === selected
        ? `⚠️ Connected ${noun.toLowerCase()} doesn't match ${selected} — the ${verb} will be cropped or padded`
        : `⚠️ Connected ${noun.toLowerCase()} is closer to ${suggestion}, not ${selected} — the ${verb} will be cropped or padded`;
    }, [wantsAspectDetection, detectedInput, data.parameters, definition]);

    // Determine status based on node state
    const status = data.isProcessing || data.isCondensing
      ? 'processing' 
      : data.error 
      ? 'error' 
      : aspectRatioWarning
      ? 'warning'
      : 'idle';
    
    const statusMessage = data.isProcessing
      ? (data.streamingInProgress ? 'Streaming response...' : 'Processing prompt...')
      : data.isCondensing
      ? 'Condensing text...'
      : data.error
      ? data.error
      : aspectRatioWarning
      ? aspectRatioWarning
      : 'Ready';

    // Calculate credit cost based on node type and settings
    // Uses the node definition's defaults when parameters are not explicitly set
    const creditCost = useMemo(() => {
      const parameters = (data.parameters || {}) as Record<string, string | number | undefined>;
      
      // Helper to get setting value with definition default fallback
      const getSetting = (settingId: string, fallback: string): string => {
        // First check if value is in parameters (handle booleans too)
        const paramValue = parameters[settingId];
        if (paramValue != null && paramValue !== '') return String(paramValue);

        // Then check data.selectedModel for model settings
        if (settingId === 'selectedModel' && typeof data.selectedModel === 'string' && data.selectedModel) {
          return data.selectedModel;
        }

        // Fall back to definition's default
        const setting = definition.settings?.find(s => s.id === settingId);
        if (setting?.default != null) return String(setting.default);

        // Ultimate fallback
        return fallback;
      };

      // Compute base (per-generation) cost
      const computeBaseCost = (): number | undefined => {
        if (isVideoGeneratorNode(nodeType)) {
          const duration = getSetting('duration', '8');
          const resolution = getSetting('resolution', '720p');
          const selectedModel = getSetting('selectedModel', 'veo-3.1-fast-generate-preview');
          // Veo 3.1: no quote for a combination the server refuses (Lite 4k,
          // 1080p/4k at a length other than 8s). Same rule as veoResolution.ts.
          if (isVeo31Model(selectedModel) && 'error' in resolveVeo31Resolution(selectedModel, resolution, duration)) {
            return undefined;
          }
          return calculateVideoCreditCost(selectedModel, duration, resolution);
        }
        
        // Special handling for Wan Animate - uses custom per-second pricing
        // Uses inputVideoDuration extracted by the useEffect above
        if (nodeType === 'WanAnimateVideo') {
          const resolution = getSetting('resolution', '480p');
          
          // Use the extracted video duration from useEffect, or fallback to 5 seconds
          const videoDurationSeconds = typeof data.inputVideoDuration === 'number' && data.inputVideoDuration > 0
            ? data.inputVideoDuration
            : 5; // Default fallback
          
          return calculateWanAnimateCreditCost(resolution, videoDurationSeconds);
        }
        
        if (isFalAIVideoNode(nodeType)) {
          const duration = getSetting('duration', '5');
          const resolution = getSetting('resolution', '720p');
          const enableAudio = getSetting('enableAudio', 'false') === 'true';
          // Map node type to default fal.ai model ID for credit calculation
          // These should match the 'default' in each node's definition
          const modelMap: Record<string, string> = {
            // Unified KlingVideo node — credit cost should be driven by
            // selectedModel + audio toggle. selectedModel below overrides.
            'KlingVideo': (() => {
              const sm = String(getSetting('selectedModel', 'kling-3.0-standard'));
              if (sm === 'kling-2.6') return enableAudio ? 'kling-2.6-audio' : 'kling-2.6';
              if (sm === 'kling-2.5-turbo') return 'kling-2.5-turbo';
              if (sm === 'kling-3.0-pro') return enableAudio ? 'kling-3.0-pro-audio' : 'kling-3.0-pro';
              return enableAudio ? 'kling-3.0-standard-audio' : 'kling-3.0-standard';
            })(),
            'Kling26Video': enableAudio ? 'kling-2.6-audio' : 'kling-2.6',
            'Kling25Video': 'kling-2.5-turbo',
            'KlingO3Video': (() => {
              const quality = getSetting('quality', 'standard');
              if (quality === 'pro') return enableAudio ? 'kling-3.0-pro-audio' : 'kling-3.0-pro';
              return enableAudio ? 'kling-3.0-standard-audio' : 'kling-3.0-standard';
            })(),
            'WanVideo': 'wan-2.2',
            'Wan26Video': 'wan-2.7',
            'LTXVideo': 'ltx-2.3',
            'LTXFastVideo': 'ltx-2.3-fast',
            'PixVerseVideo': 'pixverse-6',
            'HunyuanVideo': 'hunyuan-1.5',
            'MinimaxVideo': 'hailuo-2.3',
            'KlingAvatar': 'kling-avatar',
            'Seedance25Video': 'seedance-2.5',
            'Seedance2Video': 'seedance-2-fast',
            'MinimaxH3Video': 'minimax-h3',
          };
          const selectedModel = getSetting('selectedModel', modelMap[nodeType] || 'ltx-2.3-fast');

          // Kling Avatar is billed by the length of the connected AUDIO — the
          // model has no duration parameter, so the clip is exactly as long as
          // the track. Until the probe above measures it there is no honest
          // number, so show NO badge rather than a guessed one (returning
          // undefined here is how every non-costed node behaves). Same helper
          // as selectionCostUtils so the badge and the toolbar cannot diverge.
          if (AUDIO_DRIVEN_DURATION_MODELS.has(selectedModel)) {
            const seconds = resolveAudioDrivenSeconds(data.connectedAudioDuration);
            if (seconds === null) return undefined;
            return calculateVideoCreditCost(selectedModel, seconds, resolution);
          }

          // LTX-2.3 Pro splits price by mode (T2V is 33% more expensive than I2V).
          // For the UI estimate we use I2V (cheaper, the common case when an image
          // is connected). The server re-prices at submit time using the actual
          // mode — see falai.ts creditModelId remap for ltx-2.3 → ltx-2.3-t2v.
          if (selectedModel === 'ltx-2.3' || selectedModel === 'ltx-2.3-pro' || selectedModel === 'ltx-2.3-fast') {
            return calculateLTX23CreditCost(selectedModel, parseFloat(duration), resolution, 'image-to-video');
          }

          return calculateVideoCreditCost(selectedModel, duration, resolution);
        }

        // xAI Video nodes credit calculation (Grok Video via direct xAI API)
        if (getHandlerType(reactFlowTypeToType(nodeType)) === 'xaiVideo') {
          const duration = getSetting('duration', '6');
          const resolution = getSetting('resolution', '720p');
          // Default to legacy grok-imagine-video — but the GrokVideo node now
          // exposes a model selector with grok-imagine-video-1.5-preview as an
          // option. Internal pricing key 'grok-video' stays the alias for the
          // standard model; the 1.5-preview ID is recognised directly.
          const selectedModel = (typeof data.selectedModel === 'string' && data.selectedModel) || 'grok-imagine-video';
          const pricingId =
            selectedModel === 'grok-imagine-video-1.5-preview'
              ? 'grok-imagine-video-1.5-preview'
              : 'grok-video';
          return calculateVideoCreditCost(pricingId, duration, resolution);
        }

        // HeyGen nodes credit calculation
        const heygenNodeMap: Record<string, string> = {
          'HeyGenAvatarV3': 'heygen-digital-twin-v3',
          'HeyGenAvatarV4': 'heygen-digital-twin-v4',
          'HeyGenAvatarV': 'heygen-digital-twin-v5',
          'HeyGenVideoAgent': 'heygen-video-agent',
          'HeyGenTranslatePrecision': 'heygen-translate-precision',
          'HeyGenTranslateSpeed': 'heygen-translate-speed',
          'HeyGenPhotoVideo': 'heygen-image-to-video',
        };
        if (heygenNodeMap[nodeType]) {
          const duration = getSetting('duration', '30');
          const heygenModelId = getSetting('selectedModel', heygenNodeMap[nodeType]);
          return calculateVideoCreditCost(heygenModelId, duration, '720p');
        }

        // HeyGen Lipsync: model lives in the lipsyncVariant parameter, not selectedModel
        if (nodeType === 'HeyGenLipsync') {
          const duration = getSetting('duration', '30');
          const variant = getSetting('lipsyncVariant', 'heygen-lipsync-speed');
          return calculateVideoCreditCost(variant, duration, '720p');
        }

        if (nodeType === 'ImagePrompt') {
          // Gemini uses per-(model, resolution) pricing because Google's token
          // costs don't scale linearly with pixel area (3 Pro charges the same
          // for 1K and 2K). Default: gemini-3.1-flash-image at 1024px.
          const selectedModel = getSetting('selectedModel', 'gemini-3.1-flash-image');
          const resolution = getSetting('resolution', '1024px');
          return calculateGeminiImageCreditCost(selectedModel, resolution);
        }

        
        if (nodeType === 'GPTImage') {
          const selectedModel = getSetting('selectedModel', 'gpt-image-2');
          const resolution = getSetting('resolution', '1024px');
          // gpt-image-1-mini defaults to "low" quality (cheapest); others default to "medium"
          const defaultQuality = selectedModel === 'gpt-image-1-mini' ? 'low' : 'medium';
          const quality = getSetting('quality', defaultQuality);
          return calculateGPTImageCreditCost(selectedModel, quality, resolution);
        }
        
        if (nodeType === 'FluxImage') {
          const selectedModel = getSetting('selectedModel', 'flux-2-flash');
          return calculateImageCreditCost(selectedModel, '720p');
        }
        
        if (nodeType === 'GrokImage') {
          // Grok Image 2.0 is priced per (quality × resolution) + input images;
          // the older models stay flat. Same helper as selectionCostUtils.
          const selectedModel = getSetting('selectedModel', 'grok-image');
          return calculateGrokImageNodeCreditCost(
            selectedModel,
            { quality: getSetting('quality', 'low'), resolution: getSetting('resolution', '1k') },
            countImagesFromSources(incomingEdges.map((edge) => getNode(edge.source) as any)),
          );
        }
        
        if (isFalAIImageNode(nodeType)) {
          // fal.ai models: map node type to model ID for credit calculation
          // fal.ai uses flat pricing per generation (no resolution multiplier)
          const modelMap: Record<string, string> = {
            'ZImageTurbo': 'z-image-turbo',
            'QwenImage': 'qwen-image-2',
            'QwenImageEdit': 'qwen-image-edit',
            'Seedream': 'seedream-5-lite',
            'RecraftV3': 'recraft-v4',
            'GLMImage': 'glm-image',
            'KlingImage': 'kling-image-o3',
          };
          // Use selectedModel if available (for nodes with model switch)
          const falModel = typeof data.selectedModel === 'string' && data.selectedModel
            ? data.selectedModel
            : modelMap[nodeType] || 'z-image-turbo';
          // Kling Image is the one fal.ai image model with resolution-dependent
          // pricing (1K == 2K, 4K doubles). Quote the resolution the node is
          // actually set to, or the badge under-quotes 4K by half. Every other
          // model is flat per generation, so '720p' preserves their behavior.
          if (falModel === 'kling-image-o3') {
            return calculateImageCreditCost(falModel, getSetting('resolution', '1K'));
          }
          return calculateImageCreditCost(falModel, '720p');
        }

        // Text nodes
        if (['Kimi', 'Qwen', 'Minimax', 'Claude', 'ChatGPT', 'GeminiText', 'GrokText'].includes(nodeType)) {
          const selectedModel = typeof data.selectedModel === 'string' ? data.selectedModel : '';
          const defaultModels: Record<string, string> = {
            'Kimi': 'kimi-k2.6',
            'Qwen': 'qwen-3.5',
            'Minimax': 'minimax-m2.7',
            'Claude': 'claude-haiku-4-5',
            'ChatGPT': 'gpt-5.4-nano',
            'GeminiText': 'gemini-3.5-flash',
            'GrokText': 'grok-4.3',
          };
          const model = selectedModel || defaultModels[nodeType] || '';
          return calculateImageCreditCost(model, '720p');
        }

        // ImageScale: only costs credits in AI Enhance mode
        if (nodeType === 'ImageScale') {
          const mode = getSetting('mode', 'resize');
          if (mode === 'ai-enhance') {
            return calculateImageCreditCost('esrgan', '720p');
          }
          return 0; // Lanczos is free
        }

        // VideoScale: only costs credits in AI Enhance mode. Pricing scales
        // per-second × resolution to stay ~52-57% margin over fal.ai's actual cost.
        // Uses inputVideoDuration extracted by the useEffect above (fallback 5s).
        // FFmpeg resize path stays free.
        if (nodeType === 'VideoScale') {
          const mode = getSetting('mode', 'resize');
          if (mode === 'ai-enhance') {
            const targetResolution = getSetting('targetResolution', '1080p');
            const duration = typeof data.inputVideoDuration === 'number' && data.inputVideoDuration > 0
              ? data.inputVideoDuration
              : 5;
            return calculateVideoScaleCreditCost(targetResolution, duration);
          }
          return 0;
        }

        // ElevenLabs audio nodes (Voice/TTS char-based, SFX/Music per-generation)
        if (isAudioGen) {
          if (nodeType === 'SoundEffects') {
            return calculateElevenLabsTTSCreditCost('eleven_text_to_sound_v2', 0);
          }
          if (nodeType === 'Music') {
            const model = getSetting('selectedModel', 'music_v2');
            return calculateElevenLabsTTSCreditCost(model, 0);
          }
          // VoiceTTS — character-based. Quote the SAME text handleAudioGenerate
          // will send: an explicit prompt wins, otherwise the connected text
          // input. The server bills cleanText.length (routes/elevenlabs.ts:105,
          // :122), so pricing data.prompt alone quoted the 1-credit floor for
          // the primary text-node → Voice wiring while a 2,000-char paragraph
          // actually deducts 92.
          const model = getSetting('selectedModel', 'eleven_v3');
          const prompt = typeof data.prompt === 'string' ? data.prompt : '';
          const charCount = prompt ? prompt.length : connectedTextCharCount;
          return calculateElevenLabsTTSCreditCost(model, charCount);
        }

        // Other nodes don't show credit cost on send button
        return undefined;
      };

      const baseCost = computeBaseCost();
      // NaN = a price function refused an impossible combination: show no quote.
      if (baseCost === undefined || !Number.isFinite(baseCost)) return undefined;

      // Apply batch multiplier
      // During an active batch, use remaining count (total - current); otherwise use batchCount setting
      const batchProgress = data.batchProgress as { current: number; total: number } | undefined;
      const rawBatchCount = (parameters as Record<string, any>).batchCount;
      const parsedBatchCount = typeof rawBatchCount === 'number' ? rawBatchCount : parseInt(String(rawBatchCount || '1'), 10);
      const batchCount = Math.min(10, Math.max(1, Number.isFinite(parsedBatchCount) ? parsedBatchCount : 1));

      const effectiveCount = (batchProgress && batchProgress.total > 0)
        ? Math.max(0, batchProgress.total - batchProgress.current)
        : batchCount;

      return effectiveCount > 0 ? baseCost * effectiveCount : baseCost;

    }, [nodeType, data.selectedModel, data.parameters, data.prompt, data.batchProgress, data.inputVideoDuration, data.connectedAudioDuration, definition.settings, incomingEdges, getNode, isAudioGen, connectedTextCharCount]);

    // Check if there's a connected text input with non-empty content
    // This enables the send button even when the textarea is empty
    // Uses the reactive connectedTextContent subscription for real-time updates
    const hasConnectedTextInput = !!connectedTextContent.trim();

    // Determine the executor function for auto-execute flow
    // This maps node types to their appropriate handler using the registry
    const getExecutor = useCallback(() => {
      // Output nodes don't execute - they just receive and display content from other nodes.
      // `nodeType` is the PascalCase closure arg ('Output'), so the camelCase React Flow
      // spellings ('universalOutput'/'customOutput') never matched here. Normalising first
      // makes the guard actually fire; the outcome is unchanged because Output's registry
      // handlerType is 'none', which fell through to the same `return null`.
      const pascalType = reactFlowTypeToType(nodeType);
      if (pascalType === 'Output') {
        return null;
      }

      // Use registry to determine handler type
      const handlerType = getHandlerType(pascalType);
      
      switch (handlerType) {
        case 'image':
          return handleImageGenerate;
        case 'xaiImage':
          return handleImageGenerate;
        case 'fluxImage':
          return handleFluxImageGenerate;
        case 'falAIImage':
          return handleFalAIImageGenerate;
        case 'falAIVideo':
          return handleFalAIVideoGenerate;
        case 'xaiVideo':
          return handleXAIVideoGenerate;
        case 'imageProcess':
          return handleImageProcess;
        case 'videoProcess':
          return handleVideoProcess;
        case 'heygenVideo':
          return handleHeygenVideoGenerate;
        case 'wanAnimate':
          return handleWanAnimateGenerate;
        case 'video':
          return handleVideoGenerate;
        case 'audioGen':
          return handleAudioGenerate;
        case 'dictation':
          return handleDictationSubmit;
        case 'text':
          return handleSubmit;
        case 'claude':
          return handleClaudeSubmit;
        case 'kimi':
          return handleKimiSubmit;
        case 'qwen':
          return handleQwenSubmit;
        case 'minimax':
          return handleMinimaxSubmit;
        case 'geminiText':
          return handleGeminiTextSubmit;
        case 'grokText':
          return handleGrokTextSubmit;
        case 'none':
        default:
          // Non-executable nodes like ImagePanel, DocumentPanel
          return null;
      }
    }, [nodeType, handleImageGenerate, handleFluxImageGenerate,
        handleFalAIImageGenerate, handleFalAIVideoGenerate, handleXAIVideoGenerate, handleImageProcess, handleVideoProcess, handleHeygenVideoGenerate, handleWanAnimateGenerate, handleVideoGenerate, handleAudioGenerate,
        handleDictationSubmit, handleSubmit, handleClaudeSubmit, handleKimiSubmit, handleQwenSubmit, handleMinimaxSubmit, handleGeminiTextSubmit, handleGrokTextSubmit]);

    // Register executor for auto-execute flow
    useEffect(() => {
      const executor = getExecutor();
      if (executor) {
        executionController.registerExecutor(id, executor);
        logger.debug('[NodeAdapter] Registered executor for node:', { nodeId: id, nodeType });
      }
      
      return () => {
        executionController.unregisterExecutor(id);
      };
    }, [id, nodeType, getExecutor]);

    // Merge definition with data and handlers
    const enhancedData = {
      ...data,
      definition,
      prompt: data.prompt || '',
      output: data.output || '',
      content: data.content || '',
      pages: data.pages || [],
      currentPageIndex: data.currentPageIndex ?? 0,
      status,
      statusMessage,
      creditCost, // Credit cost for send button display
      usesOwnKey, // True when a BYOK key covers this node's provider (zero credits)
      hasConnectedTextInput, // Enable send button when text input is connected
      connectedTextContent, // Text from connected nodes to show as placeholder
      connectedVideoUrl, // Video URL from connected nodes (for VideoTrim/VideoSplit preview)
      inputWidth: data.inputWidth, // Source image dimensions (for ImageScale)
      inputHeight: data.inputHeight,
      // Upstream context for prompt improvement (Improve with AI feature)
      inputContext: upstreamContext.inputContext,
      imageContext: upstreamContext.imageContext,
      imageUrls: upstreamContext.imageUrls,
      imageUrl: data.imageUrl || '',
      videoUrl: data.videoUrl || '',
      files: data.files || [],
      fileContexts: data.fileContexts || [],
      threadId: data.threadId || null,
      context: data.context || null,
      selectedModel: data.selectedModel || '',
      parameters: data.parameters || {},
      variables: data.variables || {},
      isProcessing: data.isProcessing || false,
      error: data.error || null,
      customNodeDefinition: data.customNodeDefinition,
      _customNodeIcon: data._customNodeIcon,
      _customNodeColor: data._customNodeColor,
      // Dictation node specific
      transcription: data.transcription || '',
      language: data.language || 'en',
      audioUrl: data.audioUrl || '',
      isCondensing: data.isCondensing || false,
      onPromptChange: handlePromptChange,
      onFilesChange: handleFilesChange,
      // Only ImagePanel nodes should support file drops
      onFilesDropped: (nodeType === 'ImagePanel') ? handleFilesDropped : undefined,
      onModelChange: handleModelChange,
      onParameterChange: handleParameterChange,
      onVariableChange: handleVariableChange,
      onPageChange: handlePageChange,
      onContentChange: handleContentChange,
      onVideoTrim: handleVideoTrim,
      onExecute: handleExecute,
      // Dictation node handlers
      onTranscription: handleTranscription,
      onTranscriptionEdit: handleTranscriptionEdit,
      onCondense: handleCondense,
      onCondenseRequest: handleCondenseRequest,
      onLanguageChange: handleLanguageChange,
      onRecordingComplete: handleRecordingComplete,
      onDeductCredits: handleDeductCredits,
      onCancelGeneration: requestBatchCancel,
      onSubmit: (() => {
        // Use registry to determine the correct submit handler
        const pascalType = reactFlowTypeToType(nodeType);
        const handlerType = getHandlerType(pascalType);
        switch (handlerType) {
          case 'image': return handleImageGenerate;
          case 'xaiImage': return handleImageGenerate;
          case 'fluxImage': return handleFluxImageGenerate;
          case 'falAIImage': return handleFalAIImageGenerate;
          case 'falAIVideo': return handleFalAIVideoGenerate;
          case 'xaiVideo': return handleXAIVideoGenerate;
          case 'imageProcess': return handleImageProcess;
          case 'videoProcess': return handleVideoProcess;
          // Same handler, same switch position as getExecutor's 'heygenVideo'
          // case. Without it all 8 HeyGen node types fell through to
          // `default: return handleSubmit` — the OpenAI text handler — so the
          // per-node Send button has never generated a HeyGen video. It failed
          // closed rather than mischarging: handleSubmit posts
          // `model: data.selectedModel` (a heygen-* id) to /api/openai/chat/stream,
          // and reserveTextCredits looks that up as `openai:heygen-*`, which has no
          // pricing row, so the request 500s before any reservation or OpenAI call.
          case 'heygenVideo': return handleHeygenVideoGenerate;
          case 'wanAnimate': return handleWanAnimateGenerate;
          case 'video': return handleVideoGenerate;
          case 'audioGen': return handleAudioGenerate;
          case 'dictation': return handleDictationSubmit;
          case 'claude': return handleClaudeSubmit;
          case 'kimi': return handleKimiSubmit;
          case 'qwen': return handleQwenSubmit;
          case 'minimax': return handleMinimaxSubmit;
          case 'geminiText': return handleGeminiTextSubmit;
          case 'grokText': return handleGrokTextSubmit;
          case 'text':
          default:
            return handleSubmit;
        }
      })(),
      onImageChange: (value: string) => {
        setNodes((nodes) =>
          nodes.map((node) =>
            node.id === id ? { ...node, data: { ...node.data, imageUrl: value } } : node
          )
        );
        // Both ImagePanel hops land here — uploading an image into a container
        // and clearing it again. The URL is persisted content; without this the
        // picture is on the canvas but not in the saved flow.
        markDirty();
      },
      // Don't show delete/duplicate buttons in header for Output nodes
      ...(nodeType !== 'Output' && {
        onDelete: data.onDelete,
        onDuplicate: data.onDuplicate,
      }),
      // Output node empty-state behavior: dropzone if unconnected, blank if connected
      ...(nodeType === 'Output' && {
        outputEmptyBehavior: incomingEdges.length > 0 ? 'blank' : 'dropzone',
        onUploadMedia: incomingEdges.length === 0 ? handleOutputMediaUpload : undefined,
      }),
    };

    return <UniversalNode {...props} data={enhancedData} />;
  };
  _adapterCache.set(nodeType, adapter);
  return adapter;
}

// Export node adapters for each node type
export const UniversalStartingPromptNode = createNodeAdapter('StartingPrompt');
export const UniversalAgentPromptNode = createNodeAdapter('AgentPrompt');
export const UniversalChatGPTNode = createNodeAdapter('ChatGPT');
export const UniversalClaudeNode = createNodeAdapter('Claude');
export const UniversalKimiNode = createNodeAdapter('Kimi');
export const UniversalQwenNode = createNodeAdapter('Qwen');
export const UniversalMinimaxNode = createNodeAdapter('Minimax');
export const UniversalGeminiTextNode = createNodeAdapter('GeminiText');
export const UniversalGrokTextNode = createNodeAdapter('GrokText');
export const UniversalImagePromptNode = createNodeAdapter('ImagePrompt');
export const UniversalGPTImageNode = createNodeAdapter('GPTImage');
export const UniversalFluxImageNode = createNodeAdapter('FluxImage');
// fal.ai Image Nodes
export const UniversalZImageTurboNode = createNodeAdapter('ZImageTurbo');
export const UniversalQwenImageNode = createNodeAdapter('QwenImage');
export const UniversalQwenImageEditNode = createNodeAdapter('QwenImageEdit');
export const UniversalSeedreamNode = createNodeAdapter('Seedream');
export const UniversalRecraftV3Node = createNodeAdapter('RecraftV3');
export const UniversalGrokImageNode = createNodeAdapter('GrokImage');
export const UniversalGLMImageNode = createNodeAdapter('GLMImage');
// Video Nodes
// Fal.ai Video Nodes
export const UniversalKlingVideoNode = createNodeAdapter('KlingVideo');
export const UniversalKling26VideoNode = createNodeAdapter('Kling26Video');
export const UniversalKling25VideoNode = createNodeAdapter('Kling25Video');
export const UniversalKlingO3VideoNode = createNodeAdapter('KlingO3Video');
export const UniversalWanVideoNode = createNodeAdapter('WanVideo');
export const UniversalWan26VideoNode = createNodeAdapter('Wan26Video');
export const UniversalWanAnimateVideoNode = createNodeAdapter('WanAnimateVideo');
export const UniversalLTXVideoNode = createNodeAdapter('LTXVideo');
export const UniversalLTXFastVideoNode = createNodeAdapter('LTXFastVideo');
export const UniversalMinimaxVideoNode = createNodeAdapter('MinimaxVideo');
export const UniversalPixVerseVideoNode = createNodeAdapter('PixVerseVideo');
export const UniversalHunyuanVideoNode = createNodeAdapter('HunyuanVideo');
export const UniversalKlingAvatarNode = createNodeAdapter('KlingAvatar');
export const UniversalVeoVideoNode = createNodeAdapter('VeoVideo');
export const UniversalGeminiOmniNode = createNodeAdapter('GeminiOmni');
export const UniversalGrokVideoNode = createNodeAdapter('GrokVideo');
export const UniversalSeedance2VideoNode = createNodeAdapter('Seedance2Video');
export const UniversalSeedance25VideoNode = createNodeAdapter('Seedance25Video');
export const UniversalMinimaxH3VideoNode = createNodeAdapter('MinimaxH3Video');
export const UniversalHeyGenAvatarV3Node = createNodeAdapter('HeyGenAvatarV3');
export const UniversalHeyGenAvatarV4Node = createNodeAdapter('HeyGenAvatarV4');
export const UniversalHeyGenAvatarVNode = createNodeAdapter('HeyGenAvatarV');
export const UniversalHeyGenVideoAgentNode = createNodeAdapter('HeyGenVideoAgent');
export const UniversalHeyGenTranslatePrecisionNode = createNodeAdapter('HeyGenTranslatePrecision');
export const UniversalHeyGenTranslateSpeedNode = createNodeAdapter('HeyGenTranslateSpeed');
export const UniversalHeyGenPhotoVideoNode = createNodeAdapter('HeyGenPhotoVideo');
export const UniversalHeyGenLipsyncNode = createNodeAdapter('HeyGenLipsync');
// Audio nodes (ElevenLabs) — first audio-output generators
export const UniversalVoiceTTSNode = createNodeAdapter('VoiceTTS');
export const UniversalSoundEffectsNode = createNodeAdapter('SoundEffects');
export const UniversalMusicNode = createNodeAdapter('Music');
export const UniversalKlingImageNode = createNodeAdapter('KlingImage');
export const UniversalOutputNode = createNodeAdapter('Output');
export const UniversalImagePanelNode = createNodeAdapter('ImagePanel');
export const UniversalInpaintNode = createNodeAdapter('Inpaint');
export const UniversalDictationNode = createNodeAdapter('Dictation');
export const UniversalImageScaleNode = createNodeAdapter('ImageScale');
export const UniversalVideoTrimNode = createNodeAdapter('VideoTrim');
export const UniversalVideoSplitNode = createNodeAdapter('VideoSplit');
export const UniversalVideoScaleNode = createNodeAdapter('VideoScale');
export const UniversalVideoStitchNode = createNodeAdapter('VideoStitch');

// Export createNodeAdapter for dynamic custom node registration
export { createNodeAdapter };
